import {
  BankOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  FilePdfOutlined,
  FolderAddOutlined,
  MoneyCollectOutlined,
  OrderedListOutlined,
  PhoneOutlined,
  PrinterOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  SaveOutlined,
  SwapOutlined,
  TagOutlined,
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Spin,
  Table,
  Tooltip,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import html2pdf from "html2pdf.js";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useRef, useState, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { CREATE_jwel } from "../../../Config/Config";
import DashboardTableHeaderStyles from "../../Pages/DashboardTableHeaderStyles";
import { BillPdfDownload } from "./BillPdfDownload";
import HoldSelectDialog from "./HoldSelectDialog";
import HomeBillDialog from "./HomeBillDialog";
import TaansDetailsDialog from "./TaansDetailsDialog";
import { printReceipt } from "./eposPrint";
import CustomerBillInfoDialog from "./CustomerBillInfo";
import OfferDetailsBDialog from "./OfferDetailsBDialog";
import OfferDetailsADialog from "./OfferDetailsADialog";
import QrCodeDialog from "./QrCodeDialog";
import DirectPrint from "./DirectPrint";
import ExchangeEditItemDetails from "./ExchangeEditItemsDetails";
import EstimationSelectDialog from "../SalesInvoice/EstimationSelectDialog";
import ItemDetailsPopover from "../SalesInvoice/ItemDetailsPopover";
import PaymentModesDialog from "../SalesInvoice/PaymentModes";
import CustomerDialog from "../SalesInvoice/CustomerDialog";
import CustomerSelectDialog from "../SalesInvoice/CustomerSelectDialog";

/**
 * NOTE FOR THE DEV TEAM
 * ---------------------------------------------------------------------
 * This file keeps every API call, state variable and business-logic
 * function from the original ExchangeBill screen untouched. The only
 * thing that changed on purpose is the JSX in the `return(...)` block
 * (and the `columns` / `columns1` table renderers), which now follows
 * the exact same visual language as SalesInvoice.jsx:
 *   - same pink "INV NO" hero card + reload/history buttons
 *   - same Customer Type select
 *   - same Est No / SM Code / Tag No input group style (re-used for the
 *     new "Exchange Design" input, so every scan field looks identical)
 *   - same Mobile No + mini stat tiles
 *   - same customer empty-state / filled customer card
 *   - same badge-styled table columns (Sm Code, Design Code, Qty, Amount)
 *   - same footer: Payment Mode mini-table, Ledger Summary, Payment
 *     Status, Save/Print/PDF stack, Amount Details box (adapted to show
 *     Bill Amount / Exchange Amount / Balance Amount instead of a single
 *     net amount)
 *   - same right sidebar: Item Details card, glowing Total Quantity
 *     card, and the same animated sb-btn action buttons
 *
 * UPDATE (latest pass):
 *   - The "Exchange Code" scan field used to live in the header card
 *     next to Est No / SM Code / Design Code. It has been moved to sit
 *     directly under the "Exchange Items" section heading, right above
 *     the exchange items table, so it's obvious which table it feeds.
 *   - The Bill Items table now shows a totals footer row (total pieces
 *     and total amount) using antd's Table.Summary.
 *
 * UPDATE (responsive pass):
 *   - The old layout used `overflow: hidden` on the outer wrapper AND
 *     the left column, with no scrollable region anywhere in between.
 *     At 1360 / 1440 / 1600px widths (or shorter browser heights) the
 *     header + both tables often didn't fit in 88vh, so the footer row
 *     (Payment Mode / Save-Print-PDF / Amount Details) was silently
 *     clipped off-screen with no way to scroll down to it.
 *   - Fixed by splitting the left column into three independent
 *     regions: `.eb-header-wrap` (fixed), `.eb-scroll-mid` (the only
 *     scrollable region — holds Bill Items + Exchange Items tables),
 *     and `.eb-footer-wrap` (fixed, always visible at the bottom).
 *   - Added media query breakpoints at 1600 / 1440 / 1360px that shrink
 *     the right sidebar and add a horizontal-scroll safety net so
 *     nothing gets silently clipped on narrower screens either.
 * ---------------------------------------------------------------------
 */

const { Option } = Select;

const ExchangeBill = () => {
  // const userName = localStorage.getItem("loginName");
  const [printing, setPrinting] = useState(false);
  const receiptRef = useRef(null);
  const samplePrintRef = useRef(null);
  const jewelRef = useRef(null);
  const tagNoRef = useRef(null);
  const submitRef = useRef(null);
  const estNoRef = useRef(null);
  const fetchRef = useRef(null);
  const mobileRef = useRef(null);
  const searchRef = useRef(null);
  const smCodeRef = useRef(null);
  const smSubmitRef = useRef(null);
  const exchangeNoRef = useRef(null);
  const exchangeSubmitRef = useRef(null);
  const qrRef = useRef(null);
  const navigate = useNavigate();
  const [cardAcc, setCardAcc] = useState("");
  const [upiAcc, setUpiAcc] = useState("");
  const [onlineAcc, setOnlineAcc] = useState("");
  const [gstData, setGstData] = useState([]);
  const [totalGstData, setTotalGstData] = useState([]);
  const [barCodeData, setBarCodeData] = useState([]);
  const [updatedData, setUpdatedData] = useState([]);
  const [exchangeUpdatedData, setExchangeUpdatedData] = useState([]);
  const [designData, setDesignData] = useState([]);
  const [exchangeData, setExchangeData] = useState([]);
  const [totalBasicAmount, setTotalBasicAmount] = useState(0);
  const [totalGstAmt, setTotalGstAmt] = useState(0);
  const [totalCGSTAmt, setTotalCGSTAmt] = useState(0);
  const [totalSGSTAmt, setTotalSGSTAmt] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalDiSAmt, steTotalDisAmt] = useState(0);
  const [totalGrossAmount, setTotalGrossAmount] = useState(0);
  const [totalNetAmount, setTotalNetAmount] = useState(0);
  const [totalExchangeGstData, setTotalExchangeGstData] = useState([]);
  const [totalExchangeBasicAmount, setTotalExchangeBasicAmount] = useState(0);
  const [totalExchangeGstAmt, setTotalExchangeGstAmt] = useState(0);
  const [totalExchangeCGSTAmt, setTotalExchangeCGSTAmt] = useState(0);
  const [totalExchangeSGSTAmt, setTotalExchangeSGSTAmt] = useState(0);
  const [totalExchangeDiscount, setTotalExchangeDiscount] = useState(0);
  const [totalExchangeAmount, setTotalExchangeAmount] = useState(0);
  const [totalExchangeDiSAmt, steTotalExchangeDisAmt] = useState(0);
  const [totalExchangeGrossAmount, setTotalExchangeGrossAmount] = useState(0);
  const [totalExchangeNetAmount, setTotalExchangeNetAmount] = useState(0);
  const [totalExchangeQty, setTotalExchangeQty] = useState(0);
  const [totalBillGstData, setTotalBillGstData] = useState([]);
  const [totalBillGstAmt, setTotalBillGstAmt] = useState(0);
  const [totalBillCGSTAmt, setTotalBillCGSTAmt] = useState(0);
  const [totalBillSGSTAmt, setTotalBillSGSTAmt] = useState(0);
  const [totalBillDiscount, setTotalBillDiscount] = useState(0);
  const [totalBillAmount, setTotalBillAmount] = useState(0);
  const [totalBillDiSAmt, steTotalBillDisAmt] = useState(0);
  const [totalBillGrossAmount, setTotalBillGrossAmount] = useState(0);
  const [totalBillQty, setTotalBillQty] = useState(0);
  const [totalBillNetAmount, setTotalBillNetAmount] = useState(0);
  const [disPer, setDisPer] = useState(0);
  const [totalDiscountAmt, setTotalDiscountAmt] = useState(0);
  const [totalAdjust, setTotalAdjust] = useState(0);
  const [totalQty, setTotalQty] = useState(0);
  const [smCode, setSmCode] = useState("");
  const [smCodeData, setSmCodeData] = useState([]);
  const [cashValue, setCashValue] = useState("CASH");
  const [cashAmt, setCashAmt] = useState(0);
  const [cardAmt, setCardAmt] = useState(0);
  const [upiAmt, setUpiAmt] = useState(0);
  const [onlineAmt, setOnlineAmt] = useState(0);
  const [creditAmt, setCreditAmt] = useState(0);
  const [userArea, setUserArea] = useState();
  const [userName, setUserName] = useState();
  const [singleImage, setSingleImage] = useState();
  const [gstNo, setGstNo] = useState("");
  const [gstValue, setGstValue] = useState();
  const [userPhone, setUserPhone] = useState("");
  const [conditions, setConditions] = useState("");
  const [ins, setIns] = useState("");
  const [ins1, setIns1] = useState("");
  const [ins2, setIns2] = useState("");
  const [ins3, setIns3] = useState("");
  const [ins4, setIns4] = useState("");
  const [ins5, setIns5] = useState("");
  const [ins6, setIns6] = useState("");
  const [address, setAddress] = useState("");
  const [address1, setAddress1] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [tagNo, setTagNo] = useState();
  const [exchangeNo, setExchangeNo] = useState();
  const [estNo, setEstNo] = useState();
  const [selectJewelType, setSelectJewelType] = useState("RETAIL");
  const [billNo, setBillNo] = useState();
  const [billDate, setBillDate] = useState();
  const [hNo, setHNo] = useState();
  const [invNo, setInvNo] = useState();
  const [holdNo, setHoldNo] = useState();
  const [messageApi, contextHolder] = message.useMessage();
  const [visibleHis, setVisibleHis] = useState(false);
  const [payMode, setPayMode] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [phoneNo, setPhoneNo] = useState();
  const [cPhoneNo, setCPhoneNo] = useState();
  const [open, setOpen] = useState(false);
  const [customerSelectOpen, setCustomerSelectOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchHoldText, setSearchHoldText] = useState("");
  const [itemDetailsOpen, setItemDetailsOpen] = useState(false);
  const [itemDetailsData, setItemDetailsData] = useState({});
  const [itemDesignCode, setItemDEsignCode] = useState();
  const [itemExchangeDetailsOpen, setItemExchangeDetailsOpen] = useState(false);
  const [itemExchangeDetailsData, setItemExchangeDetailsData] = useState({});
  const [itemExchangeDesignCode, setItemExchangeDEsignCode] = useState();
  const [base64QR, setBase64QR] = useState("");
  const [base64, setBase64] = useState();
  const [base64Images, setBase64Images] = useState();
  const [customerArea, setCustomerArea] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerMode, setCustomerMode] = useState("new");
  const [prvBalance, setPrvBalance] = useState(0);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdData, setHoldData] = useState([]);
  const [filteredHoldData, setFilteredHoldData] = useState([]);
  const [selectedHoldRowKeys, setSelectedHoldRowKeys] = useState([]);
  const [homeDetailsOpen, setHomeDetailsOpen] = useState(false);
  const [productData, setProductData] = useState([]);
  const [homeExchangeDetailsOpen, setHomeExchangeDetailsOpen] = useState(false);
  const [qty, setQty] = useState(0);
  const [amount, setAmount] = useState(0);
  const [disCount, setDisCount] = useState(0);
  const [rate, setRate] = useState(0);
  const [disAmt, setDisAmt] = useState(0);
  const [productName, setProductName] = useState("");
  const [designCode, setDesignCode] = useState("");
  const [pTypeName, setPTypeName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [taansOpen, setTaansOpen] = useState(false);
  const [taansData, setTaansData] = useState([]);
  const [exchangeTaansOpen, setExchangeTaansOpen] = useState(false);
  const [exchangeTaansData, setExchangeTaansData] = useState([]);
  const [qtyData, setQtyData] = useState([]);
  const [exchangeQtyData, setExchangeQtyData] = useState([]);
  const [mobile, setMobile] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [offerAOpen, setOfferAOpen] = useState(false);
  const [offerAData, setOfferAData] = useState([]);
  const [offerARate, setOfferARate] = useState(0);
  const [offerBOpen, setOfferBOpen] = useState(false);
  const [offerBData, setOfferBData] = useState([]);
  const [offerBRate, setOfferBRate] = useState(0);
  const [comm, setComm] = useState(0);
  const [qrCodeOpen, setQrCodeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printSaving, setPrintSaving] = useState(false);
  const [thermalPrinter, setThermalPrinter] = useState("");
  const [thermalPrinterDetails, setThermalPrinterDetails] = useState({});
  const [formValues, setFormValues] = useState({
    mobile1: "",
    city: "",
    name: "",
    mobile2: "",
    phone: "",
    address1: "",
    address2: "",
    address3: "",
    address4: "",
    state: "ANDHRA PRADESH",
    stateCode: "37",
    gstin: "",
    panNo: "",
    idProof: "",
    dob: "",
    anniversary: "",
    locationType: "LOCAL",
  });
  const logoImage = localStorage.getItem("singleImage");
  const pdfModule = localStorage.getItem("pdfModule");
  const localIp = localStorage.getItem("ipAddress");
  const tenantName = localStorage.getItem("tenantName");
  const loginName = localStorage.getItem("loginName");
  const upiId = localStorage.getItem("upiId");
  const adminType = localStorage.getItem("adminType");
  const totalAmt = totalNetAmount - cardAmt - upiAmt - onlineAmt - creditAmt;
  const invPaidAmt = totalNetAmount - creditAmt;

  const isValidValue = (val) => {
    return (
      val !== null &&
      val !== undefined &&
      val !== "-" &&
      String(val).trim() !== "" &&
      String(val) !== "undefined"
    );
  };

  const upiLink = `upi://pay?pa=${upiId}&am=${upiAmt}&cu=INR`;

  dayjs.extend(utc);
  dayjs.extend(timezone);
  const isoUTC = dayjs().utc().startOf("day").toISOString();

  const totalBalance = prvBalance + creditAmt;

  const invNoAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetMaxNumberInTable?tableName=CREDIT_BILLMAST&column=BILLNO`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const rawValue = data[0]?.Column1;
        const maxInvNo = Number.isFinite(Number(rawValue))
          ? Number(rawValue)
          : 0;
        const newInvNo = maxInvNo + 1;
        setInvNo(newInvNo);
        return newInvNo;
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const holdNoAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetMaxNumberInTable?tableName=CREDIT_BILLMAST_HOLD&column=BILLNO`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const rawValue = data[0]?.Column1;
        const maxInvNo = Number.isFinite(Number(rawValue))
          ? Number(rawValue)
          : 0;
        const newInvNo = maxInvNo + 1;
        setHoldNo(newInvNo);
        return newInvNo;
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const onlineAccNoAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=ONLINEMODE_MAST`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const card = data.find((item) => item.PTYPE === "CARD");
        const upi = data.find((item) => item.PTYPE === "UPI");
        const online = data.find((item) => item.PTYPE === "ONLINE");
        if (card) setCardAcc(card.accno);
        if (upi) setUpiAcc(upi.accno);
        if (online) setOnlineAcc(online.accno);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const gstCheckAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=PURCHASE_TYPE`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setGstData(data);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const userAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=FIRM_CONFIGURE`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setUserName(data[0]?.FIRMNAME);
        setUserArea(data[0]?.CITY);
        setSingleImage(data[0]?.EPASS2);
        setGstNo(data[0]?.TINNO);
        setGstValue(data[0]?.GST);
        setUserPhone(data[0]?.MOBILE);
        setConditions(data[0]?.INSTTITLE);
        setIns(data[0]?.INST1);
        setIns1(data[0]?.INST2);
        setIns2(data[0]?.INST3);
        setIns3(data[0]?.INST4);
        setIns4(data[0]?.INST5);
        setIns5(data[0]?.INST6);
        setIns6(data[0]?.INST7);
        setAddress(data[0]?.ADD1);
        setAddress1(data[0]?.ADD2);
        setCity(data[0]?.CITY);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const userGetDetailsAPI = async (value) => {
    const mobileValue = isValidValue(value) ? value : phoneNo;
    if (!isValidValue(mobileValue)) return;
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CUSTOMER&where=MOBILENO='${value ? value : phoneNo
        }'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setUserDetails(data[0]);
        setFormValues({
          mobile1: data[0]?.Mobileno || "",
          city: data[0]?.Area || "",
          name: data[0]?.Custname || "",
          mobile2: data[0]?.Phoneno || "",
          phone: data[0]?.Phoneno || "",
          address1: data[0]?.add1 || "",
          address2: data[0]?.add2 || "",
          address3: data[0]?.add3 || "",
          address4: data[0]?.add4 || "",
          state: data[0]?.STATE || "ANDHRA PRADESH",
          stateCode: data[0]?.STATECODE || "37",
          gstin: data[0]?.TINNO || "-",
          panNo: "",
          idProof: "",
          dob: data[0]?.dob || "",
          anniversary: data[0]?.ANNVERSARY || "",
          locationType: data[0]?.STATION || "LOCAL",
        });
        setPhoneNo();
        smCodeRef.current?.focus();
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const userDetailsAPI = async (value) => {
    const mobileValue = isValidValue(value) ? value : phoneNo;
    if (!isValidValue(mobileValue)) return;
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CUSTOMER&where=MOBILENO='${value ? value : phoneNo
        }'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setUserDetails(data[0]);
        setCPhoneNo(data[0]?.Mobileno);
        OpeningLedgerBalanceAPI(data[0]?.Mobileno, data[0]?.Custname);
        setFormValues({
          mobile1: data[0]?.Mobileno || "",
          city: data[0]?.Area || "",
          name: data[0]?.Custname || "",
          mobile2: data[0]?.Phoneno || "",
          phone: data[0]?.Phoneno || "",
          address1: data[0]?.add1 || "",
          address2: data[0]?.add2 || "",
          address3: data[0]?.add3 || "",
          address4: data[0]?.add4 || "",
          state: data[0]?.STATE || "ANDHRA PRADESH",
          stateCode: data[0]?.STATECODE || "37",
          gstin: data[0]?.TINNO || "-",
          panNo: "",
          idProof: "",
          dob: data[0]?.dob || "",
          anniversary: data[0]?.ANNVERSARY || "",
          locationType: data[0]?.STATION || "LOCAL",
        });
        setPhoneNo();
        smCodeRef.current?.focus();
      } else {
        setCustomerMode("new");
        setOpen(true);
        setUserDetails({});
        setFormValues({
          mobile1: phoneNo,
          city: "",
          name: "",
          mobile2: "",
          phone: "",
          address1: "",
          address2: "",
          address3: "",
          address4: "",
          state: "ANDHRA PRADESH",
          stateCode: "37",
          gstin: "",
          panNo: "",
          idProof: "",
          dob: "",
          anniversary: "",
          locationType: "LOCAL",
        });
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const handleEditCustomer = () => {
    setFormValues({
      mobile1: userDetails?.Mobileno || "",
      city: userDetails?.Area || "",
      name: userDetails?.Custname || "",
      mobile2: userDetails?.Phoneno || "",
      phone: userDetails?.Phoneno || "",
      address1: userDetails?.add1 || "",
      address2: userDetails?.add2 || "",
      address3: userDetails?.add3 || "",
      address4: userDetails?.add4 || "",
      state: userDetails?.STATE || "ANDHRA PRADESH",
      stateCode: userDetails?.STATECODE || "37",
      gstin: userDetails?.TINNO || "",
      panNo: "",
      idProof: "",
      dob: userDetails?.dob || "",
      anniversary: userDetails?.ANNVERSARY || "",
      locationType: userDetails?.STATION || "LOCAL",
    });
    setCustomerMode("edit");
    setOpen(true);
  };

  const openNewCustomer = () => {
    setCustomerMode("new");
    setFormValues({
      mobile1: "",
      city: "",
      name: "",
      mobile2: "",
      phone: "",
      address1: "",
      address2: "",
      address3: "",
      address4: "",
      state: "ANDHRA PRADESH",
      stateCode: "37",
      gstin: "",
      panNo: "",
      idProof: "",
      dob: "",
      anniversary: "",
      locationType: "LOCAL",
    });
    setOpen(true);
  };

  const handleCustomerSelect = (customer) => {
    setCustomerSelectOpen(false);
    setUserDetails(customer);
    setCPhoneNo(customer.Mobileno);
    OpeningLedgerBalanceAPI(customer.Mobileno, customer.Custname);
    setFormValues({
      mobile1: customer.Mobileno || "",
      city: customer.Area || "",
      name: customer.Custname || "",
      mobile2: customer.Phoneno || "",
      phone: customer.Phoneno || "",
      address1: customer.add1 || "",
      address2: customer.add2 || "",
      address3: customer.add3 || "",
      address4: customer.add4 || "",
      state: customer.STATE || "ANDHRA PRADESH",
      stateCode: customer.STATECODE || "37",
      gstin: customer.TINNO || "-",
      panNo: "",
      idProof: "",
      dob: customer.dob || "",
      anniversary: customer.ANNVERSARY || "",
      locationType: customer.STATION || "LOCAL",
    });
    setTimeout(() => smCodeRef.current?.focus(), 300);
  };

  const smCodeAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=BOYMAST&where=CODE='${smCode}'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      setSmCodeData(data);
      if (!Array.isArray(data) || data.length === 0) {
        setSmCode("");
        message.open({
          type: "error",
          content: <div style={{ fontSize: "20px" }}>Sm Code Not existed</div>,
        });
        return;
      } else {
        tagNoRef.current?.focus();
        message.open({
          type: "success",
          content: (
            <div style={{ fontSize: "20px" }}>
              Sm Code existed{" "}
              <span style={{ color: "red", fontWeight: "bold" }}>
                {data[0]?.NAME}
              </span>
            </div>
          ),
        });
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const qtyAPI = async (no) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CURRENT_STOCK&where=DESIGNCODE='${no}'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      setQtyData((prev) => {
        const filteredNewItems = data
          .filter(
            (item) =>
              !prev.some((prevItem) => prevItem.DesignCode === item.DesignCode),
          )
          .map((item) => ({ DesignCode: item.DesignCode, Qty: item.Qty }));
        return [...prev, ...filteredNewItems];
      });
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const commissionAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=CONFIGURE`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) setComm(data[0]?.COMM);
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const billDataAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CURRENT_STOCK&where=DESIGNCODE='${tagNo}'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (!Array.isArray(data) || data.length === 0) {
        setTagNo();
        message.open({
          type: "error",
          content: (
            <div style={{ fontSize: "20px" }}>Design Code Not existed</div>
          ),
        });
        return;
      }
      let newQtyData = [...qtyData];
      const alreadyExists = newQtyData.some(
        (item) => item.DesignCode === data[0]?.DesignCode,
      );
      if (!alreadyExists) {
        newQtyData = [
          ...newQtyData,
          ...data.map((item) => ({
            DesignCode: item?.DesignCode,
            Qty: item?.Qty,
          })),
        ];
      }
      setQtyData(newQtyData);

      if (data[0]?.taans === true) {
        setTaansOpen(true);
        setTaansData(data.map((item) => ({ ...item, stock: 0 })));
        return;
      }
      if (data[0]?.SMODEL === "A") {
        setOfferAOpen(true);
        setOfferARate(data[0]?.CPrice);
        setOfferAData(
          data.map((item) => ({
            ...item,
            stock: 3,
            CPrice: data[0]?.OQTY3 / 3,
            offer: data[0]?.OQTY3 / 3,
          })),
        );
        return;
      }
      if (data[0]?.SMODEL === "B") {
        setOfferBOpen(true);
        setOfferBRate(data[0]?.Disper);
        setOfferBData(
          data.map((item) => ({
            ...item,
            stock: 3,
            Disper: data[0]?.OQTY3,
            disAmt: (data[0]?.CPrice * data[0]?.OQTY3) / 100,
            offer: data[0]?.CPrice - (data[0]?.CPrice * data[0]?.OQTY3) / 100,
          })),
        );
        return;
      }
      setTagNo();

      setBarCodeData((prevData) => {
        let updatedData = [...prevData];

        data.forEach((newItem) => {
          const designCode = newItem.DesignCode;
          const qtyInfo = newQtyData.find((q) => q.DesignCode === designCode);
          if (!qtyInfo || Number(qtyInfo.Qty) === 0) {
            message.error(`Qty is zero for ${designCode}`);
            return;
          }
          const maxQty = Number(qtyInfo.Qty);
          const existingTotalQty = updatedData
            .filter((item) => item.DesignCode === designCode)
            .reduce((sum, item) => sum + (Number(item.stock) || 0), 0);
          if (existingTotalQty >= maxQty) {
            message.error(`Qty limit reached for ${designCode}`);
            return;
          }
          const gstItem = gstData.find((gst) => gst.PType === newItem.PTYPE);
          let appliedGST = 0;
          if (gstItem?.ALLOWGSTVALUE === true) {
            const offerAmount = newItem.offer ?? 0;
            if (offerAmount <= gstItem.BELLOWVALUE)
              appliedGST = gstItem.BELLOWPER;
            else if (offerAmount >= gstItem.ABOVEVALUE)
              appliedGST = gstItem.ABOVEPER;
          } else {
            appliedGST = newItem?.GST;
          }
          updatedData.push({
            ...newItem,
            stock: 1,
            GSTApplied: appliedGST,
            SMCODE: smCodeData[0]?.CODE ?? "",
          });
        });

        const totalBasic = updatedData.reduce((acc, item) => {
          const offer = Number(item.offer) || 0;
          const qty = Number(item.stock) || 0;
          const gst = Number(item.GSTApplied) || 0;
          const netAmt = qty * offer;
          return acc + netAmt * (100 / (100 + gst));
        }, 0);
        setTotalBasicAmount(totalBasic);

        const totalGst = updatedData.reduce((acc, item) => {
          const offer = Number(item.offer) || 0;
          const qty = Number(item.stock) || 0;
          const gst = Number(item.GSTApplied) || 0;
          const netAmt = qty * offer;
          const basic = netAmt * (100 / (100 + gst));
          return acc + basic * (gst / 100);
        }, 0);
        setTotalBillGstAmt(totalGst);
        setTotalBillCGSTAmt(totalGst / 2);
        setTotalBillSGSTAmt(totalGst / 2);

        const totalAmt = updatedData.reduce(
          (acc, item) =>
            acc + (Number(item.offer) || 0) * (Number(item.stock) || 0),
          0,
        );
        setTotalBillAmount(totalAmt);
        setTotalBillGrossAmount(totalAmt);
        setTotalBillNetAmount(totalAmt);

        const gstGroupedMap = new Map();
        updatedData.forEach((item) => {
          const key = item.GSTApplied;
          const netAmt = item.stock * item.offer;
          const basicAmt = netAmt * (100 / (100 + key));
          const gstAmt = netAmt * (key / 100);
          if (!gstGroupedMap.has(key)) {
            gstGroupedMap.set(key, {
              GST: key,
              CGST_RATE: key / 2,
              Basic_amt: 0,
              CGST_AMT: 0,
              SGST: key / 2,
              SGST_AMT: 0,
              NET_AMT: 0,
            });
          }
          const g = gstGroupedMap.get(key);
          g.Basic_amt += basicAmt;
          g.CGST_AMT += gstAmt / 2;
          g.SGST_AMT += gstAmt / 2;
          g.NET_AMT += netAmt;
        });
        setTotalGstData(Array.from(gstGroupedMap.values()));

        return updatedData;
      });
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const exchangeQtyAPI = async (no) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CURRENT_STOCK&where=DESIGNCODE='${no}'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      setExchangeQtyData((prev) => {
        const filteredNewItems = data
          .filter(
            (item) =>
              !prev.some((prevItem) => prevItem.DesignCode === item.DesignCode),
          )
          .map((item) => ({ DesignCode: item.DesignCode, Qty: item.Qty }));
        return [...prev, ...filteredNewItems];
      });
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const exchangeDataAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CURRENT_STOCK&where=DESIGNCODE='${exchangeNo}'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (!Array.isArray(data) || data.length === 0) {
        setExchangeNo();
        message.open({
          type: "error",
          content: (
            <div style={{ fontSize: "20px" }}>Design Code Not existed</div>
          ),
        });
        return;
      }
      if (data[0]?.taans === true) {
        setExchangeTaansOpen(true);
        setExchangeTaansData(data.map((item) => ({ ...item, stock: 0 })));
        return;
      }
      setExchangeNo();

      setExchangeData((prevData) => {
        let updatedData = [...prevData];
        data.forEach((newItem) => {
          const gstItem = gstData.find((gst) => gst.PType === newItem.PTYPE);
          let appliedGST = 0;
          if (gstItem?.ALLOWGSTVALUE === true) {
            const offerAmount = newItem.offer ?? 0;
            if (offerAmount <= gstItem.BELLOWVALUE)
              appliedGST = gstItem.BELLOWPER;
            else if (offerAmount >= gstItem.ABOVEVALUE)
              appliedGST = gstItem.ABOVEPER;
          } else {
            appliedGST = newItem?.GST;
          }
          updatedData.push({
            ...newItem,
            stock: 1,
            GSTApplied: appliedGST,
            SMCODE: smCodeData[0]?.CODE ?? "",
          });
        });

        const totalBasic = updatedData.reduce((acc, item) => {
          const offer = Number(item.offer) || 0;
          const qty = Number(item.stock) || 0;
          const gst = Number(item.GSTApplied) || 0;
          const netAmt = qty * offer;
          return acc + netAmt * (100 / (100 + gst));
        }, 0);
        setTotalExchangeBasicAmount(totalBasic);

        const totalGst = updatedData.reduce((acc, item) => {
          const offer = Number(item.offer) || 0;
          const qty = Number(item.stock) || 0;
          const gst = Number(item.GSTApplied) || 0;
          const netAmt = qty * offer;
          const basic = netAmt * (100 / (100 + gst));
          return acc + basic * (gst / 100);
        }, 0);
        setTotalExchangeGstAmt(totalGst);
        setTotalExchangeCGSTAmt(totalGst / 2);
        setTotalExchangeSGSTAmt(totalGst / 2);

        const totalAmt = updatedData.reduce(
          (acc, item) =>
            acc + (Number(item.offer) || 0) * (Number(item.stock) || 0),
          0,
        );
        setTotalExchangeAmount(totalAmt);
        setTotalExchangeGrossAmount(totalAmt);
        setTotalExchangeNetAmount(totalAmt);

        const gstGroupedMap = new Map();
        updatedData.forEach((item) => {
          const key = item.GSTApplied;
          const netAmt = item.stock * item.offer;
          const basicAmt = netAmt * (100 / (100 + key));
          const gstAmt = netAmt * (key / 100);
          if (!gstGroupedMap.has(key)) {
            gstGroupedMap.set(key, {
              GST: key,
              CGST_RATE: key / 2,
              Basic_amt: 0,
              CGST_AMT: 0,
              SGST: key / 2,
              SGST_AMT: 0,
              NET_AMT: 0,
            });
          }
          const g = gstGroupedMap.get(key);
          g.Basic_amt += basicAmt;
          g.CGST_AMT += gstAmt / 2;
          g.SGST_AMT += gstAmt / 2;
          g.NET_AMT += netAmt;
        });
        setTotalExchangeGstData(Array.from(gstGroupedMap.values()));

        return updatedData;
      });
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const homeBillData = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=PRODUCT_MASTER&where=SHORTKEY='${tagNo}'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (!Array.isArray(data) || data.length === 0) {
        setTagNo();
        return;
      }
      setTagNo();
      setProductData(data);
      setDesignCode(data[0]?.SHORTKEY);
      setHomeDetailsOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  const homeExchangeData = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=PRODUCT_MASTER&where=SHORTKEY='${exchangeNo}'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (!Array.isArray(data) || data.length === 0) {
        setExchangeNo();
        return;
      }
      setExchangeNo();
      setProductData(data);
      setDesignCode(data[0]?.SHORTKEY);
      setHomeExchangeDetailsOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  const OpeningLedgerBalanceAPI = async (phone, name) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=TRANS_ENTRY_DATA_CUST&where=AGENTNAME='${phone}' AND PARTYNAME='${name}'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const nama = data.reduce(
          (acc, item) => acc + (Number(item.NAMA) || 0),
          0,
        );
        const jama = data.reduce(
          (acc, item) => acc + (Number(item.JAMA) || 0),
          0,
        );
        setPrvBalance(nama - jama);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  const billNoDate = new Date(
    dayjs(billDate).startOf("day").format("YYYY-MM-DD"),
  ).toISOString();

  const createBarcodeData = async (inv) => {
    const requestBody = barCodeData.map((item, index) => {
      const basicAmt =
        item.stock * item.offer * (100 / (100 + item.GSTApplied));
      const commValue = Number(item.stock * item.offer);
      const commission = (commValue * comm) / 100;
      const commToken = item?.TOKEN > 0 ? 0 : commission;
      return {
        billno: billNo ? billNo : inv,
        billdate: billDate
          ? dayjs(billDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        street: userDetails?.Area || "-",
        custName: userDetails?.Custname || "-",
        sno: index + 1,
        itemcode: item?.DesignCode || "",
        itemname: item?.ProdName || "",
        code: item?.Code || "",
        compName: item?.CompName || "",
        groupName: item?.GroupName || "",
        smcode: String(item?.SMCODE) ?? "-",
        qty: item?.stock || 0,
        rate: item?.CPrice || 0,
        disper: item?.Disper || 0,
        disAmt: item?.Disamt || 0,
        actrate: item?.offer || 0,
        amount: item?.offer * item?.stock || 0,
        hid: item?.HID || 0,
        dPrice: item?.DPrice || 0,
        comm: Number(commToken) || 0,
        token: item?.TOKEN || 0,
        ptype: item?.PTYPE || "",
        area: item?.Area || "",
        partyname: item?.Partyname || "",
        vatper: 0,
        vatamt: 0,
        floor: item?.FLOOR || "",
        estno: "-",
        station: "mobile",
        hsncode: item?.HSNCODE || "",
        gst: item?.GSTApplied || 0,
        gstamt: basicAmt * (item?.GSTApplied / 100) || 0,
        cgstrate: item?.GSTApplied / 2 || 0,
        cgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 || 0,
        sgstrate: item?.GSTApplied / 2 || 0,
        sgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 || 0,
        igstrate: item?.IGSTRATE || 0,
        igstamt: item?.IGSTAMT || 0,
        basicamt: Number(basicAmt) ?? 0,
        serialno: 0,
        brandcode: "0",
        salestype: "BILLING",
        prodsize: item?.Prodsize || "",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: item?.stock || 0,
        refcode: "-",
        boxes: 0,
        loosepcs: 0,
        pfcharges: 0,
        cessper: 0,
        cessamt: 0,
        packpcs: 0,
        caseprice: 0,
        pcsprice: 0,
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: item?.TOKEN || 0,
        ustatus: false,
        estbillno: false,
        cloud_upload: true,
      };
    });
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/InsertCreditBillData`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createBarcodeDet = async (inv) => {
    const requestBody = barCodeData.map((item, index) => {
      const basicAmt =
        item.stock * item.offer * (100 / (100 + item.GSTApplied));
      const rate =
        item?.SMODEL === "A"
          ? offerARate
          : item?.SMODEL === "B"
            ? offerBRate
            : 0;
      const commValue = Number(item.stock * item.offer);
      const commission = (commValue * comm) / 100;
      const commToken = item?.TOKEN > 0 ? 0 : commission;
      return {
        billno: billNo ? billNo : inv,
        billdate: billDate
          ? dayjs(billDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        street: userDetails?.Area || "-",
        custName: userDetails?.Custname || "-",
        sno: index + 1,
        itemcode: String(item?.DesignCode) ?? "",
        itemname: String(item?.ProdName) ?? "",
        code: String(item?.Code) ?? "",
        compName: String(item?.CompName) ?? "",
        groupName: String(item?.GroupName) ?? "",
        smcode: String(item?.SMCODE) ?? "-",
        qty: Number(item?.stock) ?? 0,
        rate: Number(item?.CPrice) ?? 0,
        disper: Number(item?.Disper) ?? 0,
        disAmt: Number(item?.Disamt) ?? 0,
        actrate: String(item?.offer ?? ""),
        amount: String(item?.offer * item?.stock ?? ""),
        hid: Number(item?.HID) ?? 0,
        dPrice: Number(item?.DPrice) ?? 0,
        comm: Number(commToken) ?? 0,
        token: Number(item?.TOKEN) ?? 0,
        ptype: String(item?.PTYPE) ?? "",
        area: String(item?.Area) ?? "",
        partyname: String(item?.Partyname) ?? "",
        type: "B",
        vatper: 0,
        vatamt: 0,
        floor: String(item?.FLOOR) ?? "",
        wsalebill: false,
        appno: 0,
        invno: String(item?.InvNO) ?? "",
        invdate: billDate
          ? dayjs(billDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        colour: String(item?.COLOUR) ?? "",
        fitting: String(item?.FITTING) ?? "",
        sleves: String(item?.SLEVES) ?? "",
        ecode: String(item?.ECODE) ?? "",
        prodsize: String(item?.Prodsize) ?? "",
        station: String(item?.SMODEL) ?? "-",
        iteM_DIS: Number(item?.Disamt) ?? 0,
        iteM_ACTRATE: Number(item?.offer) ?? 0,
        scheck: false,
        hsncode: String(item?.HSNCODE) ?? "",
        gst: Number(item?.GSTApplied) ?? 0,
        gstamt: basicAmt * (item?.GSTApplied / 100) ?? 0,
        cgstrate: item?.GSTApplied / 2,
        cgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 ?? 0,
        sgstrate: item?.GSTApplied / 2,
        sgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 ?? 0,
        igstrate: 0,
        igstamt: 0,
        basicamt: Number(basicAmt) ?? 0,
        serialno: 0,
        brandcode: "0",
        salestype: "BILLING",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: Number(item?.stock) ?? 0,
        refcode: "-",
        state: userDetails?.STATE || "",
        statecode: userDetails?.STATECODE || "",
        tinno: "-",
        idproof: "-",
        panno: "-",
        boxes: Number(item?.BOXES) ?? 0,
        loosepcs: Number(item?.LOOSEPCS) ?? 0,
        pfcharges: 0,
        cessper: Number(rate),
        cessamt: 0,
        packpcs: Number(item?.PACKPCS) || 0,
        caseprice: Number(item?.OQTY3),
        pcsprice: Number(item?.OQTY2),
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: 0,
        ustatus: false,
        gstin: "-",
        estbillno: false,
        cloud_upload: true,
      };
    });
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/InsertBillItemsDet`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createBarcodeMast = async (inv) => {
    const requestBody = {
      billno: billNo ? billNo : inv,
      billdate: billDate ? billNoDate : date.toISOString(),
      street: userDetails?.Area || "-",
      custName: userDetails?.Custname || "-",
      add1: userDetails?.add1 || "-",
      phoneNo: userDetails?.Mobileno || "-",
      totamt: Number(totalAmount),
      discount: Number(totalDiSAmt),
      advAmt: Number(cashAmt),
      finalamt: Number(creditAmt),
      billtype: cashValue === "CREDIT" ? "CREDIT" : "CASH",
      lessAmt: 0,
      smcode: "-",
      pstatus: false,
      ebillno: 0,
      mode:
        cashValue === "CREDIT"
          ? "CASH"
          : cashValue === "CASH"
            ? "CASH"
            : "CARD",
      accNO: "-",
      cardno: cashValue === "CREDIT" ? "0" : cashValue === "CASH" ? "0" : "-",
      cardamt: Number(cardAmt || upiAmt || onlineAmt || 0),
      sTax: 0,
      flrcode: "-",
      esmCode: "-",
      days: 0,
      cStatus: true,
      card: 0,
      hid: 0,
      estno: 0,
      adD2: userDetails?.add2 || "-",
      billtime: billDate ? billNoDate : date,
      uname: loginName ?? "-",
      cancell: "NO",
      ctime: billDate ? billNoDate : date,
      cuname: "-",
      station: "mobile",
      billstatus: false,
      funame: "-",
      fstation: "-",
      reccash: 0,
      refcash: 0,
      wsalebill: false,
      appno: 0,
      ccause: "-",
      edisamt: 0,
      chqdate: billDate ? billNoDate : date.toISOString(),
      narration: "-",
      invpayableamt: Number(totalNetAmount),
      invpaidamt: Number(invPaidAmt),
      invrtnamt: 0,
      invbalamt: Number(creditAmt),
      invstatus: true,
      extradiscount: 0,
      prevpoints: 0,
      prevamt: 0,
      billpoints: 0,
      billvalue: 0,
      redimpoints: 0,
      redimamt: 0,
      totpoints: 0,
      totvalue: 0,
      prevcardno: "-",
      adjustments: 0,
      totgst: 0,
      totogstamt: Number(totalGstAmt),
      totcgstrate: 0,
      totcgstamt: Number(totalCGSTAmt),
      totsgstrate: 0,
      totsgstamt: Number(totalSGSTAmt),
      totigstrate: 0,
      totigstamt: 0,
      serialno: 0,
      salestype: "BILLING",
      custstation: userDetails?.STATION || "LOCAL",
      transport: "-",
      custtype: "CUSTOMER",
      stype: "BILLING",
      lrno: "-",
      lrdate: "-",
      shipstreet: "-",
      shipcustname: "-",
      shipadD1: "-",
      shipadD2: "-",
      shipphoneno: "-",
      shiptinno: "-",
      noofbags: "-",
      orderno: "-",
      orderdate: "-",
      dateofsupply: "-",
      placeofsupply: "-",
      company: "-",
      place: "-",
      branch: "-",
      bankaccno: "-",
      ifsccode: "-",
      state: "-",
      statecode: "-",
      shipstate: "-",
      shipstatecode: "-",
      bankname: "-",
      beatarea: "-",
      orddate: "-",
      gstin: "-",
      totbasicamt: Number(totalBasicAmount),
      totamtaftax: 0,
      salesper: 0,
      salesamt: 0,
      grossamt: Number(totalNetAmount),
      roundoff: 0,
      netamt: Number(totalNetAmount),
      tinno: "-",
      idproof: "-",
      panno: "-",
      purchno: "-",
      disamt: Number(totalDiscountAmt) || 0,
      schemeamt: 0,
      disper: Number(disPer) || 0,
      pinvno: "-",
      m_BTYPE: "-",
      m_BNO: "-",
      m_BDATE: "-",
      includinggst: false,
      acity: "-",
      agentname: "-",
      ustatus: false,
      estbillno: false,
      paymode: "-",
      totqty: totalQty,
      refundamt: 0,
      paidcard: Number(cardAmt),
      paidcheque: 0,
      paidonline: Number(onlineAmt),
      paidupi: Number(upiAmt),
      paidcash: Number(cashAmt),
      balance: Number(creditAmt),
      ctype: "RETAIL",
      withgst: false,
      marginper: 0,
      cloud_upload: true,
      couponredem: 0,
    };
    try {
      const response = await axios.post(
        `${CREATE_jwel}/api/ERP/InsertCreditBillMast`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
      if (response?.status === 200) {
        invNoAPI();
        commissionAPI();
        handleReset();
        setCashValue("CASH");
        setCardAmt(0);
        setUpiAmt(0);
        setOnlineAmt(0);
        setCreditAmt(0);
        smCodeRef.current?.focus();
      }
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createBarcodeEXBDet = async (inv) => {
    const requestBody = exchangeData.map((item, index) => {
      const basicAmt =
        item.stock * item.offer * (100 / (100 + item.GSTApplied));
      const gstAmt = basicAmt * (item.GSTApplied / 100);
      const cGstAmt = (basicAmt * (item.GSTApplied / 100)) / 2;
      const sGstAmt = (basicAmt * (item.GSTApplied / 100)) / 2;
      const commValue = Number(item.stock * item.offer);
      const commission = (commValue * comm) / 100;
      const commToken = item?.TOKEN > 0 ? 0 : commission;
      return {
        billno: billNo ? billNo : inv,
        billdate: billDate
          ? dayjs(billDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        street: userDetails?.Area || "",
        custName: userDetails?.Custname || "",
        sno: index + 1,
        itemcode: String(item?.DesignCode) ?? "",
        itemname: String(item?.ProdName) ?? "",
        code: String(item?.Code) ?? "",
        compName: String(item?.CompName) ?? "",
        groupName: String(item?.GroupName) ?? "",
        smcode: String(item?.SMCODE) ?? "",
        qty: Number(-item?.stock) ?? 0,
        rate: Number(item?.CPrice) ?? 0,
        disper: Number(item?.Disper) ?? 0,
        disAmt: Number(item?.Disamt) ?? 0,
        actrate: String(item?.offer ?? ""),
        amount: String(item?.offer * item?.stock ?? ""),
        hid: Number(item?.HID) ?? 0,
        dPrice: Number(item?.DPrice) ?? 0,
        comm: Number(-commToken) ?? 0,
        token: Number(-item?.TOKEN) ?? 0,
        ptype: String(item?.PTYPE) ?? "",
        area: String(item?.Area) ?? "",
        partyname: String(item?.Partyname) ?? "",
        type: "EB",
        vatper: 0,
        vatamt: 0,
        floor: String(item?.FLOOR) ?? "",
        wsalebill: false,
        appno: 0,
        invno: String(item?.InvNO) ?? "",
        invdate: billDate
          ? dayjs(billDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        colour: String(item?.COLOUR) ?? "",
        fitting: String(item?.FITTING) ?? "",
        sleves: String(item?.SLEVES) ?? "",
        ecode: String(item?.ECODE) ?? "",
        prodsize: String(item?.Prodsize) ?? "",
        station: "-",
        iteM_DIS: Number(item?.Disamt) ?? 0,
        iteM_ACTRATE: Number(item?.offer) ?? 0,
        scheck: false,
        hsncode: String(item?.HSNCODE) ?? "",
        gst: Number(item?.GSTApplied) ?? 0,
        gstamt: Number(-gstAmt) ?? 0,
        cgstrate: item?.GSTApplied / 2,
        cgstamt: Number(-cGstAmt) ?? 0,
        sgstrate: item?.GSTApplied / 2,
        sgstamt: Number(-sGstAmt) ?? 0,
        igstrate: 0,
        igstamt: 0,
        basicamt: Number(-basicAmt) ?? 0,
        serialno: 0,
        brandcode: "0",
        salestype: "BILLING",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: Number(-item?.stock) ?? 0,
        refcode: "-",
        state: userDetails?.STATE || "",
        statecode: userDetails?.STATECODE || "",
        tinno: "-",
        idproof: "-",
        panno: "-",
        boxes: Number(item?.BOXES) ?? 0,
        loosepcs: Number(item?.LOOSEPCS) ?? 0,
        pfcharges: 0,
        cessper: 0,
        cessamt: 0,
        packpcs: Number(item?.PACKPCS) || 0,
        caseprice: 0,
        pcsprice: 0,
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: 0,
        ustatus: false,
        gstin: "-",
        estbillno: false,
        cloud_upload: true,
      };
    });
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/InsertBillItemsDet`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const paymentModeAPI = async (inv) => {
    let requestBody = [];
    const commonFields = {
      entrydate: billDate
        ? dayjs(billDate).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      entryno: billNo ? billNo : inv,
      vno: String(billNo ? billNo : inv),
      vdate: billDate
        ? dayjs(billDate).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      scode: 4,
      description: "-",
      salestype: "BILLING",
      hid: 0,
      sno: 0,
      cloud_upload: true,
    };

    if (cashValue === "CASH") {
      requestBody = [
        {
          ...commonFields,
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: `INVNO:${billNo ? billNo : inv}@CASH-SALE`,
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "   SALES",
          stype: "CASH SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: `INVNO:${billNo ? billNo : inv}@CASH-SALE`,
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
          paymode: "-",
        },
      ];
    } else if (
      (cashValue === "CARD" || cashValue === "UPI" || cashValue === "ONLINE") &&
      totalAmt === 0
    ) {
      requestBody = [
        {
          ...commonFields,
          ledgername: "ONLINE SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: `INVNO:${billNo ? billNo : inv}@ONLINE-SALE`,
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "ONLINE SALES",
          stype: "ONLINE SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername:
            cashValue === "CARD"
              ? cardAcc
              : cashValue === "UPI"
                ? upiAcc
                : onlineAcc,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: `INVNO:${billNo ? billNo : inv}@${cashValue === "CARD" ? "CARD-SALE" : cashValue === "UPI" ? "UPI-SALE" : "ONLINE-SALE"}`,
          jamaamount: 0,
          namaamount:
            cashValue === "CARD"
              ? Number(cardAmt)
              : cashValue === "UPI"
                ? Number(upiAmt)
                : Number(onlineAmt),
          tranS_TYPE: "ONLINE SALES",
          stype: "BANK TRANS",
          paymode: cashValue,
        },
      ];
    } else if (
      (cashValue === "CARD" || cashValue === "UPI" || cashValue === "ONLINE") &&
      totalAmt > 0
    ) {
      requestBody = [
        {
          ...commonFields,
          ledgername: "ONLINE SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: `INVNO:${billNo ? billNo : inv}@ONLINE-SALE`,
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "ONLINE SALES",
          stype: "ONLINE SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername:
            cashValue === "CARD"
              ? cardAcc
              : cashValue === "UPI"
                ? upiAcc
                : onlineAcc,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: `INVNO:${billNo ? billNo : inv}@${cashValue === "CARD" ? "CARD-SALE" : cashValue === "UPI" ? "UPI-SALE" : "ONLINE-SALE"}`,
          jamaamount: 0,
          namaamount:
            cashValue === "CARD"
              ? Number(cardAmt)
              : cashValue === "UPI"
                ? Number(upiAmt)
                : Number(onlineAmt),
          tranS_TYPE: "CASH SALES",
          stype: "BANK TRANS",
          paymode: cashValue,
        },
        {
          ...commonFields,
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: `INVNO:${billNo ? billNo : inv}@CASH-SALE`,
          jamaamount: totalNetAmount - cardAmt - upiAmt - onlineAmt - creditAmt,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
          paymode: "-",
        },
      ];
    } else if (cashValue === "CREDIT" && creditAmt > 0 && cashAmt > 0) {
      requestBody = [
        {
          ...commonFields,
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: `INVNO:${billNo ? billNo : inv}@CASH-SALE`,
          jamaamount: Number(totalNetAmount),
          namaamount: 0,
          tranS_TYPE: "   SALES",
          stype: "CASH SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: `INVNO:${billNo ? billNo : inv}@CASH-SALE`,
          jamaamount: Number(cashAmt),
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: userDetails?.Custname,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: `INVNO:${billNo ? billNo : inv}@CASH-SALE`,
          jamaamount: 0,
          namaamount: Number(creditAmt),
          tranS_TYPE: "CASH SALES",
          stype: "CUSTOMER DUE",
          paymode: "-",
        },
      ];
    } else if (cashValue === "CREDIT" && creditAmt > 0) {
      requestBody = [
        {
          ...commonFields,
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: `INVNO:${billNo ? billNo : inv}@CASH-SALE`,
          jamaamount: Number(totalNetAmount),
          namaamount: 0,
          tranS_TYPE: "   SALES",
          stype: "CASH SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: userDetails?.Custname,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: `INVNO:${billNo ? billNo : inv}@CASH-SALE`,
          jamaamount: 0,
          namaamount: Number(creditAmt),
          tranS_TYPE: "CASH SALES",
          stype: "CUSTOMER DUE",
          paymode: "-",
        },
      ];
    }

    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/InsertSingleEntryData`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const onlinePayModeAPI = async (inv) => {
    const requestBody = [
      {
        billno: billNo ? Number(billNo) : Number(inv),
        billdate: billDate ? billNoDate : date.toISOString(),
        bal: Number(totalNetAmount),
        sno: 1,
        paymode: cashValue,
        accno:
          cashValue === "CARD"
            ? cardAcc
            : cashValue === "UPI"
              ? upiAcc
              : onlineAcc,
        narr: "-",
        amt:
          cashValue === "CARD"
            ? Number(cardAmt)
            : cashValue === "UPI"
              ? Number(upiAmt)
              : Number(onlineAmt),
        cntrname: "",
        stype: "SALES",
        ptype: cashValue,
        cloud_upload: true,
      },
    ];
    try {
      await axios.post(`${CREATE_jwel}/api/ERP/InsertORGCardDet`, requestBody, {
        headers: { "Content-Type": "application/json", tenantName: tenantName },
      });
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createLedgerBalanceAPI = async (inv) => {
    const requestBody = [
      {
        area: userDetails?.Area || "-",
        partyname: userDetails?.Custname || "-",
        billno: billNo ? String(billNo) : String(inv),
        billdate: billDate ? billNoDate : date.toISOString(),
        entryno: billNo ? billNo : inv,
        entrydate: billDate ? billNoDate : date.toISOString(),
        particulars: "-",
        jamaqty: 0,
        namaqty: Number(totalQty) ?? 0,
        jama: 0,
        nama: Number(creditAmt) ?? 0,
        narration: "-",
        hid: 0,
        agentcity: "-",
        agentname: String(userDetails?.Mobileno) || "-",
        tranS_TYPE: "SALES INVOICE",
        cusT_TYPE: "CUSTOMER",
        sno: 0,
        pstatus: "",
        salestype: "BILLING",
        chequeno: "-",
        chqdate: billDate ? billNoDate : date.toISOString(),
        chqamt: 0,
        chqstatus: false,
        accno: "-",
        chqcancell: "NO",
        description: "-",
        cloud_upload: false,
      },
    ];
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/InsertTransEntryDataCust`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createExchangeDet = async (inv) => {
    const requestBody = exchangeData.map((item, index) => {
      const basicAmt =
        item.stock * item.offer * (100 / (100 + item.GSTApplied));
      const commValue = Number(item.stock * item.offer);
      const commission = (commValue * comm) / 100;
      const commToken = item?.TOKEN > 0 ? 0 : commission;
      return {
        billno: billNo ? billNo : inv,
        billdate: billDate
          ? dayjs(billDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        street: userDetails?.Area || "",
        custName: userDetails?.Custname || "",
        sno: index + 1,
        itemcode: String(item?.DesignCode) ?? "",
        itemname: String(item?.ProdName) ?? "",
        code: String(item?.Code) ?? "",
        compName: String(item?.CompName) ?? "",
        groupName: String(item?.GroupName) ?? "",
        smcode: String(item?.SMCODE) ?? "",
        qty: Number(item?.stock) ?? 0,
        rate: Number(item?.CPrice) ?? 0,
        disper: Number(item?.Disper) ?? 0,
        disAmt: Number(item?.Disamt) ?? 0,
        actrate: String(item?.offer ?? ""),
        amount: String(item?.offer * item?.stock ?? ""),
        hid: Number(item?.HID) ?? 0,
        dPrice: Number(item?.DPrice) ?? 0,
        comm: Number(commToken) ?? 0,
        token: Number(item?.TOKEN) ?? 0,
        ptype: String(item?.PTYPE) ?? "",
        area: String(item?.Area) ?? "",
        partyname: String(item?.Partyname) ?? "",
        type: "B",
        vatper: 0,
        vatamt: 0,
        floor: String(item?.FLOOR) ?? "",
        wsalebill: false,
        appno: 0,
        invno: String(item?.InvNO) ?? "",
        invdate: billDate
          ? dayjs(billDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        colour: String(item?.COLOUR) ?? "",
        fitting: String(item?.FITTING) ?? "",
        sleves: String(item?.SLEVES) ?? "",
        ecode: String(item?.ECODE) ?? "",
        prodsize: String(item?.Prodsize) ?? "",
        station: "-",
        iteM_DIS: Number(item?.Disamt) ?? 0,
        iteM_ACTRATE: Number(item?.offer) ?? 0,
        scheck: false,
        hsncode: String(item?.HSNCODE) ?? "",
        gst: Number(item?.GSTApplied) ?? 0,
        gstamt: basicAmt * (item?.GSTApplied / 100) ?? 0,
        cgstrate: item?.GSTApplied / 2,
        cgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 ?? 0,
        sgstrate: item?.GSTApplied / 2,
        sgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 ?? 0,
        igstrate: 0,
        igstamt: 0,
        basicamt: Number(basicAmt) ?? 0,
        serialno: 0,
        brandcode: "0",
        salestype: "BILLING",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: Number(item?.stock) ?? 0,
        refcode: "-",
        state: userDetails?.STATE || "",
        statecode: userDetails?.STATECODE || "",
        tinno: "-",
        idproof: "-",
        panno: "-",
        boxes: Number(item?.BOXES) ?? 0,
        loosepcs: Number(item?.LOOSEPCS) ?? 0,
        pfcharges: 0,
        cessper: 0,
        cessamt: 0,
        packpcs: Number(item?.PACKPCS) || 0,
        caseprice: 0,
        pcsprice: 0,
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: 0,
        ustatus: false,
        gstin: "-",
        estbillno: false,
        cloud_upload: true,
      };
    });
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/InsertBillItemsDetExchange`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createStockAddAPI = async () => {
    const requestBody = updatedData.map((item) => ({
      code: item?.DesignCode,
      qty: Number(item?.stock),
    }));
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/StockUpdate?action=ADD`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createStockUpdateAPI = async () => {
    const requestBody = barCodeData.map((item) => ({
      code: item?.DesignCode,
      qty: Number(item?.stock),
    }));
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/StockUpdate?action=ABC`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createExchangeStockAddAPI = async () => {
    const requestBody = exchangeData.map((item) => ({
      code: item?.DesignCode,
      qty: Number(item?.stock),
    }));
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/StockUpdate?action=ADD`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createExchangeStockUpdateAPI = async () => {
    const requestBody = exchangeUpdatedData.map((item) => ({
      code: item?.DesignCode,
      qty: Number(item?.stock),
    }));
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/StockUpdate?action=ABC`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createCustomerDetailsAPI = async (value) => {
    const requestBody = [
      {
        street: "-",
        custname: value?.name || "-",
        add1: value?.address1 || "-",
        add2: value?.address2 || "-",
        add3: value?.address3 || "-",
        area: value?.city || "-",
        phoneno: value?.phone || "-",
        mobileno: value?.mobile1 || "-",
        cardno: "",
        mstatus: "",
        occupation: "",
        education: "",
        college: "",
        discount: 0,
        dob: value?.dob || "",
        annversary: value?.anniversary || "",
        state: value?.state || "-",
        gender: "",
        district: "",
        website: "",
        category: "",
        fax: "-",
        add4: value?.address4 || "-",
        tinno: value?.gstin ?? "-",
        points: 0,
        statecode: value?.stateCode || "",
        station: value?.locationType || "",
        cloud_upload: false,
      },
    ];
    try {
      await axios.post(`${CREATE_jwel}/api/ERP/InsertCustomer`, requestBody, {
        headers: { "Content-Type": "application/json", tenantName: tenantName },
      });
      userDetailsAPI(value?.mobile1);
      setFormValues({});
      setOpen(false);
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const updateCustomerDetailsAPI = async (value) => {
    const requestBody = {
      street: "-",
      custname: value?.name || "-",
      add1: value?.address1 || "-",
      add2: value?.address2 || "-",
      add3: value?.address3 || "-",
      area: value?.city || "-",
      phoneno: value?.phone || "-",
      dob: value?.dob || "",
      annversary: value?.anniversary || "",
      state: value?.state || "-",
      add4: value?.address4 || "-",
      tinno: value?.gstin || "-",
      statecode: value?.stateCode || "",
      station: value?.locationType || "",
    };
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/CommonUpdateWithWhere?tableName=CUSTOMER&where=MOBILENO='${value?.mobile1}'`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
      userDetailsAPI(value?.mobile1);
      setFormValues({});
      setOpen(false);
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const holdMastNoAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithOrder?tableName=CREDIT_BILLMAST_HOLD&order=BILLNO DESC`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) setHoldData(data);
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const createBarcodeholdDet = async (hold) => {
    const requestBody = barCodeData.map((item, index) => ({
      billno: hold,
      billdate: billDate
        ? dayjs(billDate).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      street: userDetails?.Area || "",
      custName: userDetails?.Custname || "",
      sno: index + 1,
      itemcode: String(item?.DesignCode) ?? "",
      itemname: String(item?.ProdName) ?? "",
      code: String(item?.Code) ?? "",
      compName: String(item?.CompName) ?? "",
      groupName: String(item?.GroupName) ?? "",
      smcode: String(item?.SMCODE) ?? "",
      qty: Number(item?.stock) ?? 0,
      rate: Number(item?.CPrice) ?? 0,
      disper: Number(item?.Disper) ?? 0,
      disAmt: Number(item?.Disamt) ?? 0,
      actrate: String(item?.offer ?? ""),
      amount: String(item?.offer * item?.stock ?? ""),
      hid: Number(item?.HID) ?? 0,
      dPrice: Number(item?.DPrice) ?? 0,
      comm: 0,
      token: Number(item?.TOKEN) ?? 0,
      ptype: String(item?.PTYPE) ?? "",
      area: String(item?.Area) ?? "",
      partyname: String(item?.Partyname) ?? "",
      type: "-",
      vatper: 0,
      vatamt: 0,
      floor: String(item?.FLOOR) ?? "",
      wsalebill: false,
      appno: 0,
      invno: String(item?.InvNO) ?? "",
      invdate: dayjs(item?.Invdate).format("YYYY-MM-DD"),
      colour: String(item?.COLOUR) ?? "",
      fitting: String(item?.FITTING) ?? "",
      sleves: String(item?.SLEVES) ?? "",
      ecode: String(item?.ECODE) ?? "",
      prodsize: String(item?.Prodsize) ?? "",
      station: "-",
      iteM_DIS: Number(item?.Disamt) ?? 0,
      iteM_ACTRATE: Number(item?.offer) ?? 0,
      scheck: false,
      hsncode: String(item?.HSNCODE) ?? "",
      gst: Number(item?.GSTApplied) ?? 0,
      gstamt: item?.offer * item?.stock * (item?.GSTApplied / 100) ?? 0,
      cgstrate: item?.GSTApplied / 2,
      cgstamt: (item?.offer * item?.stock * (item?.GSTApplied / 100)) / 2 ?? 0,
      sgstrate: item?.GSTApplied / 2,
      sgstamt: (item?.offer * item?.stock * (item?.GSTApplied / 100)) / 2 ?? 0,
      igstrate: 0,
      igstamt: 0,
      basicamt:
        item?.offer * item?.stock * (item?.GSTApplied / 100) -
        (item?.offer * item?.stock * (item?.GSTApplied / 100)) / 2 -
        (item?.offer * item?.stock * (item?.GSTApplied / 100)) / 2 ?? 0,
      serialno: index + 1,
      brandcode: "-",
      salestype: "BILLING",
      fsize: "-",
      tsize: "-",
      differ: "-",
      actqty: Number(item?.stock) ?? 0,
      refcode: "-",
      state: userDetails?.STATE || "",
      statecode: userDetails?.STATECODE || "",
      tinno: "-",
      idproof: "-",
      panno: "-",
      boxes: Number(item?.BOXES) ?? 0,
      loosepcs: Number(item?.LOOSEPCS) ?? 0,
      pfcharges: 0,
      cessper: 0,
      cessamt: 0,
      packpcs: Number(item?.PACKPCS) || 0,
      caseprice: 0,
      pcsprice: 0,
      casepricers: 0,
      pcspricers: 0,
      measurementtype: "-",
      tokenper: 0,
      ustatus: false,
      gstin: "-",
      estbillno: false,
      cloud_upload: true,
    }));
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/InsertBillItemsDetHOLD`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createBarcodeHoldMast = async (hold) => {
    const requestBody = [
      {
        billno: hold,
        billdate: billDate ? billNoDate : new Date().toISOString(),
        street: userDetails?.Area || "",
        custName: userDetails?.Custname || "",
        add1: userDetails?.add1 || "",
        phoneNo: userDetails?.Mobileno || "",
        totamt: totalAmount ?? 0,
        discount: totalDiSAmt ?? 0,
        advAmt: Number(cashAmt),
        finalamt: Number(creditAmt),
        billtype: "-",
        lessAmt: 0,
        smcode: "-",
        pstatus: false,
        ebillno: 0,
        mode: "-",
        accNO: "-",
        cardno: "-",
        cardamt: 0,
        sTax: 0,
        flrcode: "-",
        esmCode: "-",
        days: 0,
        cStatus: false,
        card: 0,
        hid: 0,
        estno: 0,
        adD2: userDetails?.add2 || "",
        billtime: billDate ? billNoDate : new Date().toISOString(),
        uname: userName ?? "-",
        cancell: "-",
        ctime: billDate ? billNoDate : new Date().toISOString(),
        cuname: "-",
        station: "mobile",
        billstatus: false,
        funame: "-",
        fstation: "-",
        reccash: 0,
        refcash: 0,
        wsalebill: false,
        appno: 0,
        ccause: "-",
        edisamt: 0,
        chqdate: billDate ? billNoDate : new Date().toISOString(),
        narration: "-",
        invpayableamt: 0,
        invpaidamt: 0,
        invrtnamt: 0,
        invbalamt: 0,
        invstatus: false,
        extradiscount: 0,
        prevpoints: 0,
        prevamt: 0,
        billpoints: 0,
        billvalue: 0,
        redimpoints: 0,
        redimamt: 0,
        totpoints: 0,
        totvalue: 0,
        prevcardno: "-",
        adjustments: 0,
        totgst: 0,
        totogstamt: totalGstAmt ?? 0,
        totcgstrate: 0,
        totcgstamt: totalCGSTAmt ?? 0,
        totsgstrate: 0,
        totsgstamt: totalSGSTAmt ?? 0,
        totigstrate: 0,
        totigstamt: 0,
        serialno: 0,
        salestype: "BILL",
        custstation: "-",
        transport: "-",
        custtype: "-",
        stype: "-",
        lrno: "-",
        lrdate: "-",
        shipstreet: "-",
        shipcustname: "-",
        shipadD1: "-",
        shipadD2: "-",
        shipphoneno: "-",
        shiptinno: "-",
        noofbags: "-",
        orderno: "-",
        orderdate: "-",
        dateofsupply: "-",
        placeofsupply: "-",
        company: "-",
        place: "-",
        branch: "-",
        bankaccno: "-",
        ifsccode: "-",
        state: "-",
        statecode: "-",
        shipstate: "-",
        shipstatecode: "-",
        bankname: "-",
        beatarea: "-",
        orddate: "-",
        gstin: "-",
        totbasicamt: 0,
        totamtaftax: 0,
        salesper: 0,
        salesamt: 0,
        grossamt: 0,
        roundoff: 0,
        netamt: 0,
        tinno: "-",
        idproof: "-",
        panno: "-",
        purchno: "-",
        disamt: 0,
        schemeamt: 0,
        disper: 0,
        pinvno: "-",
        m_BTYPE: "-",
        m_BNO: "-",
        m_BDATE: "-",
        includinggst: false,
        acity: "-",
        agentname: "-",
        ustatus: false,
        estbillno: false,
        paymode: "-",
        totqty: totalQty,
        refundamt: 0,
        paidcard: 0,
        paidcheque: 0,
        paidonline: 0,
        paidupi: 0,
        paidcash: 0,
        balance: 0,
        ctype: "-",
        withgst: false,
        marginper: 0,
        cloud_upload: true,
      },
    ];
    try {
      const response = await axios.post(
        `${CREATE_jwel}/api/ERP/InsertCreditBillMastHOLD`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
      if (response?.status === 200) {
        holdNoAPI();
        handleReset();
        holdMastNoAPI();
      }
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createExchangeBillSaveAPI = async () => {
    const billNoValue = billNo ? billNo : 0;
    const billDateStr = billDate
      ? dayjs(billDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    const masterDate = billDate ? billNoDate : date.toISOString();

    // ---- billData (from createBarcodeData) ----
    const billData = barCodeData.map((item, index) => {
      const basicAmt =
        item.stock * item.offer * (100 / (100 + item.GSTApplied));
      const commValue = Number(item.stock * item.offer);
      const commission = (commValue * comm) / 100;
      const commToken = item?.TOKEN > 0 ? 0 : commission;
      return {
        billno: 0,
        billdate: billDateStr,
        street: userDetails?.Area || "-",
        custName: userDetails?.Custname || "-",
        sno: index + 1,
        itemcode: item?.DesignCode || "",
        itemname: item?.ProdName || "",
        code: item?.Code || "",
        compName: item?.CompName || "",
        groupName: item?.GroupName || "",
        smcode: String(item?.SMCODE) ?? "-",
        qty: item?.stock || 0,
        rate: item?.CPrice || 0,
        disper: item?.Disper || 0,
        disAmt: item?.Disamt || 0,
        actrate: item?.offer || 0,
        amount: item?.offer * item?.stock || 0,
        hid: item?.HID || 0,
        dPrice: item?.DPrice || 0,
        comm: Number(commToken) || 0,
        token: item?.TOKEN || 0,
        ptype: item?.PTYPE || "",
        area: item?.Area || "",
        partyname: item?.Partyname || "",
        vatper: 0,
        vatamt: 0,
        floor: item?.FLOOR || "",
        estno: "-",
        station: "mobile",
        hsncode: item?.HSNCODE || "",
        gst: item?.GSTApplied || 0,
        gstamt: basicAmt * (item?.GSTApplied / 100) || 0,
        cgstrate: item?.GSTApplied / 2 || 0,
        cgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 || 0,
        sgstrate: item?.GSTApplied / 2 || 0,
        sgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 || 0,
        igstrate: item?.IGSTRATE || 0,
        igstamt: item?.IGSTAMT || 0,
        basicamt: Number(basicAmt) ?? 0,
        serialno: 0,
        brandcode: "0",
        salestype: "BILLING",
        prodsize: item?.Prodsize || "",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: item?.stock || 0,
        refcode: "-",
        boxes: 0,
        loosepcs: 0,
        pfcharges: 0,
        cessper: 0,
        cessamt: 0,
        packpcs: 0,
        caseprice: 0,
        pcsprice: 0,
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: item?.TOKEN || 0,
        ustatus: false,
        estbillno: false,
        cloud_upload: true,
      };
    });

    // ---- billItemsDet (from createBarcodeDet) ----
    const billItemsDetMain = barCodeData.map((item, index) => {
      const basicAmt =
        item.stock * item.offer * (100 / (100 + item.GSTApplied));
      const rate =
        item?.SMODEL === "A"
          ? offerARate
          : item?.SMODEL === "B"
            ? offerBRate
            : 0;
      const commValue = Number(item.stock * item.offer);
      const commission = (commValue * comm) / 100;
      const commToken = item?.TOKEN > 0 ? 0 : commission;
      return {
        billno: 0,
        billdate: billDateStr,
        street: userDetails?.Area || "-",
        custName: userDetails?.Custname || "-",
        sno: index + 1,
        itemcode: String(item?.DesignCode) ?? "",
        itemname: String(item?.ProdName) ?? "",
        code: String(item?.Code) ?? "",
        compName: String(item?.CompName) ?? "",
        groupName: String(item?.GroupName) ?? "",
        smcode: String(item?.SMCODE) ?? "-",
        qty: Number(item?.stock) ?? 0,
        rate: Number(item?.CPrice) ?? 0,
        disper: Number(item?.Disper) ?? 0,
        disAmt: Number(item?.Disamt) ?? 0,
        actrate: String(item?.offer ?? ""),
        amount: String(item?.offer * item?.stock ?? ""),
        hid: Number(item?.HID) ?? 0,
        dPrice: Number(item?.DPrice) ?? 0,
        comm: Number(commToken) ?? 0,
        token: Number(item?.TOKEN) ?? 0,
        ptype: String(item?.PTYPE) ?? "",
        area: String(item?.Area) ?? "",
        partyname: String(item?.Partyname) ?? "",
        type: "B",
        vatper: 0,
        vatamt: 0,
        floor: String(item?.FLOOR) ?? "",
        wsalebill: false,
        appno: 0,
        invno: String(item?.InvNO) ?? "",
        invdate: billDateStr,
        colour: String(item?.COLOUR) ?? "",
        fitting: String(item?.FITTING) ?? "",
        sleves: String(item?.SLEVES) ?? "",
        ecode: String(item?.ECODE) ?? "",
        prodsize: String(item?.Prodsize) ?? "",
        station: String(item?.SMODEL) ?? "-",
        iteM_DIS: Number(item?.Disamt) ?? 0,
        iteM_ACTRATE: Number(item?.offer) ?? 0,
        scheck: false,
        hsncode: String(item?.HSNCODE) ?? "",
        gst: Number(item?.GSTApplied) ?? 0,
        gstamt: basicAmt * (item?.GSTApplied / 100) ?? 0,
        cgstrate: item?.GSTApplied / 2,
        cgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 ?? 0,
        sgstrate: item?.GSTApplied / 2,
        sgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 ?? 0,
        igstrate: 0,
        igstamt: 0,
        basicamt: Number(basicAmt) ?? 0,
        serialno: 0,
        brandcode: "0",
        salestype: "BILLING",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: Number(item?.stock) ?? 0,
        refcode: "-",
        state: userDetails?.STATE || "",
        statecode: userDetails?.STATECODE || "",
        tinno: "-",
        idproof: "-",
        panno: "-",
        boxes: Number(item?.BOXES) ?? 0,
        loosepcs: Number(item?.LOOSEPCS) ?? 0,
        pfcharges: 0,
        cessper: Number(rate),
        cessamt: 0,
        packpcs: Number(item?.PACKPCS) || 0,
        caseprice: Number(item?.OQTY3),
        pcsprice: Number(item?.OQTY2),
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: 0,
        ustatus: false,
        gstin: "-",
        estbillno: false,
        cloud_upload: true,
      };
    });

    // ---- billItemsDet (from createBarcodeEXBDet, exchangeData) — only when exchangeData present ----
    let exchangeItemsDet = [];
    if (exchangeData.length > 0) {
      exchangeItemsDet = exchangeData.map((item, index) => {
        const basicAmt =
          item.stock * item.offer * (100 / (100 + item.GSTApplied));
        const gstAmt = basicAmt * (item.GSTApplied / 100);
        const cGstAmt = (basicAmt * (item.GSTApplied / 100)) / 2;
        const sGstAmt = (basicAmt * (item.GSTApplied / 100)) / 2;
        const commValue = Number(item.stock * item.offer);
        const commission = (commValue * comm) / 100;
        const commToken = item?.TOKEN > 0 ? 0 : commission;
        return {
          billno: 0,
          billdate: billDateStr,
          street: userDetails?.Area || "",
          custName: userDetails?.Custname || "",
          sno: index + 1,
          itemcode: String(item?.DesignCode) ?? "",
          itemname: String(item?.ProdName) ?? "",
          code: String(item?.Code) ?? "",
          compName: String(item?.CompName) ?? "",
          groupName: String(item?.GroupName) ?? "",
          smcode: String(item?.SMCODE) ?? "",
          qty: Number(item?.stock) ?? 0,
          rate: Number(item?.CPrice) ?? 0,
          disper: Number(item?.Disper) ?? 0,
          disAmt: Number(item?.Disamt) ?? 0,
          actrate: String(item?.offer ?? ""),
          amount: String(item?.offer * item?.stock ?? ""),
          hid: Number(item?.HID) ?? 0,
          dPrice: Number(item?.DPrice) ?? 0,
          comm: Number(commToken) ?? 0,
          token: Number(item?.TOKEN) ?? 0,
          ptype: String(item?.PTYPE) ?? "",
          area: String(item?.Area) ?? "",
          partyname: String(item?.Partyname) ?? "",
          type: "EB",
          vatper: 0,
          vatamt: 0,
          floor: String(item?.FLOOR) ?? "",
          wsalebill: false,
          appno: 0,
          invno: String(item?.InvNO) ?? "",
          invdate: billDateStr,
          colour: String(item?.COLOUR) ?? "",
          fitting: String(item?.FITTING) ?? "",
          sleves: String(item?.SLEVES) ?? "",
          ecode: String(item?.ECODE) ?? "",
          prodsize: String(item?.Prodsize) ?? "",
          station: "-",
          iteM_DIS: Number(item?.Disamt) ?? 0,
          iteM_ACTRATE: Number(item?.offer) ?? 0,
          scheck: false,
          hsncode: String(item?.HSNCODE) ?? "",
          gst: Number(item?.GSTApplied) ?? 0,
          gstamt: Number(gstAmt) ?? 0,
          cgstrate: item?.GSTApplied / 2,
          cgstamt: Number(cGstAmt) ?? 0,
          sgstrate: item?.GSTApplied / 2,
          sgstamt: Number(sGstAmt) ?? 0,
          igstrate: 0,
          igstamt: 0,
          basicamt: Number(basicAmt) ?? 0,
          serialno: 0,
          brandcode: "0",
          salestype: "EXCHANGE",
          fsize: "-",
          tsize: "-",
          differ: "-",
          actqty: Number(item?.stock) ?? 0,
          refcode: "-",
          state: userDetails?.STATE || "",
          statecode: userDetails?.STATECODE || "",
          tinno: "-",
          idproof: "-",
          panno: "-",
          boxes: Number(item?.BOXES) ?? 0,
          loosepcs: Number(item?.LOOSEPCS) ?? 0,
          pfcharges: 0,
          cessper: 0,
          cessamt: 0,
          packpcs: Number(item?.PACKPCS) || 0,
          caseprice: 0,
          pcsprice: 0,
          casepricers: 0,
          pcspricers: 0,
          measurementtype: "-",
          tokenper: 0,
          ustatus: false,
          gstin: "-",
          estbillno: false,
          cloud_upload: true,
        };
      });
    }

    // combine both bill-items sets into one array for the single "InsertBillItemsDet"-style endpoint
    const billItemsDet = [...billItemsDetMain];

    // ---- master (from createBarcodeMast) ----
    const master = {
      billno: 0,
      billdate: masterDate,
      street: userDetails?.Area || "-",
      custName: userDetails?.Custname || "-",
      add1: userDetails?.add1 || "-",
      phoneNo: userDetails?.Mobileno || "-",
      totamt: Number(totalAmount),
      discount: Number(totalDiSAmt),
      advAmt: Number(cashAmt),
      finalamt: Number(creditAmt),
      billtype: cashValue === "CREDIT" ? "CREDIT" : "CASH",
      lessAmt: 0,
      smcode: "-",
      pstatus: false,
      ebillno: 0,
      mode:
        cashValue === "CREDIT"
          ? "CASH"
          : cashValue === "CASH"
            ? "CASH"
            : "CARD",
      accNO: "-",
      cardno: cashValue === "CREDIT" ? "0" : cashValue === "CASH" ? "0" : "-",
      cardamt: Number(cardAmt || upiAmt || onlineAmt || 0),
      sTax: 0,
      flrcode: "-",
      esmCode: "-",
      days: 0,
      cStatus: true,
      card: 0,
      hid: 0,
      estno: 0,
      adD2: userDetails?.add2 || "-",
      billtime: masterDate,
      uname: loginName ?? "-",
      cancell: "NO",
      ctime: masterDate,
      cuname: "-",
      station: "mobile",
      billstatus: false,
      funame: "-",
      fstation: "-",
      reccash: 0,
      refcash: 0,
      wsalebill: false,
      appno: 1,
      ccause: "-",
      edisamt: 0,
      chqdate: masterDate,
      narration: "-",
      invpayableamt: Number(totalNetAmount),
      invpaidamt: Number(invPaidAmt),
      invrtnamt: 0,
      invbalamt: Number(creditAmt),
      invstatus: true,
      extradiscount: 0,
      prevpoints: 0,
      prevamt: 0,
      billpoints: 0,
      billvalue: 0,
      redimpoints: 0,
      redimamt: 0,
      totpoints: 0,
      totvalue: 0,
      prevcardno: "-",
      adjustments: 0,
      totgst: 0,
      totogstamt: Number(totalGstAmt),
      totcgstrate: 0,
      totcgstamt: Number(totalCGSTAmt),
      totsgstrate: 0,
      totsgstamt: Number(totalSGSTAmt),
      totigstrate: 0,
      totigstamt: 0,
      serialno: 0,
      salestype: "BILLING",
      custstation: userDetails?.STATION || "LOCAL",
      transport: "-",
      custtype: "CUSTOMER",
      stype: "BILLING",
      lrno: "-",
      lrdate: "-",
      shipstreet: "-",
      shipcustname: "-",
      shipadD1: "-",
      shipadD2: "-",
      shipphoneno: "-",
      shiptinno: "-",
      noofbags: "-",
      orderno: "-",
      orderdate: "-",
      dateofsupply: "-",
      placeofsupply: "-",
      company: "-",
      place: "-",
      branch: "-",
      bankaccno: "-",
      ifsccode: "-",
      state: "-",
      statecode: "-",
      shipstate: "-",
      shipstatecode: "-",
      bankname: "-",
      beatarea: "-",
      orddate: "-",
      gstin: "-",
      totbasicamt: Number(totalBasicAmount),
      totamtaftax: 0,
      salesper: 0,
      salesamt: 0,
      grossamt: Number(totalNetAmount),
      roundoff: 0,
      netamt: Number(totalNetAmount),
      tinno: "-",
      idproof: "-",
      panno: "-",
      purchno: "-",
      disamt: Number(totalDiscountAmt) || 0,
      schemeamt: 0,
      disper: Number(disPer) || 0,
      pinvno: "-",
      m_BTYPE: "-",
      m_BNO: "-",
      m_BDATE: "-",
      includinggst: false,
      acity: "-",
      agentname: "-",
      ustatus: false,
      estbillno: false,
      paymode: "-",
      totqty: totalQty,
      refundamt: 0,
      paidcard: Number(cardAmt),
      paidcheque: 0,
      paidonline: Number(onlineAmt),
      paidupi: Number(upiAmt),
      paidcash: Number(cashAmt),
      balance: Number(creditAmt),
      ctype: "RETAIL",
      withgst: false,
      marginper: 0,
      cloud_upload: true,
      couponredem: 0,
    };

    // ---- singleEntryData (from paymentModeAPI) ----
    const commonFields = {
      entrydate: billDateStr,
      entryno: 0,
      vno: "0",
      vdate: billDateStr,
      scode: 4,
      description: "-",
      salestype: "BILLING",
      hid: 0,
      sno: 0,
      clouD_UPLOAD: true,
      outstandinG_CHEQUE: "-",
    };

    let singleEntryData = [];

    if (cashValue === "CASH") {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
          paymode: "-",
        },
      ];
    } else if (
      (cashValue === "CARD" || cashValue === "UPI" || cashValue === "ONLINE") &&
      totalAmt === 0
    ) {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "ONLINE SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "ONLINE SALES",
          stype: "ONLINE SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername:
            cashValue === "CARD"
              ? cardAcc
              : cashValue === "UPI"
                ? upiAcc
                : onlineAcc,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: 0,
          namaamount:
            cashValue === "CARD"
              ? Number(cardAmt)
              : cashValue === "UPI"
                ? Number(upiAmt)
                : Number(onlineAmt),
          tranS_TYPE: "ONLINE SALES",
          stype: "BANK TRANS",
          paymode: cashValue,
        },
      ];
    } else if (
      (cashValue === "CARD" || cashValue === "UPI" || cashValue === "ONLINE") &&
      totalAmt > 0
    ) {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "ONLINE SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "ONLINE SALES",
          stype: "ONLINE SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername:
            cashValue === "CARD"
              ? cardAcc
              : cashValue === "UPI"
                ? upiAcc
                : onlineAcc,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: 0,
          namaamount:
            cashValue === "CARD"
              ? Number(cardAmt)
              : cashValue === "UPI"
                ? Number(upiAmt)
                : Number(onlineAmt),
          tranS_TYPE: "CASH SALES",
          stype: "BANK TRANS",
          paymode: cashValue,
        },
        {
          ...commonFields,
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: totalNetAmount - cardAmt - upiAmt - onlineAmt - creditAmt,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
          paymode: "-",
        },
      ];
    } else if (cashValue === "CREDIT" && creditAmt > 0 && cashAmt > 0) {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: Number(totalNetAmount),
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: Number(cashAmt),
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: userDetails?.Custname,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: 0,
          namaamount: Number(creditAmt),
          tranS_TYPE: "CASH SALES",
          stype: "CUSTOMER DUE",
          paymode: "-",
        },
      ];
    } else if (cashValue === "CREDIT" && creditAmt > 0) {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: Number(totalNetAmount),
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: userDetails?.Custname,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: 0,
          namaamount: Number(creditAmt),
          tranS_TYPE: "CASH SALES",
          stype: "CUSTOMER DUE",
          paymode: "-",
        },
      ];
    }

    // ---- orgCardDet (from onlinePayModeAPI) ----
    let orgCardDet = [];
    if (
      (barCodeData.length > 0 && cashValue === "CARD") ||
      cashValue === "UPI" ||
      cashValue === "ONLINE"
    ) {
      orgCardDet = [
        {
          billno: 0,
          billdate: masterDate,
          bal: Number(totalNetAmount),
          sno: 1,
          paymode: cashValue,
          accno:
            cashValue === "CARD"
              ? cardAcc
              : cashValue === "UPI"
                ? upiAcc
                : onlineAcc,
          narr: "-",
          amt:
            cashValue === "CARD"
              ? Number(cardAmt)
              : cashValue === "UPI"
                ? Number(upiAmt)
                : Number(onlineAmt),
          cntrname: "",
          stype: "BILLING",
          ptype: cashValue,
          cloud_upload: true,
        },
      ];
    }

    // ---- transEntryDataCust (from createLedgerBalanceAPI) ----
    let transEntryDataCust = [];
    if (creditAmt > 0) {
      transEntryDataCust = [
        {
          area: userDetails?.Area || "-",
          partyname: userDetails?.Custname || "-",
          billno: "0",
          billdate: masterDate,
          entryno: 0,
          entrydate: masterDate,
          particulars: "SALE INVOICE",
          jamaqty: 0,
          namaqty: Number(totalQty) ?? 0,
          jama: 0,
          nama: Number(creditAmt) ?? 0,
          narration: "-",
          hid: 0,
          agentcity: "-",
          agentname: String(userDetails?.Mobileno) || "-",
          tranS_TYPE: "SALES INVOICE",
          cusT_TYPE: "CUSTOMER",
          sno: 0,
          pstatus: "",
          salestype: "BILLING",
          chequeno: "-",
          chqdate: masterDate,
          chqamt: 0,
          chqstatus: false,
          accno: "-",
          chqcancell: "NO",
          description: "-",
          cloud_upload: false,
        },
      ];
    }

    // ---- final combined payload ----
    const payload = {
      master,
      billData,
      billItemsDet,
      exchangeItemsDet,
      singleEntryData,
      orgCardDet,
      transEntryDataCust,
    };

    try {
      const response = await axios.post(
        `${CREATE_jwel}/api/ExchangeBill/Save`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
      if (response?.data?.isInsert === true) {
        invNoAPI();
        commissionAPI();
        handleReset();
        setCashValue("CASH");
        setCardAmt(0);
        setUpiAmt(0);
        setOnlineAmt(0);
        setCreditAmt(0);
        smCodeRef.current?.focus();
      }
      return response;
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const createExchangeBillUpdateAPI = async () => {
    const billNoValue = billNo ? billNo : 0;
    const billDateStr = billDate
      ? dayjs(billDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    const masterDate = billDate ? billNoDate : date.toISOString();

    // ---- billData (from createBarcodeData) ----
    const billData = barCodeData.map((item, index) => {
      const basicAmt =
        item.stock * item.offer * (100 / (100 + item.GSTApplied));
      const commValue = Number(item.stock * item.offer);
      const commission = (commValue * comm) / 100;
      const commToken = item?.TOKEN > 0 ? 0 : commission;
      return {
        billno: Number(billNoValue),
        billdate: billDateStr,
        street: userDetails?.Area || "-",
        custName: userDetails?.Custname || "-",
        sno: index + 1,
        itemcode: item?.DesignCode || "",
        itemname: item?.ProdName || "",
        code: item?.Code || "",
        compName: item?.CompName || "",
        groupName: item?.GroupName || "",
        smcode: String(item?.SMCODE) ?? "-",
        qty: item?.stock || 0,
        rate: item?.CPrice || 0,
        disper: item?.Disper || 0,
        disAmt: item?.Disamt || 0,
        actrate: item?.offer || 0,
        amount: item?.offer * item?.stock || 0,
        hid: item?.HID || 0,
        dPrice: item?.DPrice || 0,
        comm: Number(commToken) || 0,
        token: item?.TOKEN || 0,
        ptype: item?.PTYPE || "",
        area: item?.Area || "",
        partyname: item?.Partyname || "",
        vatper: 0,
        vatamt: 0,
        floor: item?.FLOOR || "",
        estno: "-",
        station: "mobile",
        hsncode: item?.HSNCODE || "",
        gst: item?.GSTApplied || 0,
        gstamt: basicAmt * (item?.GSTApplied / 100) || 0,
        cgstrate: item?.GSTApplied / 2 || 0,
        cgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 || 0,
        sgstrate: item?.GSTApplied / 2 || 0,
        sgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 || 0,
        igstrate: item?.IGSTRATE || 0,
        igstamt: item?.IGSTAMT || 0,
        basicamt: Number(basicAmt) ?? 0,
        serialno: 0,
        brandcode: "0",
        salestype: "BILLING",
        prodsize: item?.Prodsize || "",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: item?.stock || 0,
        refcode: "-",
        boxes: 0,
        loosepcs: 0,
        pfcharges: 0,
        cessper: 0,
        cessamt: 0,
        packpcs: 0,
        caseprice: 0,
        pcsprice: 0,
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: item?.TOKEN || 0,
        ustatus: false,
        estbillno: false,
        cloud_upload: true,
      };
    });

    // ---- billItemsDet (from createBarcodeDet) ----
    const billItemsDetMain = barCodeData.map((item, index) => {
      const basicAmt =
        item.stock * item.offer * (100 / (100 + item.GSTApplied));
      const rate =
        item?.SMODEL === "A"
          ? offerARate
          : item?.SMODEL === "B"
            ? offerBRate
            : 0;
      const commValue = Number(item.stock * item.offer);
      const commission = (commValue * comm) / 100;
      const commToken = item?.TOKEN > 0 ? 0 : commission;
      return {
        billno: Number(billNoValue),
        billdate: billDateStr,
        street: userDetails?.Area || "-",
        custName: userDetails?.Custname || "-",
        sno: index + 1,
        itemcode: String(item?.DesignCode) ?? "",
        itemname: String(item?.ProdName) ?? "",
        code: String(item?.Code) ?? "",
        compName: String(item?.CompName) ?? "",
        groupName: String(item?.GroupName) ?? "",
        smcode: String(item?.SMCODE) ?? "-",
        qty: Number(item?.stock) ?? 0,
        rate: Number(item?.CPrice) ?? 0,
        disper: Number(item?.Disper) ?? 0,
        disAmt: Number(item?.Disamt) ?? 0,
        actrate: String(item?.offer ?? ""),
        amount: String(item?.offer * item?.stock ?? ""),
        hid: Number(item?.HID) ?? 0,
        dPrice: Number(item?.DPrice) ?? 0,
        comm: Number(commToken) ?? 0,
        token: Number(item?.TOKEN) ?? 0,
        ptype: String(item?.PTYPE) ?? "",
        area: String(item?.Area) ?? "",
        partyname: String(item?.Partyname) ?? "",
        type: "B",
        vatper: 0,
        vatamt: 0,
        floor: String(item?.FLOOR) ?? "",
        wsalebill: false,
        appno: 0,
        invno: String(item?.InvNO) ?? "",
        invdate: billDateStr,
        colour: String(item?.COLOUR) ?? "",
        fitting: String(item?.FITTING) ?? "",
        sleves: String(item?.SLEVES) ?? "",
        ecode: String(item?.ECODE) ?? "",
        prodsize: String(item?.Prodsize) ?? "",
        station: String(item?.SMODEL) ?? "-",
        iteM_DIS: Number(item?.Disamt) ?? 0,
        iteM_ACTRATE: Number(item?.offer) ?? 0,
        scheck: false,
        hsncode: String(item?.HSNCODE) ?? "",
        gst: Number(item?.GSTApplied) ?? 0,
        gstamt: basicAmt * (item?.GSTApplied / 100) ?? 0,
        cgstrate: item?.GSTApplied / 2,
        cgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 ?? 0,
        sgstrate: item?.GSTApplied / 2,
        sgstamt: (basicAmt * (item?.GSTApplied / 100)) / 2 ?? 0,
        igstrate: 0,
        igstamt: 0,
        basicamt: Number(basicAmt) ?? 0,
        serialno: 0,
        brandcode: "0",
        salestype: "BILLING",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: Number(item?.stock) ?? 0,
        refcode: "-",
        state: userDetails?.STATE || "",
        statecode: userDetails?.STATECODE || "",
        tinno: "-",
        idproof: "-",
        panno: "-",
        boxes: Number(item?.BOXES) ?? 0,
        loosepcs: Number(item?.LOOSEPCS) ?? 0,
        pfcharges: 0,
        cessper: Number(rate),
        cessamt: 0,
        packpcs: Number(item?.PACKPCS) || 0,
        caseprice: Number(item?.OQTY3),
        pcsprice: Number(item?.OQTY2),
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: 0,
        ustatus: false,
        gstin: "-",
        estbillno: false,
        cloud_upload: true,
      };
    });

    // ---- billItemsDet (from createBarcodeEXBDet, exchangeData) — only when exchangeData present ----
    let exchangeItemsDet = [];
    if (exchangeData.length > 0) {
      exchangeItemsDet = exchangeData.map((item, index) => {
        const basicAmt =
          item.stock * item.offer * (100 / (100 + item.GSTApplied));
        const gstAmt = basicAmt * (item.GSTApplied / 100);
        const cGstAmt = (basicAmt * (item.GSTApplied / 100)) / 2;
        const sGstAmt = (basicAmt * (item.GSTApplied / 100)) / 2;
        const commValue = Number(item.stock * item.offer);
        const commission = (commValue * comm) / 100;
        const commToken = item?.TOKEN > 0 ? 0 : commission;
        return {
          billno: Number(billNoValue),
          billdate: billDateStr,
          street: userDetails?.Area || "",
          custName: userDetails?.Custname || "",
          sno: index + 1,
          itemcode: String(item?.DesignCode) ?? "",
          itemname: String(item?.ProdName) ?? "",
          code: String(item?.Code) ?? "",
          compName: String(item?.CompName) ?? "",
          groupName: String(item?.GroupName) ?? "",
          smcode: String(item?.SMCODE) ?? "",
          qty: Number(item?.stock) ?? 0,
          rate: Number(item?.CPrice) ?? 0,
          disper: Number(item?.Disper) ?? 0,
          disAmt: Number(item?.Disamt) ?? 0,
          actrate: String(item?.offer ?? ""),
          amount: String(item?.offer * item?.stock ?? ""),
          hid: Number(item?.HID) ?? 0,
          dPrice: Number(item?.DPrice) ?? 0,
          comm: Number(commToken) ?? 0,
          token: Number(item?.TOKEN) ?? 0,
          ptype: String(item?.PTYPE) ?? "",
          area: String(item?.Area) ?? "",
          partyname: String(item?.Partyname) ?? "",
          type: "EB",
          vatper: 0,
          vatamt: 0,
          floor: String(item?.FLOOR) ?? "",
          wsalebill: false,
          appno: 0,
          invno: String(item?.InvNO) ?? "",
          invdate: billDateStr,
          colour: String(item?.COLOUR) ?? "",
          fitting: String(item?.FITTING) ?? "",
          sleves: String(item?.SLEVES) ?? "",
          ecode: String(item?.ECODE) ?? "",
          prodsize: String(item?.Prodsize) ?? "",
          station: "-",
          iteM_DIS: Number(item?.Disamt) ?? 0,
          iteM_ACTRATE: Number(item?.offer) ?? 0,
          scheck: false,
          hsncode: String(item?.HSNCODE) ?? "",
          gst: Number(item?.GSTApplied) ?? 0,
          gstamt: Number(gstAmt) ?? 0,
          cgstrate: item?.GSTApplied / 2,
          cgstamt: Number(cGstAmt) ?? 0,
          sgstrate: item?.GSTApplied / 2,
          sgstamt: Number(sGstAmt) ?? 0,
          igstrate: 0,
          igstamt: 0,
          basicamt: Number(basicAmt) ?? 0,
          serialno: 0,
          brandcode: "0",
          salestype: "EXCHANGE",
          fsize: "-",
          tsize: "-",
          differ: "-",
          actqty: Number(item?.stock) ?? 0,
          refcode: "-",
          state: userDetails?.STATE || "",
          statecode: userDetails?.STATECODE || "",
          tinno: "-",
          idproof: "-",
          panno: "-",
          boxes: Number(item?.BOXES) ?? 0,
          loosepcs: Number(item?.LOOSEPCS) ?? 0,
          pfcharges: 0,
          cessper: 0,
          cessamt: 0,
          packpcs: Number(item?.PACKPCS) || 0,
          caseprice: 0,
          pcsprice: 0,
          casepricers: 0,
          pcspricers: 0,
          measurementtype: "-",
          tokenper: 0,
          ustatus: false,
          gstin: "-",
          estbillno: false,
          cloud_upload: true,
        };
      });
    }

    // combine both bill-items sets into one array for the single "InsertBillItemsDet"-style endpoint
    const billItemsDet = [...billItemsDetMain];

    // ---- master (from createBarcodeMast) ----
    const master = {
      billno: Number(billNoValue),
      billdate: masterDate,
      street: userDetails?.Area || "-",
      custName: userDetails?.Custname || "-",
      add1: userDetails?.add1 || "-",
      phoneNo: userDetails?.Mobileno || "-",
      totamt: Number(totalAmount),
      discount: Number(totalDiSAmt),
      advAmt: Number(cashAmt),
      finalamt: Number(creditAmt),
      billtype: cashValue === "CREDIT" ? "CREDIT" : "CASH",
      lessAmt: 0,
      smcode: "-",
      pstatus: false,
      ebillno: 0,
      mode:
        cashValue === "CREDIT"
          ? "CASH"
          : cashValue === "CASH"
            ? "CASH"
            : "CARD",
      accNO: "-",
      cardno: cashValue === "CREDIT" ? "0" : cashValue === "CASH" ? "0" : "-",
      cardamt: Number(cardAmt || upiAmt || onlineAmt || 0),
      sTax: 0,
      flrcode: "-",
      esmCode: "-",
      days: 0,
      cStatus: true,
      card: 0,
      hid: 0,
      estno: 0,
      adD2: userDetails?.add2 || "-",
      billtime: masterDate,
      uname: loginName ?? "-",
      cancell: "NO",
      ctime: masterDate,
      cuname: "-",
      station: "mobile",
      billstatus: false,
      funame: "-",
      fstation: "-",
      reccash: 0,
      refcash: 0,
      wsalebill: false,
      appno: 1,
      ccause: "-",
      edisamt: 0,
      chqdate: masterDate,
      narration: "-",
      invpayableamt: Number(totalNetAmount),
      invpaidamt: Number(invPaidAmt),
      invrtnamt: 0,
      invbalamt: Number(creditAmt),
      invstatus: true,
      extradiscount: 0,
      prevpoints: 0,
      prevamt: 0,
      billpoints: 0,
      billvalue: 0,
      redimpoints: 0,
      redimamt: 0,
      totpoints: 0,
      totvalue: 0,
      prevcardno: "-",
      adjustments: 0,
      totgst: 0,
      totogstamt: Number(totalGstAmt),
      totcgstrate: 0,
      totcgstamt: Number(totalCGSTAmt),
      totsgstrate: 0,
      totsgstamt: Number(totalSGSTAmt),
      totigstrate: 0,
      totigstamt: 0,
      serialno: 0,
      salestype: "BILLING",
      custstation: userDetails?.STATION || "LOCAL",
      transport: "-",
      custtype: "CUSTOMER",
      stype: "BILLING",
      lrno: "-",
      lrdate: "-",
      shipstreet: "-",
      shipcustname: "-",
      shipadD1: "-",
      shipadD2: "-",
      shipphoneno: "-",
      shiptinno: "-",
      noofbags: "-",
      orderno: "-",
      orderdate: "-",
      dateofsupply: "-",
      placeofsupply: "-",
      company: "-",
      place: "-",
      branch: "-",
      bankaccno: "-",
      ifsccode: "-",
      state: "-",
      statecode: "-",
      shipstate: "-",
      shipstatecode: "-",
      bankname: "-",
      beatarea: "-",
      orddate: "-",
      gstin: "-",
      totbasicamt: Number(totalBasicAmount),
      totamtaftax: 0,
      salesper: 0,
      salesamt: 0,
      grossamt: Number(totalNetAmount),
      roundoff: 0,
      netamt: Number(totalNetAmount),
      tinno: "-",
      idproof: "-",
      panno: "-",
      purchno: "-",
      disamt: Number(totalDiscountAmt) || 0,
      schemeamt: 0,
      disper: Number(disPer) || 0,
      pinvno: "-",
      m_BTYPE: "-",
      m_BNO: "-",
      m_BDATE: "-",
      includinggst: false,
      acity: "-",
      agentname: "-",
      ustatus: false,
      estbillno: false,
      paymode: "-",
      totqty: totalQty,
      refundamt: 0,
      paidcard: Number(cardAmt),
      paidcheque: 0,
      paidonline: Number(onlineAmt),
      paidupi: Number(upiAmt),
      paidcash: Number(cashAmt),
      balance: Number(creditAmt),
      ctype: "RETAIL",
      withgst: false,
      marginper: 0,
      cloud_upload: true,
      couponredem: 0,
    };

    // ---- singleEntryData (from paymentModeAPI) ----
    const commonFields = {
      entrydate: billDateStr,
      entryno: Number(billNoValue),
      vno: String(billNoValue),
      vdate: billDateStr,
      scode: 4,
      description: "-",
      salestype: "BILLING",
      hid: 0,
      sno: 0,
      clouD_UPLOAD: true,
      outstandinG_CHEQUE: "-",
    };

    let singleEntryData = [];

    if (cashValue === "CASH") {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
          paymode: "-",
        },
      ];
    } else if (
      (cashValue === "CARD" || cashValue === "UPI" || cashValue === "ONLINE") &&
      totalAmt === 0
    ) {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "ONLINE SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "ONLINE SALES",
          stype: "ONLINE SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername:
            cashValue === "CARD"
              ? cardAcc
              : cashValue === "UPI"
                ? upiAcc
                : onlineAcc,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: 0,
          namaamount:
            cashValue === "CARD"
              ? Number(cardAmt)
              : cashValue === "UPI"
                ? Number(upiAmt)
                : Number(onlineAmt),
          tranS_TYPE: "ONLINE SALES",
          stype: "BANK TRANS",
          paymode: cashValue,
        },
      ];
    } else if (
      (cashValue === "CARD" || cashValue === "UPI" || cashValue === "ONLINE") &&
      totalAmt > 0
    ) {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "ONLINE SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: totalNetAmount,
          namaamount: 0,
          tranS_TYPE: "ONLINE SALES",
          stype: "ONLINE SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername:
            cashValue === "CARD"
              ? cardAcc
              : cashValue === "UPI"
                ? upiAcc
                : onlineAcc,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: 0,
          namaamount:
            cashValue === "CARD"
              ? Number(cardAmt)
              : cashValue === "UPI"
                ? Number(upiAmt)
                : Number(onlineAmt),
          tranS_TYPE: "CASH SALES",
          stype: "BANK TRANS",
          paymode: cashValue,
        },
        {
          ...commonFields,
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: totalNetAmount - cardAmt - upiAmt - onlineAmt - creditAmt,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
          paymode: "-",
        },
      ];
    } else if (cashValue === "CREDIT" && creditAmt > 0 && cashAmt > 0) {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: Number(totalNetAmount),
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: Number(cashAmt),
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: userDetails?.Custname,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: 0,
          namaamount: Number(creditAmt),
          tranS_TYPE: "CASH SALES",
          stype: "CUSTOMER DUE",
          paymode: "-",
        },
      ];
    } else if (cashValue === "CREDIT" && creditAmt > 0) {
      singleEntryData = [
        {
          ...commonFields,
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "",
          particulars: "",
          jamaamount: Number(totalNetAmount),
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH SALES",
          paymode: "-",
        },
        {
          ...commonFields,
          ledgername: userDetails?.Custname,
          groupname: "CUSTOMER",
          subgroup: "-",
          maingroup: "CASH BOOK",
          particulars: "",
          jamaamount: 0,
          namaamount: Number(creditAmt),
          tranS_TYPE: "CASH SALES",
          stype: "CUSTOMER DUE",
          paymode: "-",
        },
      ];
    }

    // ---- orgCardDet (from onlinePayModeAPI) ----
    let orgCardDet = [];
    if (
      (barCodeData.length > 0 && cashValue === "CARD") ||
      cashValue === "UPI" ||
      cashValue === "ONLINE"
    ) {
      orgCardDet = [
        {
          billno: Number(billNoValue),
          billdate: masterDate,
          bal: Number(totalNetAmount),
          sno: 1,
          paymode: cashValue,
          accno:
            cashValue === "CARD"
              ? cardAcc
              : cashValue === "UPI"
                ? upiAcc
                : onlineAcc,
          narr: "-",
          amt:
            cashValue === "CARD"
              ? Number(cardAmt)
              : cashValue === "UPI"
                ? Number(upiAmt)
                : Number(onlineAmt),
          cntrname: "",
          stype: "BILLING",
          ptype: cashValue,
          cloud_upload: true,
        },
      ];
    }

    // ---- transEntryDataCust (from createLedgerBalanceAPI) ----
    let transEntryDataCust = [];
    if (creditAmt > 0) {
      transEntryDataCust = [
        {
          area: userDetails?.Area || "-",
          partyname: userDetails?.Custname || "-",
          billno: String(billNoValue),
          billdate: masterDate,
          entryno: Number(billNoValue),
          entrydate: masterDate,
          particulars: "SALE INVOICE",
          jamaqty: 0,
          namaqty: Number(totalQty) ?? 0,
          jama: 0,
          nama: Number(creditAmt) ?? 0,
          narration: "-",
          hid: 0,
          agentcity: "-",
          agentname: String(userDetails?.Mobileno) || "-",
          tranS_TYPE: "SALES INVOICE",
          cusT_TYPE: "CUSTOMER",
          sno: 0,
          pstatus: "",
          salestype: "BILLING",
          chequeno: "-",
          chqdate: masterDate,
          chqamt: 0,
          chqstatus: false,
          accno: "-",
          chqcancell: "NO",
          description: "-",
          cloud_upload: false,
        },
      ];
    }

    // ---- final combined payload ----
    const payload = {
      master,
      billData,
      billItemsDet,
      exchangeItemsDet,
      singleEntryData,
      orgCardDet,
      transEntryDataCust,
    };

    try {
      const response = await axios.post(
        `${CREATE_jwel}/api/ExchangeBill/Update`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
      if (response?.data?.isInsert === true) {
        invNoAPI();
        commissionAPI();
        handleReset();
        setCashValue("CASH");
        setCardAmt(0);
        setUpiAmt(0);
        setOnlineAmt(0);
        setCreditAmt(0);
        smCodeRef.current?.focus();
      }
      return response;
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const modifyOnlineAPI = async (no) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=ORG_CARDDET&where=BILLNO=${no} AND STYPE='BILLING'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        if (data[0]?.PTYPE === "CARD") {
          setCashValue("CARD");
          setCardAmt(data[0]?.AMT);
        } else if (data[0]?.PTYPE === "UPI") {
          setCashValue("UPI");
          setUpiAmt(data[0]?.AMT);
        } else if (data[0]?.PTYPE === "ONLINE") {
          setCashValue("ONLINE");
          setOnlineAmt(data[0]?.AMT);
        } else {
          setCashValue("CASH");
        }
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const modifyOpeningLedgerAPI = async (no) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=TRANS_ENTRY_DATA_CUST&where=ENTRYNO=${no} AND SALESTYPE='BILLING'`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        if (data[0]?.NAMA > 0) {
          setCashValue("CREDIT");
          setCreditAmt(data[0]?.NAMA);
        }
        userGetDetailsAPI(data[0]?.AGENTNAME);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const modifyDetAPI = async (no) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=BILL_ITEMS_DET&where=BILLNO=${no} AND TYPE='B'`,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
      let data = response?.data;
      let offerARateLocal = 0;
      let offerBRateLocal = 0;
      for (const item of data) {
        if (item.STATION === "A") offerARateLocal = item.CESSPER;
        else if (item.STATION === "B") offerBRateLocal = item.CESSPER;
      }
      setOfferARate(offerARateLocal);
      setOfferBRate(offerBRateLocal);
      const modifiedData = data.map((item) => ({
        ACITY: "-",
        AGENTNAME: "-",
        Area: item?.AREA,
        BOXES: item?.BOXES,
        BRANCHNAME: "-",
        CASERATECP: null,
        CASERATEDP: null,
        CASERATERP: null,
        CGSTAMT: item?.CGSTAMT,
        CGSTRATE: item?.CGSTRATE,
        COLOUR: item?.COLOUR,
        CPrice: item?.rate,
        CTOKEN: 0,
        Code: item?.code,
        CompName: item?.CompName,
        DPrice: item?.DPrice,
        DesignCode: item?.ITEMCODE,
        Disamt: item?.ITEM_DIS,
        Disper: item?.DISPER,
        ECODE: item?.ECODE,
        ENTRYDATE: "-",
        ENTRYNO: 0,
        EQTY: 0,
        EXPDATE: null,
        FITTING: item?.FITTING,
        FLOOR: item?.FLOOR,
        FYEAR: "-",
        GST: item?.GST,
        GSTAMT: item?.GSTAMT,
        GrosDPrice: item?.ITEM_ACTRATE,
        GroupName: item?.GroupName,
        HID: item?.HID,
        HSNCODE: item?.HSNCODE,
        ICODETYPE: "-",
        IGSTAMT: item?.IGSTAMT,
        IGSTRATE: item?.IGSTRATE,
        InvNO: item?.INVNO,
        Invdate: item?.INVDATE,
        ItemCode: "0",
        LASTBILLDATE: "-",
        LENGTH: null,
        LOOSEPCS: null,
        LQTY: null,
        OFFER_DIS: 0,
        OQTY2: Number(item?.PCSPRICE),
        OQTY3: Number(item?.CASEPRICE),
        OQTY4: null,
        PACKPCS: null,
        PNTMODEL: "-",
        PQTY: 0,
        PTYPE: item?.PTYPE,
        Partyname: item?.PARTYNAME,
        ProdName: item?.itemname,
        Prodsize: item?.PRODSIZE,
        Qty: item?.qty,
        RESELLERPRICE: null,
        RQTY: null,
        SEMIWSALERPRICE: null,
        SET_ITEMS: false,
        SGSTAMT: item?.SGSTAMT,
        SGSTRATE: item?.SGSTRATE,
        SLEVES: item?.SLEVES,
        SMODEL: item?.STATION,
        SMCODE: String(item?.SMCODE) ?? "",
        SNO: 0,
        TOKEN: item?.TOKEN,
        USTATUS: null,
        WPER: 0,
        WPRICE: 0,
        stock: item?.qty,
        cloud_upload: false,
        mesure: null,
        offer: item?.ITEM_ACTRATE,
        taans: false,
      }));
      if (response?.status === 200) {
        setUpdatedData(modifiedData);
        for (const item of modifiedData) {
          if (item.DesignCode) await qtyAPI(item.DesignCode);
        }
        setBarCodeData((prevData) => {
          const updatedData = [
            ...prevData,
            ...modifiedData.map((item) => {
              const gstItem = gstData.find((gst) => gst.PType === item.PTYPE);
              let appliedGST = 0;
              if (gstItem?.ALLOWGSTVALUE === true) {
                const offerAmount = item.offer ?? 0;
                if (offerAmount <= gstItem.BELLOWVALUE)
                  appliedGST = gstItem.BELLOWPER;
                else if (offerAmount >= gstItem.ABOVEVALUE)
                  appliedGST = gstItem.ABOVEPER;
              } else {
                appliedGST = item?.GST;
              }
              return { ...item, GSTApplied: appliedGST };
            }),
          ];

          const totalGst = updatedData.reduce((acc, item) => {
            const offer = Number(item.offer) || 0;
            const qty = Number(item.stock) || 0;
            const gst = Number(item.GSTApplied) || 0;
            const netAmt = qty * offer;
            const basic = netAmt * (100 / (100 + gst));
            return acc + basic * (gst / 100);
          }, 0);
          setTotalBillGstAmt(totalGst);

          const totalAmt = updatedData.reduce((acc, item) => {
            const offer = Number(item.offer) || 0;
            const qty = Number(item.stock) || 0;
            return acc + offer * qty;
          }, 0);
          setTotalBillAmount(totalAmt);
          const totDisAmt = updatedData.reduce(
            (acc, item) => acc + (Number(item.Disamt) || 0),
            0,
          );
          steTotalBillDisAmt(totDisAmt);
          const totGrossAmt = totalAmt - totDisAmt;
          setTotalBillGrossAmount(totGrossAmt);
          setTotalBillNetAmount(totGrossAmt);

          const gstGroupedMap = new Map();
          updatedData.forEach((item) => {
            const key = item.GSTApplied;
            const netAmt = item.stock * item.offer;
            const basicAmt = netAmt * (100 / (100 + key));
            const cgstAmt = (netAmt * (key / 100)) / 2;
            if (!gstGroupedMap.has(key)) {
              gstGroupedMap.set(key, {
                GST: key,
                CGST_RATE: key / 2,
                Basic_amt: 0,
                CGST_AMT: 0,
                SGST: key / 2,
                SGST_AMT: 0,
                NET_AMT: 0,
              });
            }
            const existing = gstGroupedMap.get(key);
            existing.Basic_amt += basicAmt;
            existing.CGST_AMT += cgstAmt;
            existing.SGST_AMT += cgstAmt;
            existing.NET_AMT += netAmt;
          });
          setTotalGstData(Array.from(gstGroupedMap.values()));

          return updatedData;
        });
      }
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const modifyMastAPI = async (no) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CREDIT_BILLMAST&where=BILLNO=${no}`,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
      let data = response?.data;
      if (response?.status === 200) {
        userGetDetailsAPI(data[0]?.PhoneNo);
        setDisPer(data[0]?.DISPER);
        setTotalDiscountAmt(data[0]?.DISAMT);
      }
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const modifyExchangeDetAPI = async (no) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=BILL_ITEMS_DET&where=BILLNO=${no} AND TYPE='EB'`,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
      let data = response?.data;
      const modifiedData = data.map((item) => ({
        ACITY: "-",
        AGENTNAME: "-",
        Area: item?.AREA,
        BOXES: item?.BOXES,
        BRANCHNAME: "-",
        CASERATECP: null,
        CASERATEDP: null,
        CASERATERP: null,
        CGSTAMT: item?.CGSTAMT,
        CGSTRATE: item?.CGSTRATE,
        COLOUR: item?.COLOUR,
        CPrice: item?.rate,
        CTOKEN: 0,
        Code: item?.code,
        CompName: item?.CompName,
        DPrice: item?.DPrice,
        DesignCode: item?.ITEMCODE,
        Disamt: item?.ITEM_DIS,
        Disper: item?.DISPER,
        ECODE: item?.ECODE,
        ENTRYDATE: "-",
        ENTRYNO: 0,
        EQTY: 0,
        EXPDATE: null,
        FITTING: item?.FITTING,
        FLOOR: item?.FLOOR,
        FYEAR: "-",
        GST: item?.GST,
        GSTAMT: item?.GSTAMT,
        GrosDPrice: item?.ITEM_ACTRATE,
        GroupName: item?.GroupName,
        HID: item?.HID,
        HSNCODE: item?.HSNCODE,
        ICODETYPE: "-",
        IGSTAMT: item?.IGSTAMT,
        IGSTRATE: item?.IGSTRATE,
        InvNO: item?.INVNO,
        Invdate: item?.INVDATE,
        ItemCode: "0",
        LASTBILLDATE: "-",
        LENGTH: null,
        LOOSEPCS: null,
        LQTY: null,
        OFFER_DIS: 0,
        OQTY2: 0,
        OQTY3: 0,
        OQTY4: null,
        PACKPCS: null,
        PNTMODEL: "-",
        PQTY: 0,
        PTYPE: item?.PTYPE,
        Partyname: item?.PARTYNAME,
        ProdName: item?.itemname,
        Prodsize: item?.PRODSIZE,
        Qty: item?.qty,
        RESELLERPRICE: null,
        RQTY: null,
        SEMIWSALERPRICE: null,
        SET_ITEMS: false,
        SGSTAMT: item?.SGSTAMT,
        SGSTRATE: item?.SGSTRATE,
        SLEVES: item?.SLEVES,
        SMODEL: "-",
        SMCODE: String(item?.SMCODE) ?? "",
        SNO: 0,
        TOKEN: item?.TOKEN,
        USTATUS: null,
        WPER: 0,
        WPRICE: 0,
        stock: item?.qty,
        cloud_upload: false,
        mesure: null,
        offer: item?.ITEM_ACTRATE,
        taans: false,
      }));
      if (response?.status === 200) {
        setExchangeUpdatedData(modifiedData);
        setExchangeData((prevData) => {
          const updatedData = [
            ...prevData,
            ...modifiedData.map((item) => {
              const gstItem = gstData.find((gst) => gst.PType === item.PTYPE);
              let appliedGST = 0;
              if (gstItem?.ALLOWGSTVALUE === true) {
                const offerAmount = item.offer ?? 0;
                if (offerAmount <= gstItem.BELLOWVALUE)
                  appliedGST = gstItem.BELLOWPER;
                else if (offerAmount >= gstItem.ABOVEVALUE)
                  appliedGST = gstItem.ABOVEPER;
              } else {
                appliedGST = item?.GST;
              }
              return {
                ...item,
                stock: 1,
                GSTApplied: appliedGST,
                SMCODE: smCodeData[0]?.CODE ?? "",
              };
            }),
          ];

          const totalGst = updatedData.reduce((acc, item) => {
            const offer = Number(item.offer) || 0;
            const qty = Number(item.stock) || 0;
            const gst = Number(item.GSTApplied) || 0;
            const netAmt = qty * offer;
            const basic = netAmt * (100 / (100 + gst));
            return acc + basic * (gst / 100);
          }, 0);
          setTotalExchangeGstAmt(totalGst);

          const totalAmt = updatedData.reduce((acc, item) => {
            const offer = Number(item.offer) || 0;
            const qty = Number(item.stock) || 0;
            return acc + offer * qty;
          }, 0);
          setTotalExchangeAmount(totalAmt);
          const totDisAmt = updatedData.reduce(
            (acc, item) => acc + (Number(item.Disamt) || 0),
            0,
          );
          steTotalExchangeDisAmt(totDisAmt);
          const totGrossAmt = totalAmt - totDisAmt;
          setTotalExchangeGrossAmount(totGrossAmt);
          setTotalExchangeNetAmount(totGrossAmt);

          const gstGroupedMap = new Map();
          updatedData.forEach((item) => {
            const key = item.GSTApplied;
            const netAmt = item.stock * item.offer;
            const basicAmt = netAmt * (100 / (100 + key));
            const cgstAmt = (netAmt * (key / 100)) / 2;
            if (!gstGroupedMap.has(key)) {
              gstGroupedMap.set(key, {
                GST: key,
                CGST_RATE: key / 2,
                Basic_amt: 0,
                CGST_AMT: 0,
                SGST: key / 2,
                SGST_AMT: 0,
                NET_AMT: 0,
              });
            }
            const existing = gstGroupedMap.get(key);
            existing.Basic_amt += basicAmt;
            existing.CGST_AMT += cgstAmt;
            existing.SGST_AMT += cgstAmt;
            existing.NET_AMT += netAmt;
          });
          setTotalExchangeGstData(Array.from(gstGroupedMap.values()));

          return updatedData;
        });
      }
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const modifyHoldDetAPI = async (no) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=BILL_ITEMS_DET_HOLD&where=BILLNO=${no}`,
        {
          headers: {
            "Content-Type": "application/json",
            tenantName: tenantName,
          },
        },
      );
      let data = response?.data;
      const modifiedData = data.map((item) => ({
        ACITY: "-",
        AGENTNAME: "-",
        Area: item?.AREA,
        BOXES: item?.BOXES,
        BRANCHNAME: "-",
        CASERATECP: null,
        CASERATEDP: null,
        CASERATERP: null,
        CGSTAMT: item?.CGSTAMT,
        CGSTRATE: item?.CGSTRATE,
        COLOUR: item?.COLOUR,
        CPrice: item?.rate,
        CTOKEN: 0,
        Code: item?.code,
        CompName: item?.CompName,
        DPrice: item?.DPrice,
        DesignCode: item?.ITEMCODE,
        Disamt: item?.ITEM_DIS,
        Disper: item?.DISPER,
        ECODE: item?.ECODE,
        ENTRYDATE: "-",
        ENTRYNO: 0,
        EQTY: 0,
        EXPDATE: null,
        FITTING: item?.FITTING,
        FLOOR: item?.FLOOR,
        FYEAR: "-",
        GST: item?.GST,
        GSTAMT: item?.GSTAMT,
        GrosDPrice: item?.ITEM_ACTRATE,
        GroupName: item?.GroupName,
        HID: item?.HID,
        HSNCODE: item?.HSNCODE,
        ICODETYPE: "-",
        IGSTAMT: item?.IGSTAMT,
        IGSTRATE: item?.IGSTRATE,
        InvNO: item?.INVNO,
        Invdate: item?.INVDATE,
        ItemCode: "0",
        LASTBILLDATE: "-",
        LENGTH: null,
        LOOSEPCS: null,
        LQTY: null,
        OFFER_DIS: 0,
        OQTY2: 0,
        OQTY3: 0,
        OQTY4: null,
        PACKPCS: null,
        PNTMODEL: "-",
        PQTY: 0,
        PTYPE: item?.PTYPE,
        Partyname: item?.PARTYNAME,
        ProdName: item?.itemname,
        Prodsize: item?.PRODSIZE,
        Qty: item?.ACTQTY,
        RESELLERPRICE: null,
        RQTY: null,
        SEMIWSALERPRICE: null,
        SET_ITEMS: false,
        SGSTAMT: item?.SGSTAMT,
        SGSTRATE: item?.SGSTRATE,
        SLEVES: item?.SLEVES,
        SMODEL: item?.STATION,
        SMCODE: String(item?.SMCODE) ?? "",
        SNO: 0,
        TOKEN: item?.TOKEN,
        USTATUS: null,
        WPER: 0,
        WPRICE: 0,
        stock: item?.qty,
        cloud_upload: false,
        mesure: null,
        offer: item?.ITEM_ACTRATE,
        taans: false,
      }));
      if (response?.status === 200) {
        setBarCodeData((prevData) => {
          let updatedData = [...prevData];
          const newItems = modifiedData.map((item) => {
            const gstItem = gstData.find((gst) => gst.PType === item.PTYPE);
            let appliedGST = 0;
            if (gstItem) {
              const offerAmount = Number(item.offer) || 0;
              if (offerAmount <= gstItem.BELLOWVALUE)
                appliedGST = gstItem.BELLOWPER;
              else if (offerAmount >= gstItem.ABOVEVALUE)
                appliedGST = gstItem.ABOVEPER;
            }
            return { ...item, stock: 1, GSTApplied: appliedGST };
          });
          newItems.forEach((newItem) => {
            const index = updatedData.findIndex(
              (existing) => existing.DesignCode === newItem.DesignCode,
            );
            if (index !== -1) {
              const currentStock = updatedData[index].stock || 0;
              const maxQty = updatedData[index].Qty || 0;
              if (currentStock < maxQty) {
                updatedData[index] = {
                  ...updatedData[index],
                  stock: currentStock + 1,
                };
              } else {
                message.warning("Reached Maximum Qty, Cannot Add More");
              }
            } else {
              updatedData.push(newItem);
            }
          });
          return updatedData;
        });
      }
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const deleteBarcodeMastAPI = async () => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=CREDIT_BILLMAST&where=BILLNO=${billNo}`,
        {},
        { headers: { tenantName: tenantName } },
      );
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const deleteBarcodeDetAPI = async () => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=BILL_ITEMS_DET&where=BILLNO=${billNo}`,
        {},
        { headers: { tenantName: tenantName } },
      );
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const deleteBarcodeDataAPI = async () => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=CREDIT_BILLDATA&where=BILLNO=${billNo}`,
        {},
        { headers: { tenantName: tenantName } },
      );
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const deleteExchangeDetAPI = async () => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=BILL_ITEMS_DET_EXCHANGE&where=BILLNO=${billNo}`,
        {},
        { headers: { tenantName: tenantName } },
      );
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const deleteBarcodePayModeAPI = async () => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=SINGLE_ENTRY_DATA&where=ENTRYNO=${invNo}`,
        {},
        { headers: { tenantName: tenantName } },
      );
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const deleteBarcodeOnlinePayAPI = async () => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=ORG_CARDDET&where=BILLNO=${invNo}`,
        {},
        { headers: { tenantName: tenantName } },
      );
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const deleteBarcodeLedgerAPI = async () => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=TRANS_ENTRY_DATA_CUST&where=ENTRYNO=${billNo}`,
        {},
        { headers: { tenantName: tenantName } },
      );
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const deleteBarcodeMastHoldAPI = async (hold) => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=CREDIT_BILLMAST_HOLD&where=BILLNO=${hold}`,
        {},
        { headers: { tenantName: tenantName } },
      );
      holdMastNoAPI();
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const deleteBarcodeDetHoldAPI = async (hold) => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=BILL_ITEMS_DET_HOLD&where=BILLNO=${hold}`,
        {},
        { headers: { tenantName: tenantName } },
      );
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const deleteCustomerDetailsAPI = async (mobile) => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=CUSTOMER&where=MOBILENO='${mobile}'`,
        {},
        { headers: { tenantName: tenantName } },
      );
    } catch (error) {
      console.error(
        "Error posting data:",
        error.response?.data || error.message,
      );
    }
  };

  const handleDelete = (indexToDelete) => {
    smCodeRef.current?.focus();
    setBarCodeData((prevData) => {
      const updatedData = [...prevData];
      updatedData.splice(indexToDelete, 1);
      return updatedData;
    });
  };

  const handleEchangeDelete = (indexToDelete) => {
    smCodeRef.current?.focus();
    setExchangeData((prevData) => {
      const updatedData = [...prevData];
      updatedData.splice(indexToDelete, 1);
      return updatedData;
    });
  };

  /* ============================= TABLE COLUMNS (badge-styled, same as SalesInvoice) ============================= */

  const columns = [
    {
      title: "#",
      dataIndex: "sno",
      key: "sno",
      width: 44,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: "13px", fontWeight: 800, color: "#64748b" }}>
          {index + 1}
        </span>
      ),
    },
    {
      title: "Sm Code",
      dataIndex: "SMCODE",
      key: "SMCODE",
      width: 78,
      render: (text) => {
        const value =
          text === null || text === undefined || text === "" || text === "-"
            ? ""
            : text;
        return value ? (
          <span
            style={{
              display: "inline-block",
              padding: "3px 9px",
              borderRadius: 7,
              background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
              border: "1.5px solid #a5b4fc",
              color: "#3730a3",
              fontWeight: 800,
              fontSize: "12.5px",
            }}
          >
            {value}
          </span>
        ) : null;
      },
    },
    {
      key: "actions",
      align: "left",
      width: 64,
      className: "col-divider",
      render: (_, record, index) => (
        <div className="si-row-actions">
          <ItemDetailsPopover record={record} smCodeRef={smCodeRef} />
          {adminType === "ADMIN" || (adminType === "OPERATOR" && !billNo) ? (
            <Popconfirm
              title="Are you sure you want to delete?"
              onConfirm={() => {
                smCodeRef.current?.focus();
                handleDelete(index);
              }}
              onCancel={() => smCodeRef.current?.focus()}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                style={{ fontSize: "16px", padding: 0 }}
              />
            </Popconfirm>
          ) : (
            ""
          )}
        </div>
      ),
    },
    {
      title: "Design Code",
      dataIndex: "DesignCode",
      key: "DesignCode",
      width: 120,
      render: (value) => (
        <span
          style={{
            display: "inline-block",
            padding: "5px 12px",
            borderRadius: 8,
            background: "linear-gradient(135deg, #FDE68A, #FBBF24)",
            border: "1.5px solid #D97706",
            color: "#78350F",
            fontWeight: 900,
            fontSize: "13px",
            letterSpacing: "0.3px",
            boxShadow: "0 2px 8px rgba(217,119,6,0.30)",
          }}
        >
          {value}
        </span>
      ),
    },
    {
      title: "Particulars",
      dataIndex: "ProdName",
      key: "ProdName",
      width: 200,
      render: (text) => {
        const shouldTooltip = text && text.length > 15;
        const display = shouldTooltip ? `${text.substring(0, 15)}...` : text;
        const content = (
          <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
            {display}
          </span>
        );
        return shouldTooltip ? (
          <Tooltip title={text}>{content}</Tooltip>
        ) : (
          content
        );
      },
    },
    {
      title: "QTY",
      dataIndex: "stock",
      align: "center",
      key: "stock",
      width: 60,
      className: "col-divider",
      render: (_, record, index) => (
        <div
          onClick={() => {
            if (
              adminType === "ADMIN" ||
              (adminType === "OPERATOR" && !billNo)
            ) {
              setItemDetailsOpen(true);
              setItemDetailsData(record);
              setItemDEsignCode(index);
            }
            smCodeRef.current?.focus();
          }}
          style={{ cursor: "pointer" }}
        >
          <span
            style={{
              display: "inline-block",
              padding: "3px 10px",
              borderRadius: 7,
              background: "linear-gradient(135deg, #DBEAFE, #BFDBFE)",
              border: "1.5px solid #60A5FA",
              color: "#1D4ED8",
              fontWeight: 800,
              fontSize: "13px",
            }}
          >
            {Number(record?.stock)?.toFixed(2)}
          </span>
        </div>
      ),
    },
    {
      title: "Rate",
      dataIndex: "CPrice",
      align: "right",
      key: "CPrice",
      width: 80,
      render: (_, record) => (
        <span style={{ fontSize: "13.5px", fontWeight: 800, color: "#334155" }}>
          {Number(record?.CPrice)?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Dis(%)",
      dataIndex: "Disper",
      align: "right",
      key: "Disper",
      width: 56,
      render: (_, record) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 6,
            background: "linear-gradient(135deg, #FCE7F3, #FBCFE8)",
            border: "1.5px solid #F472B6",
            color: "#BE185D",
            fontWeight: 800,
            fontSize: "12.5px",
          }}
        >
          {Number(record?.Disper)}
        </span>
      ),
    },
    {
      title: "Dis.Amt",
      dataIndex: "Disamt",
      align: "right",
      key: "Disamt",
      width: 80,
      render: (_, record) => (
        <span
          style={{
            display: "inline-block",
            padding: "3px 9px",
            borderRadius: 7,
            background: "linear-gradient(135deg, #FFEDD5, #FED7AA)",
            border: "1.5px solid #FB923C",
            color: "#C2410C",
            fontWeight: 800,
            fontSize: "13px",
          }}
        >
          {Number(record?.Disamt)?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Act.Rate",
      dataIndex: "offer",
      align: "right",
      key: "offer",
      width: 84,
      className: "col-divider",
      render: (_, record) => (
        <span
          style={{
            display: "inline-block",
            padding: "3px 10px",
            borderRadius: 7,
            background: "linear-gradient(135deg, #D1FAE5, #A7F3D0)",
            border: "1.5px solid #34D399",
            color: "#047857",
            fontWeight: 800,
            fontSize: "13px",
          }}
        >
          {Number(record?.offer)?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Amount",
      className: "blue-background-column",
      dataIndex: "stock",
      align: "right",
      key: "amount",
      width: 100,
      render: (_, record) => (
        <span
          style={{
            display: "inline-block",
            padding: "5px 12px",
            borderRadius: 8,
            background: "linear-gradient(135deg, #BBF7D0, #86EFAC)",
            border: "1.5px solid #22C55E",
            color: "#15803D",
            fontWeight: 900,
            fontSize: "13.5px",
            fontFamily: "monospace",
            boxShadow: "0 2px 8px rgba(34,197,94,0.28)",
          }}
        >
          {Number(record?.stock * record.offer)?.toFixed(2)}
        </span>
      ),
    },
  ];

  const columns1 = [
    {
      title: "#",
      dataIndex: "sno",
      key: "sno",
      width: 44,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: "13px", fontWeight: 800, color: "#64748b" }}>
          {index + 1}
        </span>
      ),
    },
    {
      title: "Sm Code",
      dataIndex: "SMCODE",
      key: "SMCODE",
      width: 78,
      render: (text) => {
        const value =
          text === null || text === undefined || text === "" || text === "-"
            ? ""
            : text;
        return value ? (
          <span
            style={{
              display: "inline-block",
              padding: "3px 9px",
              borderRadius: 7,
              background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
              border: "1.5px solid #a5b4fc",
              color: "#3730a3",
              fontWeight: 800,
              fontSize: "12.5px",
            }}
          >
            {value}
          </span>
        ) : null;
      },
    },
    {
      key: "actions",
      align: "left",
      width: 64,
      className: "col-divider",
      render: (_, record, index) => (
        <div className="si-row-actions">
          <ItemDetailsPopover record={record} smCodeRef={smCodeRef} />
          {adminType === "ADMIN" || (adminType === "OPERATOR" && !billNo) ? (
            <Popconfirm
              title="Are you sure you want to delete?"
              onConfirm={() => {
                handleEchangeDelete(index);
                smCodeRef.current?.focus();
              }}
              onCancel={() => smCodeRef.current?.focus()}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                style={{ fontSize: "16px", padding: 0 }}
              />
            </Popconfirm>
          ) : (
            ""
          )}
        </div>
      ),
    },
    {
      title: "Design Code",
      dataIndex: "DesignCode",
      key: "DesignCode",
      width: 120,
      render: (value) => (
        <span
          style={{
            display: "inline-block",
            padding: "5px 12px",
            borderRadius: 8,
            background: "linear-gradient(135deg, #FDE68A, #FBBF24)",
            border: "1.5px solid #D97706",
            color: "#78350F",
            fontWeight: 900,
            fontSize: "13px",
            letterSpacing: "0.3px",
            boxShadow: "0 2px 8px rgba(217,119,6,0.30)",
          }}
        >
          {value}
        </span>
      ),
    },
    {
      title: "Particulars",
      dataIndex: "ProdName",
      key: "ProdName",
      width: 200,
      render: (text) => {
        const shouldTooltip = text && text.length > 15;
        const display = shouldTooltip ? `${text.substring(0, 15)}...` : text;
        const content = (
          <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
            {display}
          </span>
        );
        return shouldTooltip ? (
          <Tooltip title={text}>{content}</Tooltip>
        ) : (
          content
        );
      },
    },
    {
      title: "QTY",
      dataIndex: "stock",
      align: "center",
      key: "stock",
      width: 60,
      className: "col-divider",
      render: (_, record, index) => (
        <div
          onClick={() => {
            if (
              adminType === "ADMIN" ||
              (adminType === "OPERATOR" && !billNo)
            ) {
              setItemExchangeDetailsOpen(true);
              setItemExchangeDetailsData(record);
              setItemExchangeDEsignCode(index);
            }
            smCodeRef.current?.focus();
          }}
          style={{ cursor: "pointer" }}
        >
          <span
            style={{
              display: "inline-block",
              padding: "3px 10px",
              borderRadius: 7,
              background: "linear-gradient(135deg, #DBEAFE, #BFDBFE)",
              border: "1.5px solid #60A5FA",
              color: "#1D4ED8",
              fontWeight: 800,
              fontSize: "13px",
            }}
          >
            {Number(record?.stock)?.toFixed(2)}
          </span>
        </div>
      ),
    },
    {
      title: "Rate",
      dataIndex: "CPrice",
      align: "right",
      key: "CPrice",
      width: 80,
      render: (_, record) => (
        <span style={{ fontSize: "13.5px", fontWeight: 800, color: "#334155" }}>
          {Number(record?.CPrice)?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Dis(%)",
      dataIndex: "Disper",
      align: "right",
      key: "Disper",
      width: 56,
      render: (_, record) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 6,
            background: "linear-gradient(135deg, #FCE7F3, #FBCFE8)",
            border: "1.5px solid #F472B6",
            color: "#BE185D",
            fontWeight: 800,
            fontSize: "12.5px",
          }}
        >
          {Number(record?.Disper)}
        </span>
      ),
    },
    {
      title: "Dis.Amt",
      dataIndex: "Disamt",
      align: "right",
      key: "Disamt",
      width: 80,
      render: (_, record) => (
        <span
          style={{
            display: "inline-block",
            padding: "3px 9px",
            borderRadius: 7,
            background: "linear-gradient(135deg, #FFEDD5, #FED7AA)",
            border: "1.5px solid #FB923C",
            color: "#C2410C",
            fontWeight: 800,
            fontSize: "13px",
          }}
        >
          {Number(record?.Disamt)?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Act.Rate",
      dataIndex: "offer",
      align: "right",
      key: "offer",
      width: 84,
      className: "col-divider",
      render: (_, record) => (
        <span
          style={{
            display: "inline-block",
            padding: "3px 10px",
            borderRadius: 7,
            background: "linear-gradient(135deg, #D1FAE5, #A7F3D0)",
            border: "1.5px solid #34D399",
            color: "#047857",
            fontWeight: 800,
            fontSize: "13px",
          }}
        >
          {Number(record?.offer)?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Amount",
      className: "blue-background-column",
      dataIndex: "stock",
      align: "right",
      key: "amount",
      width: 100,
      render: (_, record) => (
        <span
          style={{
            display: "inline-block",
            padding: "5px 12px",
            borderRadius: 8,
            background: "linear-gradient(135deg, #BBF7D0, #86EFAC)",
            border: "1.5px solid #22C55E",
            color: "#15803D",
            fontWeight: 900,
            fontSize: "13.5px",
            fontFamily: "monospace",
            boxShadow: "0 2px 8px rgba(34,197,94,0.28)",
          }}
        >
          {Number(record?.stock * record.offer)?.toFixed(2)}
        </span>
      ),
    },
  ];

  useEffect(() => {
    invNoAPI();
    commissionAPI();
    gstCheckAPI();
    userAPI();
    onlineAccNoAPI();
    holdNoAPI();
    holdMastNoAPI();
  }, []);

  useEffect(() => {
    const disAmt = barCodeData.reduce(
      (acc, item) =>
        acc + (Number(item.Disamt) || 0) * (Number(item.stock) || 0),
      0,
    );
    const qty = barCodeData.reduce(
      (sum, item) => sum + (parseFloat(item.stock) || 0),
      0,
    );
    const totalAmt = barCodeData.reduce(
      (acc, item) =>
        acc + (Number(item.CPrice) || 0) * (Number(item.stock) || 0),
      0,
    );
    const grossAmt = totalAmt - disAmt;
    const discountPer = grossAmt * disPer;
    const disCountAmt = discountPer / 100;
    const netAmount = grossAmt - disCountAmt;
    const netAmt = grossAmt - totalDiscountAmt;
    const overalAmount = disCountAmt > 0 ? netAmount : (netAmt ?? 0);
    steTotalBillDisAmt(disAmt);
    setTotalBillQty(qty);
    setTotalBillAmount(totalAmt);
    setTotalBillGrossAmount(grossAmt);
    setTotalBillNetAmount(overalAmount);
  }, [barCodeData, disPer]);

  useEffect(() => {
    const disAmt = exchangeData.reduce(
      (acc, item) =>
        acc + (Number(item.Disamt) || 0) * (Number(item.stock) || 0),
      0,
    );
    const qty = exchangeData.reduce(
      (sum, item) => sum + (parseFloat(item.stock) || 0),
      0,
    );
    const totalAmt = exchangeData.reduce(
      (acc, item) =>
        acc + (Number(item.CPrice) || 0) * (Number(item.stock) || 0),
      0,
    );
    const grossAmt = totalAmt - disAmt;
    const discountPer = grossAmt * disPer;
    const disCountAmt = discountPer / 100;
    const netAmount = grossAmt - disCountAmt;
    const netAmt = grossAmt - totalDiscountAmt;
    const overalAmount = disCountAmt > 0 ? netAmount : (netAmt ?? 0);
    steTotalExchangeDisAmt(disAmt);
    setTotalExchangeQty(qty);
    setTotalExchangeAmount(totalAmt);
    setTotalExchangeGrossAmount(grossAmt);
    setTotalExchangeNetAmount(overalAmount);
  }, [exchangeData, disPer]);

  useEffect(() => {
    const overalAmount = totalBillNetAmount - totalExchangeNetAmount;
    setTotalNetAmount(overalAmount);
    setCashAmt(overalAmount);
  }, [exchangeData, barCodeData, totalBillNetAmount, totalExchangeNetAmount]);

  const handleTagNoKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitRef.current?.click();
    }
  };
  const handleSmCodeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      smSubmitRef.current?.click();
    }
  };
  const handleEstNoKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      fetchRef.current?.click();
    }
  };
  const handleMobileNoKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      searchRef.current?.click();
    }
  };
  const handleExchangeNoKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      exchangeSubmitRef.current?.click();
    }
  };

  const filterDataByHoldNo = (value) => {
    if (value === "" || value === null || value === undefined) {
      setHNo();
      setSelectedHoldRowKeys([]);
      setFilteredHoldData(holdData);
      return;
    }
    const num = Number(value);
    const filtered = holdData.filter((item) => item.billno === num);
    setFilteredHoldData(filtered);
    const exactMatch = holdData.find((item) => item.billno === num);
    setSelectedHoldRowKeys(exactMatch ? [exactMatch.billno] : []);
  };

  const handleSearchHoldInput = (e) => {
    const value = e.target.value;
    if (value === "0") return;
    if (value.startsWith("0")) return;
    setSearchHoldText(value);
    filterDataByHoldNo(value);
  };

  const handleCheckboxChangeHold = (billNo) => {
    const isSelected = selectedHoldRowKeys.includes(billNo);
    const newKeys = isSelected ? [] : [billNo];
    const text = newKeys.join(", ");
    setSelectedHoldRowKeys(newKeys);
    setSearchHoldText(text);
    setHNo(text);
    filterDataByHoldNo(text);
  };

  useEffect(() => {
    if (smCodeData && smCodeData.length > 0) tagNoRef.current?.focus();
  }, [smCodeData]);

  const ledgerData = [
    {
      key: 1,
      tagNo: Number(prvBalance)?.toFixed(0),
      balance: Number(creditAmt)?.toFixed(0),
      amount: Number(totalBalance)?.toFixed(0),
    },
  ];

  const fetchWithRetry = async (url, retries = 3, delay = 300) => {
    for (let i = 0; i < retries; i++) {
      try {
        const res = await fetch(url, { mode: "cors" });
        if (res.ok) return res;
      } catch (e) {
        console.warn(`Retry ${i + 1} for ${url}`);
      }
      await new Promise((r) => setTimeout(r, delay));
    }
    throw new Error("Failed after retries: " + url);
  };

  const resizeBase64Img = (base64Str, maxWidth = 500, maxHeight = 500) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          if (width / height > maxWidth / maxHeight) {
            height *= maxWidth / width;
            width = maxWidth;
          } else {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = base64Str;
    });

  const urlToBase64 = async (url) => {
    const cleanUrl = decodeURIComponent(url);
    const proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(cleanUrl)}`;
    const response = await fetchWithRetry(proxyUrl, 3, 800);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const resized = await resizeBase64Img(reader.result);
          resolve(resized);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  useEffect(() => {
    if (!totalNetAmount) return;
    const canvas = qrRef.current?.querySelector("canvas");
    if (canvas) setBase64QR(canvas.toDataURL("image/png"));
  }, [totalNetAmount, upiLink]);

  const custTypeData = [{ CUSTTYPE: "RETAIL" }, { CUSTTYPE: "WHOLESALE" }];

  useEffect(() => {
    const convertImage = async () => {
      const imageUrl = logoImage;
      try {
        const base64 = await urlToBase64(imageUrl);
        setBase64Images(base64);
      } catch (err) {
        console.error("Image conversion failed:", err);
      }
    };
    convertImage();
  }, [logoImage]);

  const handleInvoicePdfDownload = async (nextInvNo) => {
    // Uses the shared BillPdfDownload helper (same as SalesInvoice) so the
    // exchange-bill PDF keeps the bill + exchange + balance breakdown.
    handleBillPrint(nextInvNo);
  };

  const handleBillPrint = (nextInvNo) => {
    BillPdfDownload(
      userDetails,
      userPhone,
      address,
      address1,
      gstNo,
      nextInvNo,
      billNo,
      billDate,
      userName,
      barCodeData,
      totalNetAmount,
      base64Images,
      base64QR,
      totalGstData,
      ins,
      ins1,
      ins2,
      ins3,
      ins4,
      ins5,
      ins6,
      totalBillAmount,
      totalBillDiSAmt,
      totalBillNetAmount,
      totalBillQty,
      totalExchangeAmount,
      totalExchangeDiSAmt,
      totalExchangeNetAmount,
      totalExchangeQty,
      gstValue,
      cashAmt,
      cardAmt,
      onlineAmt,
      upiAmt,
      creditAmt,
    );
  };

  const fetchStationInfo = async () => {
    try {
      const sysInfoRes = await axios.get(
        "http://localhost:5445/api/print/system-info",
      );
      const systemName = sysInfoRes.data?.systemName;

      if (!systemName) {
        console.warn("system-info did not return a systemName");
        return;
      }

      const configRes = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=COUNTER_CONFIG&where=SYSNAME='${systemName}'`,
        { headers: { tenantName } },
      );

      const configData = configRes.data;

      if (Array.isArray(configData) && configData.length > 0) {
        setThermalPrinter(configData[0]?.BILLINGPRINTER || "");
        setThermalPrinterDetails(configData[0]);
      } else {
        console.warn(`No COUNTER_CONFIG row found for SYSNAME='${systemName}'`);
      }
    } catch (error) {
      console.error("Failed to fetch station info:", error);
    }
  };

  const handlePrint = async () => {
    try {
      const canvas = await html2canvas(receiptRef.current, {
        scale: thermalPrinterDetails?.SCALE || 4,
        width: thermalPrinterDetails?.WIDTH || 280,
        backgroundColor: "#fff",
        useCORS: true,
      });

      const base64 = canvas.toDataURL("image/png").split(",")[1];

      await axios.post("http://localhost:5442/api/print/silent", {
        fileBase64: base64,
        printerName: thermalPrinter,
      });
    } catch (err) {
      console.error("Print failed", err);
    }
  };

  useEffect(() => {
    fetchStationInfo();
  }, []);

  const handleEposPrint = (nextInvNo) => {
    printReceipt(
      localIp,
      userDetails,
      userPhone,
      address,
      address1,
      gstNo,
      nextInvNo,
      billNo,
      userName,
      barCodeData,
      totalNetAmount,
      base64Images,
      base64QR,
      totalGstData,
      ins,
      ins1,
      ins2,
      ins3,
      ins4,
      ins5,
      ins6,
      totalBillAmount,
      totalBillDiSAmt,
      totalBillNetAmount,
      totalBillQty,
      totalExchangeAmount,
      totalExchangeDiSAmt,
      totalExchangeNetAmount,
      totalExchangeQty,
    );
  };

  const handleHomeBillSave = () => {
    if (Number(qty) === 0) {
      message.open({
        type: "error",
        content: <div style={{ fontSize: "20px" }}>Check Quantity</div>,
      });
      return;
    }
    const homeData = [
      {
        ACITY: "-",
        AGENTNAME: "-",
        Area: "-",
        BOXES: null,
        BRANCHNAME: "-",
        CASERATECP: null,
        CASERATEDP: null,
        CASERATERP: null,
        CGSTAMT: 0,
        CGSTRATE: 0,
        COLOUR: "-",
        CPrice: rate,
        CTOKEN: 0,
        Code: "-",
        CompName: brandName,
        DPrice: 0,
        DesignCode: designCode,
        Disamt: disAmt,
        Disper: disCount,
        ECODE: "-",
        ENTRYDATE: "-",
        ENTRYNO: 0,
        EQTY: 0,
        EXPDATE: null,
        FITTING: "-",
        FLOOR: "-",
        FYEAR: "-",
        GST: 0,
        GSTAMT: 0,
        GrosDPrice: 0,
        GroupName: "-",
        HID: 0,
        HSNCODE: hsnCode,
        ICODETYPE: "-",
        IGSTAMT: 0,
        IGSTRATE: 0,
        InvNO: "-",
        Invdate: "-",
        ItemCode: "0",
        LASTBILLDATE: "-",
        LENGTH: null,
        LOOSEPCS: null,
        LQTY: null,
        OFFER_DIS: 0,
        OQTY2: 0,
        OQTY3: 0,
        OQTY4: null,
        PACKPCS: null,
        PNTMODEL: "-",
        PQTY: 0,
        PTYPE: pTypeName,
        Partyname: "-",
        ProdName: productName,
        Prodsize: "-",
        Qty: qty,
        RESELLERPRICE: null,
        RQTY: null,
        SEMIWSALERPRICE: null,
        SET_ITEMS: false,
        SGSTAMT: 0,
        SGSTRATE: 0,
        SLEVES: "-",
        SMODEL: "-",
        SNO: 0,
        TOKEN: 0,
        USTATUS: null,
        WPER: 0,
        WPRICE: 0,
        stock: qty,
        cloud_upload: false,
        mesure: null,
        offer: amount,
        taans: false,
      },
    ];
    setHomeDetailsOpen(false);
    setQty(0);
    setAmount(0);
    setDisCount(0);
    setRate(0);
    setDisAmt(0);
    setProductName("");
    setDesignCode("");
    setPTypeName("");
    setHsnCode("");
    setBrandName("");
    setProductData([]);
    setBarCodeData((prevData) => {
      let updatedData = [...prevData];
      const newItems = homeData.map((item) => {
        const gstItem = gstData.find((gst) => gst.PType === item.PTYPE);
        let appliedGST = 0;
        if (gstItem) {
          const offerAmount = item.offer ?? 0;
          if (offerAmount <= gstItem.BELLOWVALUE)
            appliedGST = gstItem.BELLOWPER;
          else if (offerAmount >= gstItem.ABOVEVALUE)
            appliedGST = gstItem.ABOVEPER;
        }
        return {
          ...item,
          GSTApplied: appliedGST,
          SMCODE: smCodeData[0]?.CODE ?? "",
        };
      });
      newItems.forEach((newItem) => {
        if (newItem.stock > newItem.Qty) {
          message.open({
            type: "error",
            content: (
              <div style={{ fontSize: "18px" }}>
                Stock exceeded for Design Code {newItem.DesignCode}
              </div>
            ),
          });
          return;
        }
        updatedData.push(newItem);
      });
      return updatedData;
    });
  };

  const handleHomeExchangeSave = () => {
    if (Number(qty) === 0) {
      message.open({
        type: "error",
        content: <div style={{ fontSize: "20px" }}>Check Quantity</div>,
      });
      return;
    }
    const homeData = [
      {
        ACITY: "-",
        AGENTNAME: "-",
        Area: "-",
        BOXES: null,
        BRANCHNAME: "-",
        CASERATECP: null,
        CASERATEDP: null,
        CASERATERP: null,
        CGSTAMT: 0,
        CGSTRATE: 0,
        COLOUR: "-",
        CPrice: rate,
        CTOKEN: 0,
        Code: "-",
        CompName: brandName,
        DPrice: 0,
        DesignCode: designCode,
        Disamt: disAmt,
        Disper: disCount,
        ECODE: "-",
        ENTRYDATE: "-",
        ENTRYNO: 0,
        EQTY: 0,
        EXPDATE: null,
        FITTING: "-",
        FLOOR: "-",
        FYEAR: "-",
        GST: 0,
        GSTAMT: 0,
        GrosDPrice: 0,
        GroupName: "-",
        HID: 0,
        HSNCODE: hsnCode,
        ICODETYPE: "-",
        IGSTAMT: 0,
        IGSTRATE: 0,
        InvNO: "-",
        Invdate: "-",
        ItemCode: "0",
        LASTBILLDATE: "-",
        LENGTH: null,
        LOOSEPCS: null,
        LQTY: null,
        OFFER_DIS: 0,
        OQTY2: 0,
        OQTY3: 0,
        OQTY4: null,
        PACKPCS: null,
        PNTMODEL: "-",
        PQTY: 0,
        PTYPE: pTypeName,
        Partyname: "-",
        ProdName: productName,
        Prodsize: "-",
        Qty: qty,
        RESELLERPRICE: null,
        RQTY: null,
        SEMIWSALERPRICE: null,
        SET_ITEMS: false,
        SGSTAMT: 0,
        SGSTRATE: 0,
        SLEVES: "-",
        SMODEL: "-",
        SNO: 0,
        TOKEN: 0,
        USTATUS: null,
        WPER: 0,
        WPRICE: 0,
        stock: qty,
        cloud_upload: false,
        mesure: null,
        offer: amount,
        taans: false,
      },
    ];
    setHomeExchangeDetailsOpen(false);
    setQty(0);
    setAmount(0);
    setDisCount(0);
    setRate(0);
    setDisAmt(0);
    setProductName("");
    setDesignCode("");
    setPTypeName("");
    setHsnCode("");
    setBrandName("");
    setProductData([]);
    setExchangeData((prevData) => {
      let updatedData = [...prevData];
      const newItems = homeData.map((item) => {
        const gstItem = gstData.find((gst) => gst.PType === item.PTYPE);
        let appliedGST = 0;
        if (gstItem) {
          const offerAmount = item.offer ?? 0;
          if (offerAmount <= gstItem.BELLOWVALUE)
            appliedGST = gstItem.BELLOWPER;
          else if (offerAmount >= gstItem.ABOVEVALUE)
            appliedGST = gstItem.ABOVEPER;
        }
        return {
          ...item,
          GSTApplied: appliedGST,
          SMCODE: smCodeData[0]?.CODE ?? "",
        };
      });
      newItems.forEach((newItem) => {
        if (newItem.stock > newItem.Qty) {
          message.open({
            type: "error",
            content: (
              <div style={{ fontSize: "18px" }}>
                Stock exceeded for Design Code {newItem.DesignCode}
              </div>
            ),
          });
          return;
        }
        updatedData.push(newItem);
      });
      return updatedData;
    });
  };

  const handleTaansBillSave = () => {
    if (Number(taansData[0]?.stock) === 0) {
      message.open({
        type: "error",
        content: <div style={{ fontSize: "20px" }}>Check Quantity</div>,
      });
      return;
    }
    setTaansOpen(false);
    setTaansData([]);
    setBarCodeData((prevData) => {
      let updatedData = [...prevData];
      const newItems = taansData.map((item) => {
        const gstItem = gstData.find((gst) => gst.PType === item.PTYPE);
        let appliedGST = 0;
        if (gstItem) {
          const offerAmount = item.offer ?? 0;
          if (offerAmount <= gstItem.BELLOWVALUE)
            appliedGST = gstItem.BELLOWPER;
          else if (offerAmount >= gstItem.ABOVEVALUE)
            appliedGST = gstItem.ABOVEPER;
        }
        return {
          ...item,
          GSTApplied: appliedGST,
          SMCODE: smCodeData[0]?.CODE ?? "",
        };
      });
      newItems.forEach((newItem) => {
        if (newItem.stock > newItem.Qty) {
          message.open({
            type: "error",
            content: (
              <div style={{ fontSize: "18px" }}>
                Stock exceeded for Design Code {newItem.DesignCode}
              </div>
            ),
          });
          return;
        }
        updatedData.push(newItem);
      });
      return updatedData;
    });
  };

  const handleTaansExchangeSave = () => {
    if (Number(exchangeTaansData[0]?.stock) === 0) {
      message.open({
        type: "error",
        content: <div style={{ fontSize: "20px" }}>Check Quantity</div>,
      });
      return;
    }
    setExchangeTaansOpen(false);
    setExchangeTaansData([]);
    setExchangeData((prevData) => {
      let updatedData = [...prevData];
      const newItems = exchangeTaansData.map((item) => {
        const gstItem = gstData.find((gst) => gst.PType === item.PTYPE);
        let appliedGST = 0;
        if (gstItem) {
          const offerAmount = item.offer ?? 0;
          if (offerAmount <= gstItem.BELLOWVALUE)
            appliedGST = gstItem.BELLOWPER;
          else if (offerAmount >= gstItem.ABOVEVALUE)
            appliedGST = gstItem.ABOVEPER;
        }
        return { ...item, GSTApplied: appliedGST };
      });
      newItems.forEach((newItem) => {
        if (newItem.stock > newItem.Qty) {
          message.open({
            type: "error",
            content: (
              <div style={{ fontSize: "18px" }}>
                Stock exceeded for Design Code {newItem.DesignCode}
              </div>
            ),
          });
          return;
        }
        updatedData.push(newItem);
      });
      return updatedData;
    });
  };

  const handleOfferABillSave = () => {
    if (Number(offerAData[0]?.stock) === 0) {
      message.open({
        type: "error",
        content: <div style={{ fontSize: "20px" }}>Check Quantity</div>,
      });
      return;
    }
    setOfferAOpen(false);
    setOfferAData([]);
    setBarCodeData((prevData) => {
      let updatedData = [...prevData];
      const newItems = offerAData.map((item) => {
        const gstItem = gstData.find((gst) => gst.PType === item.PTYPE);
        let appliedGST = 0;
        if (gstItem) {
          const offerAmount = item.offer ?? 0;
          if (offerAmount <= gstItem.BELLOWVALUE)
            appliedGST = gstItem.BELLOWPER;
          else if (offerAmount >= gstItem.ABOVEVALUE)
            appliedGST = gstItem.ABOVEPER;
        }
        return {
          ...item,
          GSTApplied: appliedGST,
          SMCODE: smCodeData[0]?.CODE ?? "",
        };
      });
      newItems.forEach((newItem) => {
        if (newItem.stock > newItem.Qty) return;
        updatedData.push(newItem);
      });
      return updatedData;
    });
  };

  const handleOfferBBillSave = () => {
    if (Number(offerBData[0]?.stock) === 0) {
      message.open({
        type: "error",
        content: <div style={{ fontSize: "20px" }}>Check Quantity</div>,
      });
      return;
    }
    setOfferBOpen(false);
    setOfferBData([]);
    setBarCodeData((prevData) => {
      let updatedData = [...prevData];
      const newItems = offerBData.map((item) => {
        const gstItem = gstData.find((gst) => gst.PType === item.PTYPE);
        let appliedGST = 0;
        if (gstItem) {
          const offerAmount = item.offer ?? 0;
          if (offerAmount <= gstItem.BELLOWVALUE)
            appliedGST = gstItem.BELLOWPER;
          else if (offerAmount >= gstItem.ABOVEVALUE)
            appliedGST = gstItem.ABOVEPER;
        }
        return {
          ...item,
          GSTApplied: appliedGST,
          SMCODE: smCodeData[0]?.CODE ?? "",
        };
      });
      newItems.forEach((newItem) => {
        if (newItem.stock > newItem.Qty) return;
        updatedData.push(newItem);
      });
      return updatedData;
    });
  };

  const handleReset = () => {
    window.location.reload();
    userAPI();
  };

  const handleRestore = () => {
    smCodeRef.current?.focus();
    invNoAPI();
    commissionAPI();
    setCardAcc("");
    setUpiAcc("");
    setOnlineAcc("");
    setGstData([]);
    setTotalGstData([]);
    setBarCodeData([]);
    setDesignData([]);
    setTotalGstAmt(0);
    setTotalCGSTAmt(0);
    setTotalSGSTAmt(0);
    setTotalDiscount(0);
    setTotalAmount(0);
    steTotalDisAmt(0);
    setTotalGrossAmount(0);
    setTotalNetAmount(0);
    setDisPer(0);
    setTotalDiscountAmt(0);
    setTotalAdjust(0);
    setTotalQty(0);
    setSmCode("");
    setSmCodeData([]);
    setCashValue("CASH");
    setCashAmt(0);
    setCardAmt(0);
    setUpiAmt(0);
    setOnlineAmt(0);
    setCreditAmt(0);
    setUserArea();
    setUserName();
    setSingleImage();
    setGstNo("");
    setUserPhone();
    setConditions("");
    setIns("");
    setIns1("");
    setIns2("");
    setIns3("");
    setIns4("");
    setIns5("");
    setIns6("");
    setAddress("");
    setAddress1("");
    setCity("");
    setTagNo();
    setEstNo();
    setExchangeNo();
    setSelectJewelType("RETAIL");
    setBillNo();
    setUserDetails({});
    setPhoneNo();
    setSearchText("");
    setItemDetailsData({});
    setItemDEsignCode();
    setPrvBalance(0);
    setSearchHoldText();
    holdMastNoAPI();
    setExchangeData([]);
    setTotalExchangeGstData([]);
    setTotalExchangeGstAmt(0);
    setTotalExchangeCGSTAmt(0);
    setTotalExchangeSGSTAmt(0);
    setTotalExchangeDiscount(0);
    setTotalExchangeAmount(0);
    steTotalExchangeDisAmt(0);
    setTotalExchangeGrossAmount(0);
    setTotalExchangeNetAmount(0);
    setTotalExchangeQty(0);
    setTotalBillGstData([]);
    setTotalBillGstAmt(0);
    setTotalBillCGSTAmt(0);
    setTotalBillSGSTAmt(0);
    setTotalBillDiscount(0);
    setTotalBillAmount(0);
    steTotalBillDisAmt(0);
    setTotalBillGrossAmount(0);
    setTotalBillQty(0);
    setTotalBillNetAmount(0);
    setExchangeTaansData([]);
    setTaansData([]);
    setFilteredHoldData([]);
    setTotalBasicAmount(0);
    setTotalExchangeBasicAmount(0);
    setQtyData([]);
    setExchangeQtyData([]);
    setOfferAData([]);
    setOfferARate(0);
    setOfferBData([]);
    setOfferBRate(0);
    userAPI();
  };

  const handleQrcodeOpen = () => setQrCodeOpen(true);
  const handleQrcodeCancel = () => {
    setQrCodeOpen(false);
    smCodeRef.current?.focus();
  };

  /* ============================= SAVE / PRINT / PDF (wired to existing API helpers) ============================= */

  const runSaveFlow = async () => {
    if (barCodeData.length === 0 && exchangeData.length === 0) {
      messageApi.open({
        type: "error",
        content: (
          <span style={{ fontSize: "18px", fontWeight: 600 }}>
            Please add bill or exchange items.
          </span>
        ),
      });
      return null;
    }
    if (cashValue === "CREDIT" && Object.keys(userDetails || {}).length === 0) {
      messageApi.open({
        type: "error",
        content: (
          <span style={{ fontSize: "18px", fontWeight: 600 }}>
            Please enter customer details for CREDIT bill.
          </span>
        ),
      });
      return null;
    }
    if (billNo) {
      // await createStockAddAPI();
      // await createExchangeStockAddAPI();
      // await deleteBarcodeMastAPI();
      // await deleteBarcodeDetAPI();
      // await deleteBarcodeDataAPI();
      // await deleteExchangeDetAPI();
      // await deleteBarcodePayModeAPI();
      // await deleteBarcodeOnlinePayAPI();
      // await deleteBarcodeLedgerAPI();
      // await createBarcodeData();
      // await createBarcodeDet();
      // if (exchangeData.length > 0) {
      //   await createBarcodeEXBDet();
      // }
      // await createStockUpdateAPI();
      // await createExchangeStockUpdateAPI();
      // await paymentModeAPI();
      // if (creditAmt > 0) await createLedgerBalanceAPI();
      // if (
      //   (barCodeData.length > 0 && cashValue === "CARD") ||
      //   cashValue === "UPI" ||
      //   cashValue === "ONLINE"
      // ) {
      //   await onlinePayModeAPI();
      // }
      // await createBarcodeMast();
      // return billNo;
      createExchangeBillUpdateAPI();
    } else {
      // const nextInvNo = nextInvNoOverride ?? (await invNoAPI());
      // if (exchangeData.length > 0) await createExchangeStockAddAPI();
      // await createStockUpdateAPI();
      // await createBarcodeData(nextInvNo);
      // await createBarcodeDet(nextInvNo);
      // if (exchangeData.length > 0) {
      //   await createBarcodeEXBDet(nextInvNo);
      // }
      // await paymentModeAPI(nextInvNo);
      // if (creditAmt > 0) await createLedgerBalanceAPI(nextInvNo);
      // if (
      //   (barCodeData.length > 0 && cashValue === "CARD") ||
      //   cashValue === "UPI" ||
      //   cashValue === "ONLINE"
      // ) {
      //   await onlinePayModeAPI(nextInvNo);
      // }
      // await createBarcodeMast(nextInvNo);
      // return nextInvNo;
      createExchangeBillSaveAPI();
    }
  };

  const handleOpenPaymentModes = () => {
    smCodeRef.current?.focus();
    if (barCodeData.length === 0 && exchangeData.length === 0) {
      messageApi.open({
        type: "error",
        content: (
          <span style={{ fontSize: "18px", fontWeight: 600 }}>
            Please add bill or exchange items before selecting payment mode.
          </span>
        ),
      });
      return false;
    }
    setPayMode(true);
    return true;
  };

  const handleSaveClick = async () => {
    if (saving) return;
    smCodeRef.current?.focus();
    try {
      setSaving(true);
      await runSaveFlow();
    } catch (error) {
      console.error("Error creating exchange bill:", error);
      messageApi.open({
        type: "error",
        content: (
          <span style={{ fontSize: "18px", fontWeight: 600 }}>
            Something went wrong while creating the bill.
          </span>
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePrintClick = async () => {
    if (printSaving) return;
    smCodeRef.current?.focus();
    try {
      setPrintSaving(true);
      invNoAPI();
      // handleBillPrint();
      handlePrint();
      await runSaveFlow();
    } catch (error) {
      console.error("Error creating exchange bill:", error);
      messageApi.open({
        type: "error",
        content: (
          <span style={{ fontSize: "18px", fontWeight: 600 }}>
            Something went wrong while creating the bill.
          </span>
        ),
      });
    } finally {
      setPrintSaving(false);
    }
  };

  // Refs to always access latest state and handlers in global keydown listener
  const handleOpenPaymentModesRef = useRef(handleOpenPaymentModes);
  handleOpenPaymentModesRef.current = handleOpenPaymentModes;

  const handleSaveClickRef = useRef(handleSaveClick);
  handleSaveClickRef.current = handleSaveClick;

  const handlePrintClickRef = useRef(handlePrintClick);
  handlePrintClickRef.current = handlePrintClick;

  const openNewCustomerRef = useRef(openNewCustomer);
  openNewCustomerRef.current = openNewCustomer;

  const setCustomerSelectOpenRef = useRef(setCustomerSelectOpen);
  setCustomerSelectOpenRef.current = setCustomerSelectOpen;

  const modalsStateRef = useRef({ open, customerSelectOpen, payMode });
  modalsStateRef.current = { open, customerSelectOpen, payMode };

  useEffect(() => {
    const handleGlobalShortcuts = (e) => {
      if (e.key === "F9" && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        if (modalsStateRef.current.open || modalsStateRef.current.customerSelectOpen) {
          return;
        }
        handleOpenPaymentModesRef.current?.();
        return;
      }

      if (e.key === "F10" && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        if (modalsStateRef.current.open || modalsStateRef.current.customerSelectOpen) {
          return;
        }
        if (modalsStateRef.current.payMode) {
          setPayMode(false);
        }
        handlePrintClickRef.current?.();
        return;
      }

      const isAltOnly = e.altKey && !e.ctrlKey && !e.metaKey;
      if (!isAltOnly) return;

      const keyLower = (e.key || "").toLowerCase();

      if (keyLower === "s" || e.code === "KeyS") {
        e.preventDefault();
        e.stopPropagation();
        if (modalsStateRef.current.open || modalsStateRef.current.customerSelectOpen) {
          return;
        }
        if (modalsStateRef.current.payMode) {
          setPayMode(false);
        }
        handleSaveClickRef.current?.();
        return;
      }

      if (keyLower === "b" || e.code === "KeyB") {
        e.preventDefault();
        e.stopPropagation();
        if (modalsStateRef.current.open) {
          setOpen(false);
        }
        if (modalsStateRef.current.payMode) {
          setPayMode(false);
        }
        setCustomerSelectOpenRef.current?.(true);
        return;
      }

      if (keyLower === "c" || e.code === "KeyC") {
        e.preventDefault();
        e.stopPropagation();
        if (modalsStateRef.current.customerSelectOpen) {
          setCustomerSelectOpenRef.current?.(false);
        }
        if (modalsStateRef.current.payMode) {
          setPayMode(false);
        }
        openNewCustomerRef.current?.();
        return;
      }
    };

    window.addEventListener("keydown", handleGlobalShortcuts);
    return () => {
      window.removeEventListener("keydown", handleGlobalShortcuts);
    };
  }, []);

  const handlePdfClick = async () => {
    smCodeRef.current?.focus();
    if (barCodeData.length === 0 && exchangeData.length === 0) {
      messageApi.open({
        type: "error",
        content: (
          <span style={{ fontSize: "18px", fontWeight: 600 }}>
            Please add bill or exchange items.
          </span>
        ),
      });
      return;
    }
    const nextInvNo = billNo ? billNo : await invNoAPI();
    handleInvoicePdfDownload(nextInvNo);
  };

  const addressProf =
    Object.keys(userDetails || {}).length > 0
      ? [
        userDetails?.add1,
        userDetails?.add2,
        userDetails?.add3,
        userDetails?.add4,
      ]
        .filter(Boolean)
        .join(", ")
      : "";

  const custInitial = (userDetails?.Custname || "?")
    .trim()
    .charAt(0)
    .toUpperCase();
  const paymentStatusLabel =
    totalNetAmount !== 0 &&
      (cashAmt > 0 || cardAmt > 0 || upiAmt > 0 || onlineAmt > 0 || creditAmt > 0)
      ? "IN PROGRESS"
      : "NOT STARTED";
  const isTallied =
    totalNetAmount !== 0 && paymentStatusLabel === "IN PROGRESS";

  const styles = {
    row: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    label: { fontSize: 12, color: "#64748b" },
    value: { fontSize: 13, fontWeight: 500, color: "#1e293b" },
  };

  return (
    <Spin spinning={loading} tip="Loading...">
      {contextHolder}
      <div ref={qrRef} style={{ display: "none" }}>
        <QRCodeCanvas value={upiLink} size={200} includeMargin={true} />
      </div>
      <div
        style={{
          padding: "5px",
          display: "flex",
          flexDirection: "column",
          height: "88vh",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", flex: 1, gap: 10, minHeight: 0 }}>
          {/* ================= LEFT COLUMN ================= */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflowX: "hidden",
              overflowY: "auto",
            }}
          >
            {/* ===== Header card ===== */}
            <Row
              gutter={[16, 16]}
              align="middle"
              wrap={false}
              style={{ flexWrap: "nowrap" }}
            >
              <Card
                bordered
                className="custometagnocard tax-card-in"
                style={{
                  position: "relative",
                  background:
                    "linear-gradient(145deg,#ffffff 0%,#f7f9fc 60%,#eef1f6 100%)",
                  borderRadius: "12px",
                  width: "100%",
                  padding: "0",
                  boxShadow: "0 3px 16px rgba(0,0,0,0.08)",
                  border: "1px solid #e2e6ec",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                    gap: 0,
                    width: "100%",
                    padding: "8px 10px 6px",
                  }}
                >
                  {/* COL 1 — Inv No + Customer Type */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      flex: "0 0 250px",
                      justifyContent: "center",
                      background:
                        "linear-gradient(160deg, #f3e7ec 0%, #f5c0d2 55%, #f8a5c5 100%)",
                      borderRadius: "12px",
                      padding: "8px",
                      border: "1px solid rgba(241, 99, 142, 0.3)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="tax-inv-badge"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg,rgba(255, 239, 248, 0.97) 0%,rgba(254, 219, 236, 0.97) 100%)",
                        padding: "8px 12px",
                        color: "#8a1e50",
                        border: "1px solid rgba(59,130,246,0.25)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          lineHeight: 1.2,
                        }}
                      >
                        <h2
                          style={{
                            fontSize: "0.75rem",
                            margin: 0,
                            whiteSpace: "nowrap",
                            fontWeight: 600,
                            letterSpacing: "0.6px",
                            textTransform: "uppercase",
                            opacity: 0.72,
                          }}
                        >
                          Inv No
                        </h2>
                        <span
                          style={{
                            fontSize: 22,
                            fontWeight: "bold",
                            letterSpacing: "-0.5px",
                          }}
                        >
                          {billNo ? billNo : invNo}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#0f766e",
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                          }}
                        >
                          <CalendarOutlined style={{ fontSize: 10 }} />{" "}
                          {dayjs().format("DD-MMM-YYYY")}
                        </span>
                      </div>
                      <Button
                        icon={<FolderAddOutlined />}
                        shape="circle"
                        style={{
                          fontSize: 18,
                          background: "rgba(246, 59, 146, 0.12)",
                          border: "1px solid rgba(246, 59, 146, 0.28)",
                          color: "#d81d7a",
                        }}
                        onClick={() => {
                          setVisibleHis(true);
                          smCodeRef.current?.focus();
                        }}
                      />
                      <Button
                        icon={
                          <ReloadOutlined
                            style={{
                              color: "#d97706",
                              fontSize: "15px",
                              transition: "transform 1s ease-in-out",
                              transform: rotating
                                ? "rotate(360deg)"
                                : "rotate(0deg)",
                            }}
                          />
                        }
                        shape="circle"
                        onClick={handleReset}
                        style={{
                          flexShrink: 0,
                          background: "rgba(251,191,36,0.14)",
                          border: "1px solid rgba(251,191,36,0.28)",
                        }}
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.70rem",
                          fontWeight: 600,
                          color: "black",
                        }}
                      >
                        ▶ Customer Type
                      </span>
                      <Select
                        ref={jewelRef}
                        value={
                          selectJewelType
                            ? selectJewelType
                            : custTypeData[0]?.CUSTTYPE || null
                        }
                        onChange={(value) => {
                          setSelectJewelType(value);
                          setTimeout(() => tagNoRef?.current?.focus(), 0);
                        }}
                        style={{ width: "100%" }}
                      >
                        {custTypeData.map((jewel, index) => (
                          <Option key={index} value={jewel.CUSTTYPE}>
                            {jewel.CUSTTYPE}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div
                    style={{
                      width: "1px",
                      background:
                        "linear-gradient(to bottom,transparent,#cbd5e1 25%,#cbd5e1 75%,transparent)",
                      margin: "0 12px",
                      flexShrink: 0,
                    }}
                  />

                  {/* COL 2 — Est No / SM Code / Design Code */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      flex: "0 0 24%",
                      justifyContent: "center",
                      background:
                        "linear-gradient(160deg,#fdfeff 0%,#f5f8fc 100%)",
                      borderRadius: "12px",
                      padding: "8px 10px",
                      border: "1px solid rgba(148,163,184,0.16)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        paddingLeft: 6,
                      }}
                    >
                      <span
                        style={{
                          width: 100,
                          fontSize: "0.70rem",
                          fontWeight: 600,
                          color: "#7c3aed",
                          flexShrink: 0,
                        }}
                      >
                        ▶ Est No
                      </span>
                      <Input
                        ref={estNoRef}
                        value={estNo}
                        onChange={(e) => setEstNo(e.target.value)}
                        onKeyDown={handleEstNoKeyDown}
                        style={{
                          flex: 1,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      />
                      <Button
                        type="primary"
                        ref={fetchRef}
                        onClick={() => smCodeRef?.current?.focus()}
                        style={{
                          background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
                          border: "none",
                          borderRadius: "6px",
                          fontWeight: 600,
                          fontSize: "12px",
                          padding: "0 10px",
                        }}
                      >
                        Go
                      </Button>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        paddingLeft: 6,
                      }}
                    >
                      <span
                        style={{
                          width: 100,
                          fontSize: "0.70rem",
                          fontWeight: 600,
                          color: "#4b5563",
                          flexShrink: 0,
                        }}
                      >
                        ▶ SM Code
                      </span>
                      <Input
                        ref={smCodeRef}
                        autoFocus
                        value={smCode}
                        onChange={(e) => setSmCode(e.target.value)}
                        onKeyDown={handleSmCodeKeyDown}
                        style={{
                          flex: 1,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      />
                      <Button
                        type="primary"
                        ref={smSubmitRef}
                        onClick={() => {
                          tagNoRef?.current?.focus();
                          if (smCode) smCodeAPI();
                        }}
                        style={{
                          background: "linear-gradient(135deg,#16a34a,#15803d)",
                          border: "none",
                          borderRadius: "6px",
                          fontWeight: 600,
                          fontSize: "12px",
                          padding: "0 10px",
                        }}
                      >
                        Go
                      </Button>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        paddingLeft: 6,
                      }}
                    >
                      <span
                        style={{
                          width: 100,
                          fontSize: "0.70rem",
                          fontWeight: 600,
                          color: "#A72703",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <TagOutlined style={{ fontSize: 11 }} /> Design Code
                      </span>
                      <Input
                        ref={tagNoRef}
                        onKeyDown={handleTagNoKeyDown}
                        value={tagNo}
                        onChange={(e) => setTagNo(e.target.value)}
                        style={{
                          flex: 1,
                          fontWeight: "bold",
                          textAlign: "center",
                          background: "linear-gradient(135deg,#fff5f5,#fdf0ef)",
                          border: "1.5px solid #e5a09e",
                          color: "#c0392b",
                          fontSize: "14px",
                          borderRadius: "6px",
                        }}
                      />
                      <Button
                        type="primary"
                        ref={submitRef}
                        onClick={async () => {
                          if (
                            (tagNo === "0" || !tagNo) &&
                            barCodeData?.length > 0
                          ) {
                            mobileRef.current?.focus();
                            setTagNo("");
                          } else {
                            await billDataAPI();
                            await homeBillData();
                          }
                        }}
                        style={{
                          background: "linear-gradient(135deg,#c0392b,#a93226)",
                          border: "none",
                          borderRadius: "6px",
                          fontWeight: 600,
                          fontSize: "12px",
                          padding: "0 10px",
                        }}
                      >
                        Go
                      </Button>
                    </div>
                  </div>

                  <div
                    style={{
                      width: "1px",
                      background:
                        "linear-gradient(to bottom,transparent,#cbd5e1 25%,#cbd5e1 75%,transparent)",
                      margin: "0 12px",
                      flexShrink: 0,
                    }}
                  />

                  {/* COL 3 — Mobile No + item mini info */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      width: "22%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        paddingLeft: 6,
                      }}
                    >
                      <span
                        style={{
                          width: 72,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: "#4b5563",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <PhoneOutlined style={{ fontSize: 13 }} /> Mobile No
                      </span>
                      <Input
                        ref={mobileRef}
                        onKeyDown={handleMobileNoKeyDown}
                        value={phoneNo}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[a-zA-Z]/g, "");
                          if (value.length <= 10) setPhoneNo(value);
                        }}
                        style={{
                          flex: 1,
                          fontWeight: "bold",
                          textAlign: "center",
                          color: "#374151",
                          fontSize: "13px",
                          borderColor: "#d1d5db",
                        }}
                      />
                      <Button
                        type="primary"
                        ref={searchRef}
                        onClick={() => {
                          if (/^\d{10}$/.test(phoneNo)) userDetailsAPI();
                        }}
                        style={{
                          background: "linear-gradient(135deg,#475569,#334155)",
                          border: "none",
                          borderRadius: "6px",
                          fontWeight: 600,
                          fontSize: "12px",
                          padding: "0 10px",
                        }}
                      >
                        Go
                      </Button>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 7,
                        justifyContent: "center",
                        background:
                          "linear-gradient(160deg,#f8fafc 0%,#f0f3f8 100%)",
                        borderRadius: "12px",
                        padding: "8px 10px",
                        border: "1px solid rgba(148,163,184,0.16)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {[
                        {
                          label: "Bill Qty",
                          icon: <MoneyCollectOutlined />,
                          bg: "rgba(240,253,244,0.95)",
                          border: "rgba(134,239,172,0.50)",
                          iconBg: "rgba(220,252,231,1)",
                          iconColor: "#15803d",
                          value: totalBillQty || 0,
                        },
                        {
                          label: "Exch Qty",
                          icon: <TagOutlined />,
                          bg: "rgba(247,250,255,0.95)",
                          border: "rgba(191,219,254,0.55)",
                          iconBg: "rgba(219,234,254,1)",
                          iconColor: "#1d4ed8",
                          value: totalExchangeQty || 0,
                        },
                        {
                          label: "Purchase",
                          icon: <OrderedListOutlined />,
                          bg: "rgba(255,252,245,0.95)",
                          border: "rgba(253,230,138,0.60)",
                          iconBg: "rgba(254,243,199,1)",
                          iconColor: "#b45309",
                          value: barCodeData[0]?.COLOUR ?? "-",
                        },
                      ].map(
                        ({
                          label,
                          icon,
                          bg,
                          border,
                          iconBg,
                          iconColor,
                          value,
                        }) => (
                          <div
                            key={label}
                            style={{
                              height: "64px",
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                              gap: 4,
                              marginTop: 22,
                              padding: "6px 8px",
                              borderRadius: 10,
                              border: `1px solid ${border}`,
                              background: bg,
                              flexDirection: "column",
                              position: "relative",
                              flex: 1,
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "-18px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                background: iconBg,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: iconColor,
                                fontSize: 12,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                              }}
                            >
                              {icon}
                            </div>
                            <span
                              style={{
                                fontSize: "0.60rem",
                                color: "#6b7280",
                                flexShrink: 0,
                                textAlign: "center",
                                fontWeight: 500,
                                letterSpacing: "0.2px",
                              }}
                            >
                              {label}
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "rgba(0,0,0,0.72)",
                              }}
                            >
                              {value}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      width: "1px",
                      background:
                        "linear-gradient(to bottom,transparent,#cbd5e1 25%,#cbd5e1 75%,transparent)",
                      margin: "0 12px",
                      flexShrink: 0,
                    }}
                  />

                  {/* COL 4 — Customer card */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "stretch",
                    }}
                  >
                    {!userDetails?.Custname ? (
                      <div
                        className="tax-customer-empty"
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          padding: "10px 14px",
                          background:
                            "linear-gradient(135deg,#fafbff 0%,#f3f5fd 100%)",
                          borderRadius: "12px",
                          border: "1.5px dashed #bfdbfe",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg,#eff6ff,#dbeafe)",
                            border: "1.5px solid #bfdbfe",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <UserOutlined
                            style={{ fontSize: 16, color: "#60a5fa" }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: "0.66rem",
                            color: "rgb(140,143,148)",
                            textAlign: "center",
                            lineHeight: 1.5,
                            letterSpacing: "0.2px",
                          }}
                        >
                          No customer selected.
                        </span>
                        <div style={{ display: "flex", gap: 6, width: "100%" }}>
                          <button
                            className="sb-btn sb-btn-sm sb-browse"
                            onClick={() => {
                              setCustomerSelectOpen(true);
                            }}
                            title="Browse Customers (Alt+B)"
                          >
                            <span className="sb-badge sb-badge-sm">
                              <SearchOutlined style={{ fontSize: 11 }} />
                            </span>
                            Browse (Alt+B)
                          </button>
                          <button
                            className="sb-btn sb-btn-sm sb-create"
                            onClick={() => {
                              openNewCustomer();
                              smCodeRef.current?.focus();
                            }}
                            title="Create Customer (Alt+C)"
                          >
                            <span className="sb-badge sb-badge-sm">
                              <PlusOutlined style={{ fontSize: 11 }} />
                            </span>
                            Create (Alt+C)
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="tax-customer-card"
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          background: "#ffffff",
                          borderRadius: "12px",
                          border: "1px solid #e2e8f0",
                          overflow: "hidden",
                          boxShadow:
                            "0 1px 3px rgba(0,0,0,0.06), 0 3px 10px rgba(15,23,42,0.05)",
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            background:
                              "linear-gradient(120deg,#1e293b 0%,#334155 100%)",
                            padding: "7px 10px",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: "9px",
                              background: "rgba(255,255,255,0.14)",
                              border: "1px solid rgba(255,255,255,0.20)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 13,
                              flexShrink: 0,
                            }}
                          >
                            {custInitial}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Tooltip title={userDetails?.Custname}>
                              <div
                                style={{
                                  fontSize: "0.79rem",
                                  fontWeight: 700,
                                  color: "#fff",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {userDetails?.Custname?.length > 20
                                  ? `${userDetails.Custname.slice(0, 20)}...`
                                  : userDetails?.Custname}
                              </div>
                            </Tooltip>
                            <div
                              style={{
                                fontSize: "0.61rem",
                                color: "rgba(255,255,255,0.60)",
                                marginTop: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              {userDetails?.Area && (
                                <span>{userDetails.Area}</span>
                              )}
                              {userDetails?.Area && userDetails?.Mobileno && (
                                <span style={{ opacity: 0.5 }}>·</span>
                              )}
                              {userDetails?.Mobileno && (
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "rgba(255,255,255,0.85)",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {userDetails.Mobileno}
                                </span>
                              )}
                            </div>
                          </div>
                          <div
                            style={{ display: "flex", gap: 4, flexShrink: 0 }}
                          >
                            <Tooltip title="Browse Customers (Alt+B)">
                              <button
                                onClick={() => {
                                  setCustomerSelectOpen(true);
                                }}
                                style={{
                                  width: 23,
                                  height: 23,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "rgba(148,163,184,0.20)",
                                  border: "1px solid rgba(203,213,225,0.36)",
                                  borderRadius: 5,
                                  color: "#e2e8f0",
                                  cursor: "pointer",
                                  fontSize: 11,
                                }}
                              >
                                <SearchOutlined />
                              </button>
                            </Tooltip>
                            <Tooltip title="Edit Customer">
                              <button
                                onClick={handleEditCustomer}
                                style={{
                                  width: 23,
                                  height: 23,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "rgba(251,191,36,0.16)",
                                  border: "1px solid rgba(253,230,138,0.32)",
                                  borderRadius: 5,
                                  color: "#d97706",
                                  cursor: "pointer",
                                  fontSize: 10,
                                }}
                              >
                                <EditOutlined />
                              </button>
                            </Tooltip>
                            <Tooltip title="QR Code">
                              <button
                                onClick={handleQrcodeOpen}
                                style={{
                                  width: 23,
                                  height: 23,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "rgba(59,130,246,0.16)",
                                  border: "1px solid rgba(147,197,253,0.32)",
                                  borderRadius: 5,
                                  color: "#93c5fd",
                                  cursor: "pointer",
                                  fontSize: 11,
                                }}
                              >
                                <QrcodeOutlined />
                              </button>
                            </Tooltip>
                            <Tooltip title="New Customer (Alt+C)">
                              <button
                                onClick={() => {
                                  openNewCustomer();
                                  smCodeRef.current?.focus();
                                }}
                                style={{
                                  width: 23,
                                  height: 23,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "rgba(34,197,94,0.16)",
                                  border: "1px solid rgba(134,239,172,0.32)",
                                  borderRadius: 5,
                                  color: "#86efac",
                                  cursor: "pointer",
                                  fontSize: 10,
                                }}
                              >
                                <UserOutlined />
                              </button>
                            </Tooltip>
                          </div>
                        </div>

                        <div
                          style={{
                            padding: "6px 10px 7px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          {addressProf && (
                            <Tooltip title={addressProf}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  overflow: "hidden",
                                }}
                              >
                                <EnvironmentOutlined
                                  style={{
                                    fontSize: 9,
                                    color: "#94a3b8",
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "0.65rem",
                                    color: "#6b7280",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {addressProf}
                                </span>
                              </div>
                            </Tooltip>
                          )}
                          <div style={{ display: "flex", gap: 4 }}>
                            {[
                              {
                                label: "GST",
                                value: userDetails?.TINNO,
                                color: "#b45309",
                                bg: "#fffbeb",
                                border: "#fef3c7",
                              },
                              {
                                label: "STATE",
                                value: userDetails?.STATE,
                                color: "#1d4ed8",
                                bg: "#eff6ff",
                                border: "#dbeafe",
                              },
                              {
                                label: "STATION",
                                value: userDetails?.STATION,
                                color: "#475569",
                                bg: "#f1f5f9",
                                border: "#e2e8f0",
                              },
                            ].map((item) => (
                              <div
                                key={item.label}
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  display: "flex",
                                  flexDirection: "column",
                                  padding: "2px 5px",
                                  background: item.value ? item.bg : "#fafafa",
                                  border: `1px solid ${item.value ? item.border : "#f1f1f1"}`,
                                  borderRadius: "5px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.52rem",
                                    fontWeight: 700,
                                    color: "#9ca3af",
                                    letterSpacing: "0.2px",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {item.label}
                                </span>
                                <span
                                  style={{
                                    fontSize: "0.62rem",
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    color: item.value ? item.color : "#cbd5e1",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.value || "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </Row>

            {/* ===== Bill items table ===== */}
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div
                  className="tax-card-in"
                  style={{
                    marginTop: 3,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 12px",
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#15803d",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "0.68rem",
                        fontWeight: 800,
                        color: "#15803d",
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                      }}
                    >
                      Bill Items
                    </span>
                  </div>
                  <div
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "0 0 10px 10px",
                      overflow: "hidden",
                    }}
                  >
                    <DashboardTableHeaderStyles saleCode={1}>
                      <Table
                        columns={columns}
                        dataSource={barCodeData}
                        pagination={false}
                        size="small"
                        rowKey={(_, index) => index}
                        scroll={{ y: 150 }}
                        summary={(pageData) => {
                          const footerQty = pageData.reduce(
                            (acc, item) => acc + (Number(item?.stock) || 0),
                            0,
                          );
                          const footerAmount = pageData.reduce(
                            (acc, item) =>
                              acc +
                              (Number(item?.stock) || 0) *
                              (Number(item?.offer) || 0),
                            0,
                          );
                          return (
                            <Table.Summary fixed>
                              <Table.Summary.Row
                                style={{ background: "#f8fafc" }}
                              >
                                <Table.Summary.Cell index={0} colSpan={5}>
                                  <div
                                    style={{
                                      textAlign: "right",
                                      fontWeight: 800,
                                      fontSize: "12.5px",
                                      color: "#1e293b",
                                    }}
                                  >
                                    Total
                                  </div>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5} align="center">
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "3px 10px",
                                      borderRadius: 7,
                                      background:
                                        "linear-gradient(135deg, #DBEAFE, #BFDBFE)",
                                      border: "1.5px solid #60A5FA",
                                      color: "#1D4ED8",
                                      fontWeight: 800,
                                      fontSize: "13px",
                                    }}
                                  >
                                    {footerQty.toFixed(2)}
                                  </span>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6} colSpan={4} />
                                <Table.Summary.Cell index={10} align="right">
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "5px 12px",
                                      borderRadius: 8,
                                      background:
                                        "linear-gradient(135deg, #BBF7D0, #86EFAC)",
                                      border: "1.5px solid #22C55E",
                                      color: "#15803D",
                                      fontWeight: 900,
                                      fontSize: "13.5px",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {footerAmount.toFixed(2)}
                                  </span>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            </Table.Summary>
                          );
                        }}
                      />
                    </DashboardTableHeaderStyles>
                  </div>
                </div>
              </Col>
            </Row>

            {/* ===== Exchange items table ===== */}
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div
                  className="tax-card-in"
                  style={{
                    marginTop: 6,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 12px",
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#7c3aed",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "0.68rem",
                        fontWeight: 800,
                        color: "#7c3aed",
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                      }}
                    >
                      Exchange Items
                    </span>
                  </div>
                  {/* Exchange Code scan field — lives right under the Exchange Items
                      heading (and above the exchange table) so it's obvious which
                      table it feeds, instead of sitting up in the header card. */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 12px",
                      background: "#faf8ff",
                      borderBottom: "1px solid #ede9fe",
                    }}
                  >
                    <span
                      style={{
                        width: 120,
                        fontSize: "0.70rem",
                        fontWeight: 600,
                        color: "#7c3aed",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <SwapOutlined style={{ fontSize: 11 }} /> Exchange Code
                    </span>
                    <Input
                      ref={exchangeNoRef}
                      onKeyDown={handleExchangeNoKeyDown}
                      value={exchangeNo}
                      onChange={(e) => setExchangeNo(e.target.value)}
                      style={{
                        maxWidth: 220,
                        fontWeight: "bold",
                        textAlign: "center",
                        background: "linear-gradient(135deg,#f5f3ff,#ede9fe)",
                        border: "1.5px solid #c4b5fd",
                        color: "#6d28d9",
                        fontSize: "14px",
                        borderRadius: "6px",
                      }}
                    />
                    <Button
                      type="primary"
                      ref={exchangeSubmitRef}
                      onClick={async () => {
                        if (!exchangeNo) {
                          message.open({
                            type: "error",
                            content: (
                              <div style={{ fontSize: "18px" }}>
                                Enter Exchange Design Code
                              </div>
                            ),
                          });
                          return;
                        }
                        await exchangeDataAPI();
                        await homeExchangeData();
                      }}
                      style={{
                        background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
                        border: "none",
                        borderRadius: "6px",
                        fontWeight: 600,
                        fontSize: "12px",
                        padding: "0 10px",
                      }}
                    >
                      Go
                    </Button>
                  </div>
                  <div
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "0 0 10px 10px",
                      overflow: "hidden",
                    }}
                  >
                    <DashboardTableHeaderStyles saleCode={1}>
                      <Table
                        columns={columns1}
                        dataSource={exchangeData}
                        pagination={false}
                        size="small"
                        rowKey={(_, index) => "ex-" + index}
                        scroll={{ y: 130 }}
                      />
                    </DashboardTableHeaderStyles>
                  </div>
                </div>
              </Col>
            </Row>

            {/* ===== Footer ===== */}
            <Row
              gutter={[8, 0]}
              style={{
                flexShrink: 0,
                width: "100%",
                background: "linear-gradient(180deg,#f8fafc 0%,#ffffff 100%)",
                padding: "6px 8px",
                borderTop: "1px solid #dde3ec",
                boxShadow: "0 -3px 20px rgba(0,0,0,0.09)",
                borderRadius: "10px 10px 0 0",
                alignItems: "stretch",
                marginTop: "auto",
              }}
            >
              {/* Payment Mode */}
              <Col span={7} style={{ paddingRight: "4px", display: "flex" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "70px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      type="primary"
                      style={{
                        height: "140px",
                        width: "100%",
                        fontWeight: "600",
                        whiteSpace: "normal",
                        background:
                          "linear-gradient(160deg, #a5b4fc 0%, #4f46e5 65%, #4338ca 100%)",
                        border: "none",
                        color: "#ffffff",
                        flexDirection: "column",
                        borderRadius: "12px",
                        fontSize: "13px",
                        boxShadow: "0 4px 14px rgba(79,70,229,0.40)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={handleOpenPaymentModes}
                      title="Payment Mode (F9)"
                    >
                      <span
                        style={{
                          fontSize: "20px",
                          color: "#4338ca",
                          borderRadius: "50%",
                          backgroundColor: "rgba(255,255,255,0.92)",
                          padding: "10px",
                          marginBottom: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          boxShadow: "0 2px 8px rgba(67,56,202,0.35)",
                        }}
                      >
                        <CreditCardOutlined />
                      </span>
                      Payment Mode (F9)
                    </Button>
                  </div>

                  <div
                    style={{
                      flex: 1,
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "0.8rem",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            background:
                              "linear-gradient(90deg, rgb(158 170 189), rgb(177 191 215))",
                            color: "rgb(27 27 27)",
                          }}
                        >
                          <th
                            style={{
                              textAlign: "left",
                              padding: "8px 10px",
                              fontSize: "0.7rem",
                              borderBottom: "1px solid #ddd",
                            }}
                          >
                            Pay Mode
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              padding: "8px 10px",
                              fontSize: "0.7rem",
                              borderBottom: "1px solid #ddd",
                            }}
                          >
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td
                            style={{
                              padding: "6px 10px",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            Cash
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              borderBottom: "1px solid #eee",
                              textAlign: "right",
                            }}
                          >
                            {Math.round(cashAmt || 0)}
                          </td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              padding: "6px 10px",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            UPI / Online
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              borderBottom: "1px solid #eee",
                              textAlign: "right",
                            }}
                          >
                            {Number(upiAmt) > 0
                              ? Math.round(upiAmt || 0)
                              : Math.round(onlineAmt || 0)}
                          </td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              padding: "6px 10px",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            Card
                          </td>
                          <td
                            style={{
                              padding: "6px 10px",
                              borderBottom: "1px solid #eee",
                              textAlign: "right",
                            }}
                          >
                            {Math.round(cardAmt || 0)}
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            style={{
                              textAlign: "right",
                              padding: "6px 10px",
                              borderTop: "2px solid #cbd5e1",
                              backgroundColor: "#f8fafc",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              color:
                                Number(creditAmt) > 0 ? "#b91c1c" : "#15803d",
                              textTransform: "uppercase",
                            }}
                          >
                            Credit Due
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              padding: "6px 10px",
                              borderTop: "2px solid #cbd5e1",
                              backgroundColor: "#f8fafc",
                              fontSize: "0.75rem",
                              fontWeight: 800,
                              fontFamily: "monospace",
                              color:
                                Number(creditAmt) > 0 ? "#b91c1c" : "#15803d",
                            }}
                          >
                            {Number(creditAmt || 0).toFixed(0)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </Col>

              {/* Ledger Summary */}
              <Col span={5}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg,#fb923c 0%,#ea580c 100%)",
                      borderRadius: "8px",
                      padding: "7px 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      color: "#fff",
                      boxShadow: "0 2px 8px rgba(234,88,12,0.35)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      🛒 Total Quantity
                    </span>
                    <span style={{ fontSize: "18px", fontWeight: 800 }}>
                      {totalBillQty + totalExchangeQty}
                    </span>
                  </div>

                  <div
                    style={{
                      background:
                        "linear-gradient(150deg, rgba(241,245,255,0.95) 0%, rgba(236,242,254,0.97) 100%)",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      color: "#374151",
                      border: "1px solid rgba(148,163,184,0.45)",
                      boxShadow: "0 1px 6px rgba(71,85,105,0.08)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        color: "#475569",
                        borderLeft: "2px solid rgba(100,116,139,0.50)",
                        paddingLeft: "6px",
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <BankOutlined /> Ledger Summary
                    </div>
                    {[
                      {
                        label: "Prv. Balance",
                        value: Number(prvBalance)?.toFixed(0),
                      },
                      {
                        label: "Ledger Balance",
                        value: Number(creditAmt)?.toFixed(0),
                      },
                    ].map((item, i) => (
                      <div
                        key={i}
                        style={{
                          ...styles.row,
                          borderBottom: "0.5px solid #e2e8f0",
                          paddingBottom: 5,
                          paddingTop: 3,
                        }}
                      >
                        <span style={styles.label}>{item.label}</span>
                        <span style={styles.value}>{item.value}</span>
                      </div>
                    ))}
                    <div style={{ ...styles.row, paddingTop: 4 }}>
                      <span
                        style={{
                          ...styles.label,
                          fontWeight: 500,
                          color: "#1e293b",
                        }}
                      >
                        Total Balance
                      </span>
                      <span
                        style={{
                          fontSize: 15,
                          fontWeight: 500,
                          color: "#185FA5",
                          background: "#e6f1fb",
                          padding: "4px 12px",
                          borderRadius: 8,
                        }}
                      >
                        {Number(totalBalance)?.toFixed(0)}
                      </span>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Payment Status */}
              <Col span={3}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "10px 14px",
                    height: "100%",
                    borderRadius: "10px",
                    border: isTallied
                      ? "1.5px solid rgba(134,239,172,0.70)"
                      : "1.5px solid rgba(252, 229, 165, 0.7)",
                    background: isTallied
                      ? "linear-gradient(150deg,rgba(240,253,244,0.95) 0%,rgba(220,252,231,0.90) 100%)"
                      : "linear-gradient(150deg,rgba(255,251,235,0.95) 0%,rgba(254,243,199,0.90) 100%)",
                    boxShadow: isTallied
                      ? "0 3px 14px rgba(22,163,74,0.15)"
                      : "0 3px 14px rgba(245,158,11,0.15)",
                    gap: "6px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.58rem",
                      fontWeight: 700,
                      color: isTallied ? "#15803d" : "#b45309",
                      borderLeft: `2px solid ${isTallied ? "rgba(22,163,74,0.60)" : "rgba(245,158,11,0.60)"}`,
                      paddingLeft: "6px",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      alignSelf: "flex-start",
                      marginTop: "4px",
                    }}
                  >
                    Payment Status
                  </div>
                  <svg width="40" height="40" viewBox="0 0 42 42" fill="none">
                    <defs>
                      <linearGradient
                        id="eb-status-grad"
                        x1="0"
                        y1="0"
                        x2="1"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={isTallied ? "#4ade80" : "#F8CD71"}
                        />
                        <stop
                          offset="100%"
                          stopColor={isTallied ? "#15803d" : "#F8CD71"}
                        />
                      </linearGradient>
                    </defs>
                    <circle
                      cx="21"
                      cy="21"
                      r="20"
                      fill="none"
                      stroke={
                        isTallied
                          ? "rgba(34,197,94,0.22)"
                          : "rgba(239,191,68,0.22)"
                      }
                      strokeWidth="1.5"
                    />
                    <circle
                      cx="21"
                      cy="21"
                      r="16"
                      fill="url(#eb-status-grad)"
                    />
                    <ellipse
                      cx="17.5"
                      cy="15"
                      rx="7"
                      ry="3.8"
                      fill="rgba(255,255,255,0.28)"
                    />
                    {isTallied ? (
                      <path
                        d="M12.5 21.5L18.5 27.5L29.5 15"
                        stroke="white"
                        strokeWidth="2.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ) : (
                      <>
                        <circle
                          cx="21"
                          cy="21"
                          r="7"
                          fill="none"
                          stroke="white"
                          strokeWidth="2.2"
                        />
                        <path
                          d="M21 17V21L24 23"
                          stroke="white"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </>
                    )}
                  </svg>
                  <div
                    style={{
                      fontSize: "0.6rem",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: isTallied ? "#15803d" : "#b45309",
                      textAlign: "center",
                    }}
                  >
                    {paymentStatusLabel}
                  </div>
                </div>
              </Col>

              {/* Save / Print / PDF */}
              <Col
                span={4}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                <Button
                  disabled={saving}
                  loading={saving}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    background:
                      "linear-gradient(135deg,#22c55e 0%,#15803d 100%)",
                    border: "none",
                    color: "white",
                    height: "40px",
                    fontSize: "14px",
                    borderRadius: "10px",
                    fontWeight: 700,
                    boxShadow: "0 3px 12px rgba(21,128,61,0.42)",
                  }}
                  onClick={handleSaveClick}
                  title="Save Bill (Alt+S)"
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.25)",
                      fontSize: 12,
                    }}
                  >
                    <SaveOutlined />
                  </span>
                  {saving ? "Saving..." : billNo ? "UPDATE (Alt+S)" : "SAVE (Alt+S)"}
                </Button>
                <Button
                  disabled={printSaving}
                  loading={printSaving}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    background:
                      "linear-gradient(135deg,#fbbf24 0%,#d97706 100%)",
                    border: "none",
                    color: "white",
                    height: "40px",
                    fontSize: "14px",
                    borderRadius: "10px",
                    fontWeight: 700,
                    boxShadow: "0 3px 12px rgba(217,119,6,0.42)",
                  }}
                  onClick={handlePrintClick}
                  title="Print & Save Bill (F10)"
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.25)",
                      fontSize: 12,
                    }}
                  >
                    <PrinterOutlined />
                  </span>
                  {printSaving ? "Printing..." : "PRINT (F10)"}
                </Button>
                <Button
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    background:
                      "linear-gradient(135deg,#f87171 0%,#991b1b 100%)",
                    border: "none",
                    color: "white",
                    height: "40px",
                    fontSize: "14px",
                    borderRadius: "10px",
                    fontWeight: 700,
                    boxShadow: "0 3px 12px rgba(153,27,27,0.42)",
                  }}
                  onClick={handlePdfClick}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.25)",
                      fontSize: 12,
                    }}
                  >
                    <FilePdfOutlined />
                  </span>
                  PDF
                </Button>
              </Col>

              {/* Amount Details */}
              <Col span={5}>
                <div
                  style={{
                    background:
                      "linear-gradient(150deg, rgba(241, 245, 255, 0.95) 0%, rgb(210 225 255 / 97%) 100%)",
                    borderRadius: "12px",
                    fontWeight: "bold",
                    padding: "10px 14px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    border: "1px solid rgba(148,163,184,0.5)",
                    boxShadow: "0 4px 14px rgba(71,85,105,0.12)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "#475569",
                      borderLeft: "3px solid #475569",
                      paddingLeft: "8px",
                      letterSpacing: "0.6px",
                      textTransform: "uppercase",
                    }}
                  >
                    Amount Details
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <label
                      style={{ flex: 1, fontSize: "0.75rem", color: "#475569" }}
                    >
                      Bill Amount
                    </label>
                    <Input
                      disabled
                      value={Number(totalBillNetAmount || 0).toFixed(0)}
                      style={{
                        width: 130,
                        fontWeight: "bold",
                        textAlign: "right",
                        backgroundColor: "rgba(248,250,252,0.95)",
                        color: "#334155",
                        border: "1.5px solid rgba(148,163,184,0.6)",
                        borderRadius: "6px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <label
                      style={{ flex: 1, fontSize: "0.75rem", color: "#475569" }}
                    >
                      Exchange Amount
                    </label>
                    <Input
                      disabled
                      value={Number(totalExchangeNetAmount || 0).toFixed(0)}
                      style={{
                        width: 130,
                        fontWeight: "bold",
                        textAlign: "right",
                        backgroundColor: "rgba(248,250,252,0.95)",
                        color: "#334155",
                        border: "1.5px solid rgba(148,163,184,0.6)",
                        borderRadius: "6px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: 6,
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg,rgba(255, 239, 248, 0.97) 0%,rgba(254, 219, 236, 0.97) 100%)",
                      border: "1px solid rgba(246, 59, 146, 0.28)",
                      padding: "8px 12px",
                    }}
                  >
                    <label
                      style={{
                        flex: 1,
                        fontSize: "13px",
                        fontWeight: 800,
                        color: "#8a1e50",
                        letterSpacing: "0.4px",
                        textTransform: "uppercase",
                      }}
                    >
                      Balance Amount
                    </label>
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: 800,
                        color: "#8a1e50",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      {Number(totalNetAmount || 0).toFixed(0)}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* ================= RIGHT SIDEBAR ================= */}
          <div
            style={{
              width: 210,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              overflowY: "auto",
            }}
          >
            <style>{`
    @keyframes sb-glow { 0%, 100% { box-shadow: 0 2px 10px var(--sb-glow-c, rgba(0,0,0,0.12)); } 50% { box-shadow: 0 6px 22px var(--sb-glow-c, rgba(0,0,0,0.12)); } }
    @keyframes sb-icon-bounce { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-2px) rotate(-6deg); } }
    @keyframes sb-icon-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
    @keyframes sb-ring-pulse { 0% { box-shadow: 0 0 0 0 rgba(249,115,22,0.45); } 70% { box-shadow: 0 0 0 12px rgba(249,115,22,0); } 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); } }
    @keyframes sb-shine { 0% { transform: translateX(-120%) skewX(-20deg); } 100% { transform: translateX(220%) skewX(-20deg); } }
    @keyframes sb-tile-icon-wiggle { 0%, 80%, 100% { transform: rotate(0deg); } 85% { transform: rotate(-10deg); } 92% { transform: rotate(10deg); } }
    .sb-tile-icon { display: inline-block; animation: sb-tile-icon-wiggle 3.5s ease-in-out infinite; }
    .sb-tile:nth-child(1) .sb-tile-icon { animation-delay: 0.0s; }
    .sb-tile:nth-child(2) .sb-tile-icon { animation-delay: 0.15s; }
    .sb-tile:nth-child(3) .sb-tile-icon { animation-delay: 0.30s; }
    .sb-tile:nth-child(4) .sb-tile-icon { animation-delay: 0.45s; }
    .sb-tile:nth-child(5) .sb-tile-icon { animation-delay: 0.60s; }
    .sb-tile:nth-child(6) .sb-tile-icon { animation-delay: 0.75s; }
    .sb-tile-value { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block; max-width: 100%; }
    .sb-qty-card { position: relative; overflow: hidden; border-radius: 14px; padding: 14px 12px; text-align: center; background: linear-gradient(135deg,#fb923c 0%,#ea580c 100%); color: #fff; animation: sb-glow 2.4s ease-in-out infinite; --sb-glow-c: rgba(234,88,12,0.45); }
    .sb-qty-card::after { content: ""; position: absolute; top: 0; left: 0; width: 40%; height: 100%; background: linear-gradient(120deg, transparent, rgba(255,255,255,0.35), transparent); animation: sb-shine 2.8s ease-in-out infinite; }
    .sb-qty-icon { width: 34px; height: 34px; border-radius: 10px; margin: 0 auto 6px; background: rgba(255,255,255,0.22); display: flex; align-items: center; justify-content: center; font-size: 17px; animation: sb-ring-pulse 2s infinite; }
    .sb-qty-label { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; opacity: 0.85; }
    .sb-qty-value { font-size: 26px; font-weight: 800; line-height: 1.15; }
    .sb-btn { position: relative; display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: none; border-radius: 12px; cursor: pointer; text-align: left; color: #fff; font-weight: 700; font-size: 13px; letter-spacing: 0.2px; transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease; animation: sb-glow 2.6s ease-in-out infinite; }
    .sb-btn:hover { transform: translateY(-2px) scale(1.015); filter: brightness(1.06); }
    .sb-btn:active { transform: translateY(0) scale(0.99); }
    .sb-badge { flex-shrink: 0; width: 28px; height: 28px; border-radius: 9px; background: rgba(255,255,255,0.28); display: flex; align-items: center; justify-content: center; font-size: 15px; animation: sb-icon-bounce 1.8s ease-in-out infinite; }
    .sb-badge-spin { animation: sb-icon-spin 3s linear infinite; }
    .sb-chevron { margin-left: auto; opacity: 0.75; font-size: 12px; }
    .sb-hold-details { --sb-glow-c: rgba(45,212,191,0.45); background: linear-gradient(135deg,#5eead4,#0d9488); }
    .sb-hold { --sb-glow-c: rgba(245,158,11,0.45); background: linear-gradient(135deg,#fcd34d,#d97706); }
    .sb-info { --sb-glow-c: rgba(56,189,248,0.45); background: linear-gradient(135deg,#7dd3fc,#0284c7); }
    .sb-return { --sb-glow-c: rgba(167,139,250,0.45); background: linear-gradient(135deg,#c4b5fd,#7c3aed); }
    .sb-invoice { --sb-glow-c: rgba(211, 52, 131, 0.45);  background: linear-gradient(135deg, #e76ea0, #960549d7); }
    
    .sb-btn-sm { padding: 6px 10px; border-radius: 8px; font-size: 0.68rem; gap: 5px; justify-content: center; flex: 1; }
    .sb-badge-sm { width: 20px; height: 20px; border-radius: 6px; font-size: 11px; }
    .sb-browse { --sb-glow-c: rgba(209, 194, 185, 0.45); background: linear-gradient(135deg, #787a7ade, #928f8d); }
    .sb-create { --sb-glow-c: rgba(128, 160, 140, 0.45); background: linear-gradient(135deg, #6b69d6, #535480); }
  `}</style>

            {/* Item Details card */}
            <div
              style={{
                background: "linear-gradient(150deg, #eef2ff 0%, #f5f7ff 100%)",
                borderRadius: "12px",
                padding: "12px 8px",
                border: "1px solid #dbe3f7",
                boxShadow: "0 2px 10px rgba(99,102,241,0.10)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "0.66rem",
                  fontWeight: 800,
                  color: "#4f46e5",
                  marginBottom: "8px",
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#6366f1",
                  }}
                />
                Item Details
              </div>

              <div
                style={{
                  textAlign: "center",
                  fontSize: "15px",
                  fontWeight: 800,
                  color: "#312e81",
                  marginBottom: 10,
                }}
              >
                {barCodeData[0]?.DesignCode ||
                  exchangeData[0]?.DesignCode ||
                  "-"}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  {
                    label: "Available",
                    value: barCodeData[0]?.Qty ?? exchangeData[0]?.Qty ?? "-",
                    icon: "📦",
                    tint: "#dcfce7",
                    color: "#15803d",
                  },
                  {
                    label: "Size",
                    value:
                      barCodeData[0]?.Prodsize ??
                      exchangeData[0]?.Prodsize ??
                      "-",
                    icon: "📏",
                    tint: "#dbeafe",
                    color: "#1d4ed8",
                  },
                  {
                    label: "Color",
                    value:
                      barCodeData[0]?.COLOUR ?? exchangeData[0]?.COLOUR ?? "-",
                    icon: "🎨",
                    tint: "#fef3c7",
                    color: "#b45309",
                  },
                  {
                    label: "HSN",
                    value:
                      barCodeData[0]?.HSNCODE ??
                      exchangeData[0]?.HSNCODE ??
                      "-",
                    icon: "🏷️",
                    tint: "#f1f5f9",
                    color: "#475569",
                  },
                  {
                    label: "Fashion",
                    value:
                      barCodeData[0]?.FITTING ??
                      exchangeData[0]?.FITTING ??
                      "-",
                    icon: "✨",
                    tint: "#ccfbf1",
                    color: "#0f766e",
                  },
                  {
                    label: "Offer",
                    value:
                      barCodeData[0]?.SLEVES ?? exchangeData[0]?.SLEVES ?? "-",
                    icon: "🎁",
                    tint: "#f3e8ff",
                    color: "#7e22ce",
                  },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="sb-tile"
                    title={String(row.value)}
                    style={{
                      background: row.tint,
                      borderRadius: "9px",
                      padding: "7px 10px",
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        color: row.color,
                        fontWeight: 700,
                        fontSize: "10.5px",
                        flexShrink: 0,
                      }}
                    >
                      <span className="sb-tile-icon">{row.icon}</span>
                      {row.label}
                    </div>
                    <span
                      className="sb-tile-value"
                      style={{
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: "#1e293b",
                        textAlign: "right",
                      }}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* QTY — highlighted stat card */}
            {/* <div className="sb-qty-card">
              <div className="sb-qty-icon">🛒</div>
              <div className="sb-qty-label">Total Quantity</div>
              <div className="sb-qty-value">{totalBillQty + totalExchangeQty}</div>
            </div> */}

            {/* {holdData?.length > 0 && (
              <button
                className="sb-btn sb-hold-details"
                onClick={() => {
                  setHoldOpen(true);
                  smCodeRef.current?.focus();
                }}
              >
                <span className="sb-badge">📋</span>
                Hold Bill Details
                <span className="sb-chevron">›</span>
              </button>
            )}

            <button
              className="sb-btn sb-hold"
              onClick={async () => {
                smCodeRef.current?.focus();
                if (barCodeData.length > 0) {
                  const nextHoldNo = await holdNoAPI();
                  await createBarcodeholdDet(nextHoldNo);
                  await createBarcodeHoldMast(nextHoldNo);
                } else {
                  messageApi.open({ type: "error", content: <span style={{ fontSize: "18px", fontWeight: 600 }}>Please add Design Code Details.</span> });
                }
              }}
            >
              <span className="sb-badge">⏸️</span>
              Hold Bill
              <span className="sb-chevron">›</span>
            </button> */}

            <button
              className="sb-btn sb-info"
              style={{ minHeight: 58, padding: "14px 12px" }}
              onClick={() => {
                setMobileOpen(true);
                smCodeRef.current?.focus();
              }}
            >
              <span className="sb-badge">ℹ️</span>
              Cust. Bill Info
              <span className="sb-chevron">›</span>
            </button>

            <button
              className="sb-btn sb-return"
              style={{ minHeight: 58, padding: "14px 12px" }}
              onClick={() => navigate("/sale-return")}
            >
              <span className="sb-badge sb-badge-spin">↩️</span>
              Sale Return
              <span className="sb-chevron">›</span>
            </button>

            <button
              className="sb-btn sb-invoice"
              style={{ minHeight: 58, padding: "14px 12px" }}
              onClick={() => navigate("/sales-invoice")}
            >
              <span className="sb-badge">🛒</span>
              Sales Invoice
              <span className="sb-chevron">›</span>
            </button>
          </div>
        </div>
      </div>

      {/* ================= Dialogs ================= */}
      <EstimationSelectDialog
        visibleHis={visibleHis}
        setVisibleHis={setVisibleHis}
        modifyOnlineAPI={modifyOnlineAPI}
        modifyDetAPI={modifyDetAPI}
        modifyMastAPI={modifyMastAPI}
        modifyExchangeDetAPI={modifyExchangeDetAPI}
        modifyLedgerAPI={modifyOpeningLedgerAPI}
        setBillNo={setBillNo}
        setBillDate={setBillDate}
        handleReset={handleRestore}
        setSearchText={setSearchText}
        searchText={searchText}
        smCodeRef={smCodeRef}
        apiTableName="CREDIT_BILLMAST"
        extraWhere="AND APPNO=1"
      />
      <PaymentModesDialog
        billNo={billNo}
        invNo={invNo}
        payMode={payMode}
        setPayMode={setPayMode}
        totalNetAmount={totalNetAmount}
        cashValue={cashValue}
        setCashValue={setCashValue}
        cashAmt={cashAmt}
        setCashAmt={setCashAmt}
        cardAmt={cardAmt}
        setCardAmt={setCardAmt}
        upiAmt={upiAmt}
        setUpiAmt={setUpiAmt}
        onlineAmt={onlineAmt}
        setOnlineAmt={setOnlineAmt}
        creditAmt={creditAmt}
        setCreditAmt={setCreditAmt}
        userDetails={userDetails}
        cardAcc={cardAcc}
        upiAcc={upiAcc}
        onlineAcc={onlineAcc}
        setOpen={setOpen}
        base64QR={base64QR}
        cPhoneNo={cPhoneNo}
        setCPhoneNo={setCPhoneNo}
        userDetailsAPI={userDetailsAPI}
        smCodeRef={smCodeRef}
      />
      <CustomerDialog
        open={open}
        mode={customerMode}
        onCancel={() => {
          setOpen(false);
          setFormValues({
            mobile1: "",
            city: "",
            name: "",
            mobile2: "",
            phone: "",
            address1: "",
            address2: "",
            address3: "",
            address4: "",
            state: "ANDHRA PRADESH",
            stateCode: "37",
            gstin: "",
            panNo: "",
            idProof: "",
            dob: "",
            anniversary: "",
            locationType: "LOCAL",
          });
          smCodeRef.current?.focus();
        }}
        onSave={
          customerMode === "edit"
            ? updateCustomerDetailsAPI
            : createCustomerDetailsAPI
        }
        formValues={formValues}
        setFormValues={setFormValues}
        deleteCustomerDetailsAPI={deleteCustomerDetailsAPI}
        smCodeRef={smCodeRef}
        customerLookupAPI={async (mobile) => {
          try {
            const res = await axios.get(
              `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CUSTOMER&where=MOBILENO='${mobile}'`,
              { headers: { tenantName } },
            );
            return res.data?.[0] ?? null;
          } catch {
            return null;
          }
        }}
      />
      <CustomerSelectDialog
        open={customerSelectOpen}
        source="CUSTOMER"
        custType="CUSTOMER"
        onCancel={() => {
          setCustomerSelectOpen(false);
          smCodeRef.current?.focus();
        }}
        onSelect={handleCustomerSelect}
        onAddCustomer={openNewCustomer}
      />
      <ExchangeEditItemDetails
        itemDetailsOpen={itemDetailsOpen}
        setItemDetailsOpen={setItemDetailsOpen}
        setItemDetailsData={setItemDetailsData}
        itemDetailsData={itemDetailsData}
        itemDesignCode={itemDesignCode}
        setBarCodeData={setBarCodeData}
        barCodeData={barCodeData}
        itemExchangeDetailsOpen={itemExchangeDetailsOpen}
        setItemExchangeDetailsOpen={setItemExchangeDetailsOpen}
        setItemExchangeDetailsData={setItemExchangeDetailsData}
        itemExchangeDetailsData={itemExchangeDetailsData}
        itemExchangeDesignCode={itemExchangeDesignCode}
        setExchangeData={setExchangeData}
        exchangeData={exchangeData}
        qtyData={qtyData}
        exchangeQtyData={exchangeQtyData}
        offerARate={offerARate}
        offerBRate={offerBRate}
        smCodeRef={smCodeRef}
        adminType={adminType}
      />
      <HoldSelectDialog
        setHoldOpen={setHoldOpen}
        holdOpen={holdOpen}
        holdData={holdData}
        setHoldData={setHoldData}
        filteredHoldData={filteredHoldData}
        setFilteredHoldData={setFilteredHoldData}
        selectedHoldRowKeys={selectedHoldRowKeys}
        handleSearchHoldInput={handleSearchHoldInput}
        handleCheckboxChangeHold={handleCheckboxChangeHold}
        handleReset={handleRestore}
        setSearchHoldText={setSearchHoldText}
        searchHoldText={searchHoldText}
        filterDataByHoldNo={filterDataByHoldNo}
        setHNo={setHNo}
        deleteBarcodeMastHoldAPI={deleteBarcodeMastHoldAPI}
        deleteBarcodeDetHoldAPI={deleteBarcodeDetHoldAPI}
        modifyHoldDetAPI={modifyHoldDetAPI}
        smCodeRef={smCodeRef}
      />
      <HomeBillDialog
        homeDetailsOpen={homeDetailsOpen}
        setHomeDetailsOpen={setHomeDetailsOpen}
        homeExchangeDetailsOpen={homeExchangeDetailsOpen}
        setHomeExchangeDetailsOpen={setHomeExchangeDetailsOpen}
        qty={qty}
        setQty={setQty}
        amount={amount}
        setAmount={setAmount}
        disCount={disCount}
        setDisCount={setDisCount}
        rate={rate}
        setRate={setRate}
        disAmt={disAmt}
        setDisAmt={setDisAmt}
        productName={productName}
        setProductName={setProductName}
        designCode={designCode}
        setDesignCode={setDesignCode}
        setPTypeName={setPTypeName}
        setHsnCode={setHsnCode}
        productData={productData}
        setProductData={setProductData}
        setBrandName={setBrandName}
        handleHomeBillSave={handleHomeBillSave}
        handleHomeExchangeSave={handleHomeExchangeSave}
      />
      <TaansDetailsDialog
        taansOpen={taansOpen}
        setTaansOpen={setTaansOpen}
        exchangeTaansOpen={exchangeTaansOpen}
        setExchangeTaansOpen={setExchangeTaansOpen}
        taansData={taansData}
        setTaansData={setTaansData}
        exchangeTaansData={exchangeTaansData}
        setExchangeTaansData={setExchangeTaansData}
        handleTaansBillSave={handleTaansBillSave}
        handleTaansExchangeSave={handleTaansExchangeSave}
      />
      <OfferDetailsADialog
        taansOpen={offerAOpen}
        setTaansOpen={setOfferAOpen}
        taansData={offerAData}
        setTaansData={setOfferAData}
        handleTaansBillSave={handleOfferABillSave}
        offerARate={offerARate}
      />
      <OfferDetailsBDialog
        taansOpen={offerBOpen}
        setTaansOpen={setOfferBOpen}
        taansData={offerBData}
        setTaansData={setOfferBData}
        handleTaansBillSave={handleOfferBBillSave}
        offerARate={offerBRate}
      />
      <CustomerBillInfoDialog
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        mobile={mobile}
        setMobile={setMobile}
        smCodeRef={smCodeRef}
      />
      <DirectPrint
        userDetails={userDetails}
        userPhone={userPhone}
        address={address}
        address1={address1}
        gstNo={gstNo}
        nextInvNo={invNo}
        billNo={billNo}
        billDate={billDate}
        userName={userName}
        barCodeData={barCodeData}
        totalNetAmount={totalNetAmount}
        base64Images={base64Images}
        base64QR={base64QR}
        totalGstData={totalGstData}
        ins={ins}
        ins1={ins1}
        ins2={ins2}
        ins3={ins3}
        ins4={ins4}
        ins5={ins4}
        ins6={ins5}
        totalBillAmount={totalBillAmount}
        totalBillDiSAmt={totalBillDiSAmt}
        totalBillNetAmount={totalBillNetAmount}
        totalBillQty={totalBillQty}
        totalExchangeAmount={totalExchangeAmount}
        totalExchangeDiSAmt={totalExchangeDiSAmt}
        totalExchangeNetAmount={totalExchangeNetAmount}
        totalExchangeQty={totalExchangeQty}
        gstValue={gstValue}
        cashAmt={cashAmt}
        cardAmt={cardAmt}
        onlineAmt={onlineAmt}
        upiAmt={upiAmt}
        creditAmt={creditAmt}
        handlePrint={handlePrint}
        receiptRef={receiptRef}
        ref={samplePrintRef}
      />
      <QrCodeDialog
        qrCodeOpen={qrCodeOpen}
        handleQrcodeCancel={handleQrcodeCancel}
        base64QR={base64QR}
      />
    </Spin>
  );
};

export default ExchangeBill;
